import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'react-toastify';
import StripePayment from './StripePayment';
import CourseRegistrationForm from './CourseRegistrationForm';
import UnifiedConsentFormModal from './UnifiedConsentFormModal';
import ParentBillingDashboard from './billing/ParentBillingDashboard';
import CampPaymentModal from './parent/CampPaymentModal';
import ChildScheduleView from './parent/ChildScheduleView';
import AddSessionModal from './parent/AddSessionModal';
import { config } from '../config';
import { formatDateLocal, formatTimePST } from '../utils/dateUtils';
import './billing/BillingDashboard.css';

interface ProgramEnrollment {
  enrollmentId: string;
  programId: string;
  programName: string;
  programTier: string;
  programType: string;
  durationMinutes: number;
  sessionsRequired: number;
  enrollmentDate?: string;
  enrollmentStatus: string;
}

interface PaymentPlan {
  id: string;
  planType: string;
  amount: number;
  frequency: string;
  startDate?: string;
  endDate?: string;
  nextBillingDate?: string;
  status: string;
  autoInvoice: boolean;
  notes?: string;
}

interface Child {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  dateOfBirth: string;
  age: number;
  gradeLevel: string;
  tier: 'foundations' | 'innovators' | 'pioneers';
  enrollmentDate: string;
  status: 'trial' | 'active' | 'inactive' | 'graduated' | 'suspended';
  trialSessionUsed: boolean;
  slackUsername?: string;
  photoUrl?: string;
  learningGoals?: string;
  profileNotes?: string;
  totalSessionsPurchased?: number;
  totalSessionsPurchasedPaid?: number;
  totalSessionsPurchasedPending?: number;
  totalSessionsUsed?: number;
  remainingSessions?: number;
  remainingSessionsNet?: number;
  activePackages?: number;
  programEnrollments?: ProgramEnrollment[];
  paymentPlans?: PaymentPlan[];
  campEnrollments?: CampEnrollment[];
}

interface CampEnrollment {
  enrollmentId: string;
  campId: string;
  campName: string;
  enrollmentType: 'full_camp' | 'weekly' | 'drop_in';
  selectedWeeks?: number[];
  daysAttending?: string[];
  startDate?: string;
  endDate?: string;
  finalPrice: number;
  paymentStatus: string;
  enrollmentDate?: string;
  needsEarlyDropoff: boolean;
  needsLatePickup: boolean;
}

interface SessionPackage {
  id: string;
  packageType: string;
  tier: string;
  totalSessions: number;
  usedSessions: number;
  remainingSessions: number;
  purchaseDate: string;
  expiryDate: string;
  originalPrice: number;
  discountApplied: number;
  finalPrice: number;
  paymentStatus: 'pending' | 'paid' | 'refunded' | 'partial';
  isShared: boolean;
  sharedWithStudents: string[];
  status: 'active' | 'expired' | 'completed' | 'cancelled';
}

interface Program {
  id: string;
  name: string;
  programType: 'after_school' | 'homeschool' | 'vacation_camp' | 'competition';
  tier: string;
  description: string;
  durationMinutes: number;
  sessionsRequired: number;
  status: string;
}

interface Camp {
  id: string;
  name: string;
  camp_type: string;
  tier: string; // Legacy
  applicable_tiers?: string[]; // New: multiple tiers
  tier_pricing_model?: string;
  tier_pricing?: { [key: string]: { base_price?: number; weekly_price?: number; drop_in_price?: number } };
  note?: string;
  discount_codes_enabled?: boolean;
  start_date: string;
  end_date: string;
  status: string;
  capacity: number;
  enrolled_count: number;
  pricing_model: string;
  base_price?: number;
  weekly_price?: number;
  drop_in_price?: number;
  total_weeks: number;
  camp_start_time?: string;
  camp_end_time?: string;
  early_dropoff_enabled?: boolean;
  early_dropoff_start?: string;
  early_dropoff_end?: string;
  early_dropoff_fee?: number;
  late_pickup_enabled?: boolean;
  late_pickup_start?: string;
  late_pickup_end?: string;
  late_pickup_fee?: number;
  snack_time_start?: string;
  snack_time_end?: string;
  lunch_break_start?: string;
  lunch_break_end?: string;
  location_name?: string;
  location_address?: string;
  theme?: string;
  description?: string;
  age_min?: number;
  age_max?: number;
  materials_needed?: string;
  requirements?: string;
  camp_days?: Array<{
    id: string;
    day_number: number;
    week_number: number;
    date: string;
    is_available: boolean;
    unavailable_reason?: string;
  }>;
}

interface Course {
  id: string;
  name: string;
  tier: 'foundations' | 'innovators' | 'pioneers';
  description: string;
  durationMinutes: number;
  sessionsRequired: number;
  prerequisites: string;
  skillsCovered: string[];
  applicablePrograms: string[];
  status: string;
}

interface CourseEnrollment {
  id: string;
  courseId: string;
  studentId: string;
  enrollmentDate: string;
  status: 'enrolled' | 'completed' | 'dropped';
}

interface Schedule {
  id: string;
  type: 'program' | 'course' | 'session';
  programId?: string;
  courseId?: string;
  studentId: string;
  scheduledDate: string;
  scheduledTime: string;
  duration: number;
  status: 'scheduled' | 'completed' | 'cancelled' | 'missed';
  location: string;
  instructor?: string;
  notes?: string;
}

interface UpcomingClass {
  id: string;
  type: 'program' | 'course' | 'session' | 'camp';
  name: string;
  studentName: string;
  scheduledDate: string;
  scheduledTime: string;
  duration: number;
  location: string;
  instructor?: string;
  canReschedule: boolean;
  rescheduleDeadline: string;
}

interface FamilyPlan {
  id: string;
  totalChildren: number;
  discountApplied: number;
  savingsAmount: number;
  isActive: boolean;
}

interface Referral {
  id: string;
  referralCode: string;
  status: 'pending' | 'active' | 'expired';
  discountAmount: number;
  requirements: string;
  expiryDate: string;
}

interface AttendanceRecord {
  id: string;
  student_id: string;
  student_name: string;
  date: string;
  time?: string;
  status: 'present' | 'absent' | 'late';
  program_name: string;
  notes?: string;
  created_at: string;
}

interface ConsentForm {
  id: string;
  student_id: string;
  student_name: string;
  form_type: 'MAKERSPACE' | 'MEDIA' | 'PHOTOGRAPHY' | 'GENERAL';
  title: string;
  description?: string;
  status: 'PENDING' | 'SIGNED' | 'EXPIRED' | 'REJECTED';
  created_at: string;
  signed_at?: string;
  expires_at?: string;
  s3_url?: string;
  can_sign: boolean;
}

interface ProgressReport {
  id: string;
  childName: string;
  programName: string;
  date: string;
  skills: string[];
  achievements: string[];
  areasForImprovement: string[];
  teacherNotes: string;
  overallRating: 'excellent' | 'good' | 'satisfactory' | 'needs-improvement';
}

interface Message {
  id: string;
  from: string;
  to: string;
  subject: string;
  message: string;
  date: string;
  read: boolean;
  priority: 'low' | 'medium' | 'high';
}

const ParentDashboard: React.FC = () => {
  const { user, signOut } = useAuth();
  const [children, setChildren] = useState<Child[]>([]);
  const [sessionPackages, setSessionPackages] = useState<SessionPackage[]>([]);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [camps, setCamps] = useState<Camp[]>([]);
  const [selectedCamp, setSelectedCamp] = useState<Camp | null>(null);
  const [showCampEnrollment, setShowCampEnrollment] = useState(false);
  const [isCampEnrollmentSubmitting, setIsCampEnrollmentSubmitting] = useState(false);
  const [showCampDetails, setShowCampDetails] = useState(false);
  const [selectedChildForCamp, setSelectedChildForCamp] = useState<Child | null>(null);
  const [campEnrollmentType, setCampEnrollmentType] = useState<'full_camp' | 'weekly' | 'drop_in'>('full_camp');
  const [selectedWeeks, setSelectedWeeks] = useState<number[]>([]);
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [needsEarlyDropoff, setNeedsEarlyDropoff] = useState(false);
  const [needsLatePickup, setNeedsLatePickup] = useState(false);
  const [earlyDropoffDays, setEarlyDropoffDays] = useState<string[]>([]);
  const [latePickupDays, setLatePickupDays] = useState<string[]>([]);
  const [discountCode, setDiscountCode] = useState<string>('');
  const [discountValidation, setDiscountValidation] = useState<any>(null);
  const [validatingDiscount, setValidatingDiscount] = useState(false);
  const [showCampPayment, setShowCampPayment] = useState(false);
  const [campInvoiceData, setCampInvoiceData] = useState<any>(null);
  const [campEnrollmentId, setCampEnrollmentId] = useState<string>('');
  const [familyPlan, setFamilyPlan] = useState<FamilyPlan | null>(null);
  const [referral, setReferral] = useState<Referral | null>(null);
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [selectedChildForAttendance, setSelectedChildForAttendance] = useState<string | null>(null);
  const [attendanceChildren, setAttendanceChildren] = useState<Child[]>([]);
  const [expandedAttendanceRecords, setExpandedAttendanceRecords] = useState<Set<string>>(new Set());
  const [attendanceStartDate, setAttendanceStartDate] = useState<string>('');
  const [attendanceEndDate, setAttendanceEndDate] = useState<string>('');
  const [showStudentDropdown, setShowStudentDropdown] = useState<boolean>(false);
  const [consentForms, setConsentForms] = useState<ConsentForm[]>([]);
  const [pendingConsentForms, setPendingConsentForms] = useState<ConsentForm[]>([]);
  const [showConsentFormModal, setShowConsentFormModal] = useState(false);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState<string | null>(null);
  const [selectedPdfForm, setSelectedPdfForm] = useState<ConsentForm | null>(null);
  const [progressReports, setProgressReports] = useState<ProgressReport[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [announcements, setAnnouncements] = useState<any[]>([]);
  const [dismissedAnnouncements, setDismissedAnnouncements] = useState<Set<string>>(() => {
    // Load dismissed announcements from localStorage
    try {
      const stored = localStorage.getItem('dismissedAnnouncements');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'children' | 'packages' | 'programs' | 'camps' | 'attendance' | 'progress' | 'messages' | 'billing' | 'program-details' | 'consent-forms' | 'session-details'>('overview');
  const [showMobileNavDrawer, setShowMobileNavDrawer] = useState(false);
  const [programDetails, setProgramDetails] = useState<any>(null);
  const [selectedChild, setSelectedChild] = useState<Child | null>(null);
  const [showChildScheduleView, setShowChildScheduleView] = useState(false);
  const [selectedChildForSchedule, setSelectedChildForSchedule] = useState<Child | null>(null);
  const [childrenCarouselIndex, setChildrenCarouselIndex] = useState(0);
  const childrenCarouselRef = useRef<HTMLDivElement>(null);
  const [availablePrivateSessions, setAvailablePrivateSessions] = useState<any[]>([]);
  const [selectedPrivateSessions, setSelectedPrivateSessions] = useState<string[]>([]);
  const [showMessageForm, setShowMessageForm] = useState(false);
  const [showPackageForm, setShowPackageForm] = useState(false);
  const [showCampForm, setShowCampForm] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [newMessage, setNewMessage] = useState({ to: '', subject: '', message: '' });
  const [selectedPackage, setSelectedPackage] = useState<SessionPackage | null>(null);
  const [selectedChildForPurchase, setSelectedChildForPurchase] = useState<Child | null>(null);
  const [selectedPackageType, setSelectedPackageType] = useState<any>(null);
  const [selectedChildForAddSession, setSelectedChildForAddSession] = useState<Child | null>(null);
  const [showAddSessionModal, setShowAddSessionModal] = useState(false);
  const [purchaseCalculation, setPurchaseCalculation] = useState<any>(null);
  const [campDiscountRules, setCampDiscountRules] = useState<any[]>([]);
  const [sessionPackageTemplates, setSessionPackageTemplates] = useState<any[]>([]);
  const [expandedCamps, setExpandedCamps] = useState<Set<string>>(new Set());
  
  const toggleCampExpanded = (campId: string) => {
    const newExpanded = new Set(expandedCamps);
    if (newExpanded.has(campId)) {
      newExpanded.delete(campId);
    } else {
      newExpanded.add(campId);
    }
    setExpandedCamps(newExpanded);
  };
  
  // Course filtering state
  const [courseFilterTier, setCourseFilterTier] = useState<string>('');
  const [courseFilterProgram, setCourseFilterProgram] = useState<string>('');
  const [courseSearchTerm, setCourseSearchTerm] = useState<string>('');
  
  // Course enrollment state
  const [courseEnrollments, setCourseEnrollments] = useState<CourseEnrollment[]>([]);
  const [selectedChildForEnrollment, setSelectedChildForEnrollment] = useState<Child | null>(null);
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
  const [enrollmentCourse, setEnrollmentCourse] = useState<Course | null>(null);
  
  // Scheduling state
  const [upcomingClasses, setUpcomingClasses] = useState<UpcomingClass[]>([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedScheduleItem, setSelectedScheduleItem] = useState<any>(null);
  const [scheduleType, setScheduleType] = useState<'program' | 'course' | 'session'>('session');
  
  // Homeschool program enrollment state
  const [schedulePreference, setSchedulePreference] = useState<'standard' | 'outside' | ''>('');
  const [customScheduleMessage, setCustomScheduleMessage] = useState('');
  const [callbackNumber, setCallbackNumber] = useState('');
  const [callbackTimePreference, setCallbackTimePreference] = useState('');
  const [preferredDays, setPreferredDays] = useState<string[]>([]);
  
  // Stripe payment state
  const [showStripePayment, setShowStripePayment] = useState(false);
  const [stripePaymentData, setStripePaymentData] = useState<{
    amount: number;
    description: string;
    metadata?: Record<string, any>;
  } | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState<any>(null);

  // Success notification state
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState({ title: '', message: '' });

  // Helper functions for course enrollment
  const getChildEnrollments = (childId: string) => {
    return courseEnrollments.filter(enrollment => 
      enrollment.studentId === childId && enrollment.status === 'enrolled'
    );
  };

  const isChildEnrolledInCourse = (childId: string, courseId: string) => {
    return courseEnrollments.some(enrollment => 
      enrollment.studentId === childId && 
      enrollment.courseId === courseId && 
      enrollment.status === 'enrolled'
    );
  };

  const canEnrollChild = (childId: string) => {
    const childEnrollments = getChildEnrollments(childId);
    return childEnrollments.length < 2;
  };

  const handleEnrollCourse = (course: Course) => {
    if (children.length === 0) {
      alert('No children available for enrollment');
      return;
    }
    
    setEnrollmentCourse(course);
    setShowEnrollmentModal(true);
  };

  const handleConfirmEnrollment = (childId: string) => {
    if (!enrollmentCourse) return;
    
    const childEnrollments = getChildEnrollments(childId);
    if (childEnrollments.length >= 2) {
      alert('This child is already enrolled in 2 courses. Please unenroll from a course first.');
      return;
    }
    
    // Create new enrollment
    const newEnrollment: CourseEnrollment = {
      id: `enrollment_${Date.now()}`,
      courseId: enrollmentCourse.id,
      studentId: childId,
      enrollmentDate: new Date().toISOString().split('T')[0],
      status: 'enrolled'
    };
    
    setCourseEnrollments(prev => [...prev, newEnrollment]);
    setShowEnrollmentModal(false);
    setEnrollmentCourse(null);
    setSelectedChildForEnrollment(null);
    
    alert('Successfully enrolled in course!');
  };

  const handleUnenrollCourse = (enrollmentId: string) => {
    setCourseEnrollments(prev => 
      prev.map(enrollment => 
        enrollment.id === enrollmentId 
          ? { ...enrollment, status: 'dropped' as const }
          : enrollment
      )
    );
    alert('Successfully unenrolled from course!');
  };

  // Helper functions for scheduling
  const canReschedule = (scheduledDate: string) => {
    const classDate = new Date(scheduledDate);
    const now = new Date();
    const hoursUntilClass = (classDate.getTime() - now.getTime()) / (1000 * 60 * 60);
    return hoursUntilClass > 24; // Can reschedule if more than 24 hours away
  };

  const handleScheduleClass = (type: 'program' | 'course' | 'session', item: any) => {
    console.log('🔍 handleScheduleClass called with type:', type);
    if (type === 'program') {
      console.log('🔍 Opening course registration form for program');
      // For program scheduling, open course registration form
      // If no children, show message to add child first
      if (children.length === 0) {
        console.log('🔍 No children found, showing alert');
        alert('Please add a child first before registering for a program. Click "Add New Child" to get started.');
        return;
      }
      // Use the first child for registration (user can change this in the form)
      const firstChild = children[0];
      console.log('🔍 Using first child for registration:', firstChild);
      handleOpenCourseRegistration(firstChild);
      return;
    }
    setScheduleType(type);
    setSelectedScheduleItem(item);
    setShowScheduleModal(true);
  };

  const handleRescheduleClass = (classId: string) => {
    const classItem = upcomingClasses.find(c => c.id === classId);
    if (classItem) {
      setSelectedScheduleItem(classItem);
      setShowScheduleModal(true);
    }
  };

  // Real upcoming sessions data from API
  const [upcomingSessions, setUpcomingSessions] = useState<any[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [expandedChildSessions, setExpandedChildSessions] = useState<Set<string>>(new Set());
  
  // Student enrollment form state
  const [showStudentForm, setShowStudentForm] = useState(false);
  const [studentFormData, setStudentFormData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    grade: '',
    school: '',
    emergency_contact: '',
    emergency_phone: '',
    medical_conditions: '',
    allergies: '',
    tier: 'foundations',
    create_login: false,
    login_email: '',
    force_password_change: false
  });
  const [studentFormLoading, setStudentFormLoading] = useState(false);
  const [enrollmentSuccess, setEnrollmentSuccess] = useState(false);
  
  // Registration flow state
  const [registrationStep, setRegistrationStep] = useState<'student-info' | 'consent-forms' | 'complete'>('student-info');
  const [newlyRegisteredStudent, setNewlyRegisteredStudent] = useState<any>(null);
  const [newlyCreatedConsentForms, setNewlyCreatedConsentForms] = useState<ConsentForm[]>([]);
  const [signingConsentFormIndex, setSigningConsentFormIndex] = useState(0);
  // Use ref to track current index during registration to prevent reset on re-renders
  const signingConsentFormIndexRef = useRef(0);

  // Course registration form state
  const [showCourseRegistration, setShowCourseRegistration] = useState(false);
  const [selectedStudentForRegistration, setSelectedStudentForRegistration] = useState<Child | null>(null);
  
  // Session details state
  const [sessionDetails, setSessionDetails] = useState<any[]>([]);
  const [sessionFilterProgram, setSessionFilterProgram] = useState<string>('');
  const [sessionFilterCourse, setSessionFilterCourse] = useState<string>('');
  const [sessionFilterStatus, setSessionFilterStatus] = useState<string>('');

  // Filtered courses
  const filteredCourses = courses.filter(course => {
    // Filter by tier
    if (courseFilterTier && course.tier !== courseFilterTier) {
      return false;
    }
    
    // Filter by program type
    if (courseFilterProgram && !course.applicablePrograms.includes(courseFilterProgram)) {
      return false;
    }
    
    // Filter by search term
    if (courseSearchTerm) {
      const searchLower = courseSearchTerm.toLowerCase();
      const matchesName = course.name.toLowerCase().includes(searchLower);
      const matchesDescription = course.description.toLowerCase().includes(searchLower);
      const matchesSkills = course.skillsCovered.some(skill => 
        skill.toLowerCase().includes(searchLower)
      );
      
      if (!matchesName && !matchesDescription && !matchesSkills) {
        return false;
      }
    }
    
    return true;
  });

  // Fetch dashboard data when user is loaded
  useEffect(() => {
    if (user?.email) {
      console.log('🔍 User available, fetching dashboard data...', user);
      fetchDashboardData();
      fetchCampDiscountRules();
      fetchSessionPackageTemplates();
      fetchUpcomingSessions();
    } else {
      console.log('⏳ Waiting for user to load...');
    }
  }, [user?.email]);

  // Fetch attendance records when attendance tab is activated
  useEffect(() => {
    if (activeTab === 'attendance' && user?.email) {
      // Initialize attendanceChildren from children if not already set
      if (attendanceChildren.length === 0 && children.length > 0) {
        setAttendanceChildren(children);
      }
      // Reset date filters when tab is activated (will use backend default: current date + previous 15 days)
      setAttendanceStartDate('');
      setAttendanceEndDate('');
      fetchAttendanceRecords();
    }
  }, [activeTab, user?.email, children]);

  // Fetch consent forms when component loads
  useEffect(() => {
    if (user?.email) {
      fetchConsentForms();
    }
  }, [user?.email]);

  // Fetch camps when camps tab is active
  useEffect(() => {
    if (activeTab === 'camps') {
      fetchCamps();
    }
  }, [activeTab, children]);

  // Clear discount code when camp changes
  useEffect(() => {
    if (!selectedCamp) {
      setDiscountCode('');
      setDiscountValidation(null);
    }
  }, [selectedCamp]);

  // Clear extended hours selections when weeks/days change
  useEffect(() => {
    setEarlyDropoffDays([]);
    setLatePickupDays([]);
    setNeedsEarlyDropoff(false);
    setNeedsLatePickup(false);
  }, [selectedWeeks, selectedDays, campEnrollmentType]);

  // Fetch upcoming sessions from API
  const fetchUpcomingSessions = async () => {
    if (!user?.email) return;
    
    setSessionsLoading(true);
    try {
      const response = await fetch(`${config.BACKEND_URL}/api/upcoming-sessions/${user.email}`);
      if (response.ok) {
        const data = await response.json();
        setUpcomingSessions(data.sessions || []);
      } else {
        console.error('Failed to fetch upcoming sessions');
        setUpcomingSessions([]);
      }
    } catch (error) {
      console.error('Error fetching upcoming sessions:', error);
      setUpcomingSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  };

  // Fetch available camps
  const fetchCamps = async () => {
    try {
      console.log('🏕️ Fetching available camps...');
      const token = localStorage.getItem('cognitoToken') || '';
      
      // Fetch published camps with day details
      const response = await fetch(`${config.BACKEND_URL}/api/camps?status=published&include_days=true`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Camps loaded:', data);
        // Filter camps by student tier.
        // If children are not loaded yet, keep camps visible to avoid blank UI flicker.
        if (!children || children.length === 0) {
          setCamps(data);
          return;
        }
        const filteredCamps = data.filter((camp: Camp) => {
          // If camp has applicable_tiers, check if any child's tier matches
          if (camp.applicable_tiers && camp.applicable_tiers.length > 0) {
            return children.some(child => 
              child.tier && camp.applicable_tiers?.includes(child.tier)
            );
          }
          // Legacy: check if camp.tier matches any child's tier or is 'all'
          if (camp.tier) {
            if (camp.tier === 'all') return true;
            return children.some(child => child.tier === camp.tier);
          }
          // If no tier info, show the camp (backward compatibility)
          return true;
        });
        console.log(`✅ Filtered ${filteredCamps.length} camps for student tiers`);
        setCamps(filteredCamps);
      } else {
        console.error('Failed to fetch camps');
        setCamps([]);
      }
    } catch (error) {
      console.error('Error fetching camps:', error);
      setCamps([]);
    }
  };

  // Handle student enrollment form
  const handleStudentFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.email) return;

    // Validate required fields
    if (!studentFormData.grade) {
      alert('Please select a grade.');
      return;
    }
    if (!studentFormData.school || studentFormData.school.trim() === '') {
      alert('Please enter the school name.');
      return;
    }
    if (!studentFormData.emergency_contact || studentFormData.emergency_contact.trim() === '') {
      alert('Please enter the emergency contact name.');
      return;
    }
    if (!studentFormData.emergency_phone || studentFormData.emergency_phone.trim() === '') {
      alert('Please enter the emergency contact phone number.');
      return;
    }

    setStudentFormLoading(true);
    try {
      // First get the parent ID from the dashboard data
      const dashboardResponse = await fetch(`${config.BACKEND_URL}/api/parent-dashboard/${user.email}`);
      if (!dashboardResponse.ok) {
        throw new Error('Failed to get parent information');
      }
      const dashboardData = await dashboardResponse.json();
      const parentId = dashboardData.parent_id;
      
      if (!parentId) {
        throw new Error('Parent ID not found');
      }
      
      // Map form data to API schema
      const studentData = {
        first_name: studentFormData.first_name,
        last_name: studentFormData.last_name,
        date_of_birth: studentFormData.date_of_birth,
        grade_level: studentFormData.grade || 'K', // Map grade to grade_level
        parent_id: parentId,
        profile_notes: `Medical: ${studentFormData.medical_conditions || 'None'}\nAllergies: ${studentFormData.allergies || 'None'}\nEmergency Contact: ${studentFormData.emergency_contact || 'Not provided'}\nEmergency Phone: ${studentFormData.emergency_phone || 'Not provided'}\nSchool: ${studentFormData.school || 'Not provided'}`,
        learning_goals: `Enrolled in ${studentFormData.tier} tier program`,
        // Login options
        create_login: studentFormData.create_login || false,
        login_email: studentFormData.login_email || null,
        force_password_change: studentFormData.force_password_change || false
      };
      
      const response = await fetch(`${config.BACKEND_URL}/api/students/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(studentData)
      });
      
      if (response.ok) {
        const newStudent = await response.json();
        console.log('✅ Student created:', newStudent);
        
        // Store newly registered student data
        setNewlyRegisteredStudent({
          id: newStudent.student_id,
          firstName: studentFormData.first_name,
          lastName: studentFormData.last_name,
          dateOfBirth: studentFormData.date_of_birth,
          tier: studentFormData.tier,
          // Store student info for consent forms
          studentName: `${studentFormData.first_name} ${studentFormData.last_name}`,
          studentDOB: studentFormData.date_of_birth
        });
        
        // Refresh dashboard data and consent forms (consent forms will be created automatically by backend)
        await fetchDashboardData();
        await fetchConsentForms();
        
        // Complete registration immediately - consent forms can be signed later
        setEnrollmentSuccess(true);
        setShowStudentForm(false);
        setRegistrationStep('student-info');
        
        // Reset form
        setStudentFormData({
          first_name: '',
          last_name: '',
          date_of_birth: '',
          grade: '',
          school: '',
          emergency_contact: '',
          emergency_phone: '',
          medical_conditions: '',
          allergies: '',
          tier: 'foundations',
          create_login: false,
          login_email: '',
          force_password_change: false
        });
        
        console.log('✅ Student registration complete. Consent forms can be signed later from the Consent Forms tab.');
      } else {
        const errorData = await response.json();
        console.error('Failed to create student:', errorData);
        alert('Failed to enroll student. Please try again.');
      }
    } catch (error) {
      console.error('Error creating student:', error);
      alert('Error enrolling student. Please try again.');
    } finally {
      setStudentFormLoading(false);
    }
  };

  const handleStudentFormChange = (field: string, value: string | boolean) => {
    setStudentFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddNewChild = () => {
    // Show simple student registration form (not consent form)
    setShowStudentForm(true);
    setEnrollmentSuccess(false);
    setRegistrationStep('student-info'); // Start with simple student form
    setNewlyRegisteredStudent(null);
    setNewlyCreatedConsentForms([]);
    signingConsentFormIndexRef.current = 0;
    setSigningConsentFormIndex(0);
    // Reset form data
    setStudentFormData({
      first_name: '',
      last_name: '',
      date_of_birth: '',
      grade: '',
      school: '',
      emergency_contact: '',
      emergency_phone: '',
      medical_conditions: '',
      allergies: '',
      tier: 'foundations',
      create_login: false,
      login_email: '',
      force_password_change: false
    });
  };
  
  // Handle consent form signing during registration
  const handleRegistrationConsentFormSign = async (formId: string, signatureData: string) => {
    try {
      // Use ref to get current index to avoid stale closure issues
      const currentIndex = signingConsentFormIndexRef.current;
      const totalForms = newlyCreatedConsentForms.length;
      
      console.log('🔍 Signing consent form during registration:', formId);
      console.log('🔍 Current form index (from ref):', currentIndex);
      console.log('🔍 Current form index (from state):', signingConsentFormIndex);
      console.log('🔍 Total forms:', totalForms);
      
      // Sign form WITHOUT refreshing consent forms (skipRefresh = true)
      const response = await handleSignConsentForm(formId, signatureData, true);
      
      if (response && response.success) {
        console.log('✅ Consent form signed successfully:', response);
        
        // Calculate next index using ref value
        const nextIndex = currentIndex + 1;
        
        console.log(`🔍 Checking if more forms: ${currentIndex} < ${totalForms - 1}`);
        
        if (currentIndex < totalForms - 1) {
          // Move to next form - update both ref and state
          console.log(`✅ Moving to next consent form: ${nextIndex + 1} of ${totalForms}`);
          
          // Update ref first (synchronous)
          signingConsentFormIndexRef.current = nextIndex;
          
          // Update state (asynchronous)
          setSigningConsentFormIndex(nextIndex);
          
          console.log(`🔄 Updated ref to: ${signingConsentFormIndexRef.current}`);
          
          // Return response with continue flag for registration mode
          return {
            ...response,
            continue_to_next: true
          };
        } else {
          // All consent forms signed - complete registration
          console.log('✅ All consent forms signed, completing registration');
          
          // Refresh consent forms now that all are signed
          await fetchConsentForms();
          
          setRegistrationStep('complete');
          // Don't set enrollmentSuccess here - the registration-complete div will handle the display
          
          // Reset form
          setStudentFormData({
            first_name: '',
            last_name: '',
            date_of_birth: '',
            grade: '',
            school: '',
            emergency_contact: '',
            emergency_phone: '',
            medical_conditions: '',
            allergies: '',
            tier: 'foundations',
            create_login: false,
            login_email: '',
            force_password_change: false
          });
          
          // Refresh dashboard
          await fetchDashboardData();
        }
        return response;
      }
      return null;
    } catch (error) {
      console.error('❌ Error signing consent form during registration:', error);
      throw error;
    }
  };

  // Course registration handlers
  const handleOpenCourseRegistration = (child: Child) => {
    console.log('🔍 handleOpenCourseRegistration called with child:', child);
    setSelectedStudentForRegistration(child);
    setShowCourseRegistration(true);
    console.log('🔍 Course registration form should now be visible');
  };

  const handleViewProgramDetails = async (child: Child) => {
    setSelectedChild(child);
    setLoading(true);
    try {
        const response = await axios.get(`${config.BACKEND_URL}/api/program-details/${user?.email}/${child.id}`);
      if (response.data.success) {
        setProgramDetails(response.data);
        setActiveTab('program-details');
      } else {
        console.error('Failed to fetch program details:', response.data.message);
        setError('Failed to fetch program details');
      }
    } catch (error) {
      console.error('Error fetching program details:', error);
      setError('Failed to fetch program details');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseCourseRegistration = () => {
    setShowCourseRegistration(false);
    setSelectedStudentForRegistration(null);
  };

  const handleCourseRegistrationSuccess = async (registrationData: any) => {
    console.log('Course registration successful:', registrationData);
    setShowCourseRegistration(false);
    setSelectedStudentForRegistration(null);
    // Refresh dashboard data
    fetchDashboardData();
    fetchUpcomingSessions();
    
    // If we're in program details view, refresh the program details
    if (activeTab === 'program-details' && selectedChild) {
      try {
        const response = await axios.get(`${config.BACKEND_URL}/api/program-details/${user?.email}/${selectedChild.id}`);
        if (response.data.success) {
          setProgramDetails(response.data);
        }
      } catch (error) {
        console.error('Error refreshing program details:', error);
      }
    }
  };

  // Fetch camp discount rules from API
  const fetchCampDiscountRules = async () => {
    try {
      const response = await fetch(`${config.BACKEND_URL}/api/packages/camp-discounts/all`);
      if (response.ok) {
        const data = await response.json();
        setCampDiscountRules(data.camp_discount_rules || []);
      } else {
        console.error('Failed to fetch camp discount rules');
      }
    } catch (err: any) {
      console.error('Error fetching camp discount rules:', err);
    }
  };

  // Fetch session package templates from API
  const fetchSessionPackageTemplates = async () => {
    try {
      const response = await fetch(`${config.BACKEND_URL}/api/packages/templates`);
      if (response.ok) {
        const data = await response.json();
        setSessionPackageTemplates(data.session_package_templates || []);
      } else {
        console.error('Failed to fetch session package templates');
      }
    } catch (err: any) {
      console.error('Error fetching session package templates:', err);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching comprehensive parent dashboard data from database...');
      console.log('🔍 User object:', user);
      console.log('🔍 ParentDashboard state before fetchDashboardData - showStripePayment:', showStripePayment, 'stripePaymentData is null:', stripePaymentData === null);
      
      // Get parent email from user context or localStorage
      let parentEmail = user?.email;
      if (!parentEmail) {
        // Try to get from localStorage or use a default for testing
        const storedEmail = localStorage.getItem('userEmail');
        if (storedEmail) {
          parentEmail = storedEmail;
          console.log('🔍 Using email from localStorage:', parentEmail);
        } else {
          // For testing, try renjithmadhavan@gmail.com
          parentEmail = 'renjithmadhavan@gmail.com';
          console.log('🔍 Using default email for testing:', parentEmail);
        }
      }
      
      if (!parentEmail) {
        throw new Error('No user email available');
      }
      console.log('🔍 Using parent email:', parentEmail);
      
      // Fetch all dashboard data from API - use full backend URL
      const response = await fetch(`${config.BACKEND_URL}/api/parent-dashboard/${parentEmail}`);
      console.log('🔍 API response status:', response.status);
      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data');
      }
      
      const data = await response.json();
      console.log('🔍 API response data:', data);
      console.log('🔍 Children count:', data.children?.length || 0);
      console.log('🔍 Programs count:', data.programs?.length || 0);
      
      // Set all the data from the API response
      const childrenData = data.children || [];
      const programsData = data.programs || [];
      
      console.log('🔍 Programs received from API:', programsData.length);
      console.log('🔍 All programs:', programsData.map((p: Program) => `${p.name} (tier: ${p.tier}, type: ${p.programType})`));
      const privateGroupProgram = programsData.find((p: Program) => p.name === 'Private Group' || p.tier === 'all');
      console.log('🔍 Private Group program found:', privateGroupProgram ? `${privateGroupProgram.name} (tier: ${privateGroupProgram.tier})` : 'NOT FOUND');
      
      setChildren(childrenData);
      setSessionPackages(data.sessionPackages || []);
      setPrograms(programsData);
      setCourses(data.courses || []);
      // Do not set camps from parent-dashboard response; camps are fetched via fetchCamps()
      // to keep tab behavior consistent after enrollment/payment refresh.
      setAnnouncements(data.announcements || []);
      setFamilyPlan(data.familyPlan);
      setAttendanceRecords(data.attendanceRecords || []);
      setProgressReports(data.progressReports || []);
      setMessages(data.messages || []);
      
      console.log('✅ Dashboard state updated:', {
        children: childrenData.length,
        childrenData: childrenData,
        programs: programsData.length,
        courses: (data.courses || []).length
      });
      
      // Force update to ensure pending actions section appears correctly
      if (childrenData.length === 0) {
        console.log('⚠️ No children found - showing register student prompt');
        console.log('⚠️ Pending actions section should be visible');
      } else {
        console.log(`✅ Found ${childrenData.length} children - register prompt will NOT show`);
      }
      
      // Set referral data (this might need a separate API call)
      setReferral({
        id: '1',
        referralCode: 'BUDDY2024',
        status: 'active',
        discountAmount: 15.00,
        requirements: 'Both students must complete at least 2 full months of sessions',
        expiryDate: '2025-12-31'
      });
      
      console.log('✅ Dashboard data loaded successfully from database');
      console.log('🔍 ParentDashboard state after fetchDashboardData - showStripePayment:', showStripePayment, 'stripePaymentData is null:', stripePaymentData === null);
      
      // TODO: Remove mock data after API is fully tested
      /*
      // Mock comprehensive data for OARA Makerspace (FALLBACK - REMOVE AFTER TESTING)
      const mockChildren: Child[] = [
        {
          id: '1',
          firstName: 'Alex',
          lastName: 'Wilson',
          email: 'alex.wilson@example.com',
          dateOfBirth: '2015-03-15',
          age: 9,
          gradeLevel: '4',
          tier: 'foundations',
          enrollmentDate: '2024-09-01',
          status: 'active',
          trialSessionUsed: true,
          slackUsername: 'alex_wilson',
          learningGoals: 'Learn robotics and programming basics',
          profileNotes: 'Very creative, loves building things'
        },
        {
          id: '2',
          firstName: 'Emma',
          lastName: 'Wilson',
          email: 'emma.wilson@example.com',
          dateOfBirth: '2012-08-22',
          age: 12,
          gradeLevel: '7',
          tier: 'innovators',
          enrollmentDate: '2024-09-01',
          status: 'active',
          trialSessionUsed: true,
          slackUsername: 'emma_wilson',
          learningGoals: 'Advanced coding and robotics',
          profileNotes: 'Excellent problem solver, natural leader'
        }
      ];
      
      setChildren(mockChildren);

      // Mock session packages with OARA pricing (same-tier sharing only)
      const mockPackages: SessionPackage[] = [
        {
          id: '1',
          packageType: '10-Session Pack',
          tier: 'foundations',
          totalSessions: 10,
          usedSessions: 3,
          remainingSessions: 7,
          purchaseDate: '2024-09-01',
          expiryDate: '2025-06-25',
          originalPrice: 200.00,
          discountApplied: 20.00,
          finalPrice: 180.00,
          paymentStatus: 'paid',
          isShared: true,
          sharedWithStudents: [], // Only Alex (foundations) can share this
          status: 'active'
        },
        {
          id: '2',
          packageType: '5-Session Pack',
          tier: 'innovators',
          totalSessions: 5,
          usedSessions: 2,
          remainingSessions: 3,
          purchaseDate: '2024-10-01',
          expiryDate: '2025-06-25',
          originalPrice: 120.00,
          discountApplied: 0.00,
          finalPrice: 120.00,
          paymentStatus: 'paid',
          isShared: true,
          sharedWithStudents: [], // Only Emma (innovators) can share this
          status: 'active'
        }
      ];
      
      setSessionPackages(mockPackages);

      // Mock programs
      const mockPrograms: Program[] = [
        {
          id: '1',
          name: 'After-School Program',
          type: 'after-school',
          tier: 'all',
          ageRange: 'K-12',
          description: 'Regular after-school sessions with flexible timing',
          schedule: 'Mon-Fri, 3:30 PM - 6:30 PM',
          price: 0,
          memberDiscount: 0,
          capacity: 50,
          enrolledCount: 35
        },
        {
          id: '2',
          name: 'Homeschool Program',
          type: 'homeschool',
          tier: 'all',
          ageRange: 'K-12',
          description: 'Specialized homeschool sessions',
          schedule: 'Mon-Fri, 1:30 PM - 3:00 PM',
          price: 0,
          memberDiscount: 0,
          capacity: 30,
          enrolledCount: 15
        },
        {
          id: '3',
          name: 'Free Chess Club',
          type: 'drop-in',
          tier: 'all',
          ageRange: '6-18',
          description: 'Free chess club for all ages',
          schedule: 'Tuesdays, 4:00 PM - 6:00 PM',
          price: 0,
          memberDiscount: 0,
          capacity: 20,
          enrolledCount: 12
        }
      ];
      
      setPrograms(mockPrograms);

      // Mock camps
      const mockCamps: Camp[] = [
        {
          id: '1',
          name: 'Summer Robotics Camp',
          dates: 'July 15-19, 2025',
          ageRange: '8-14',
          basePrice: 200.00,
          memberDiscount: 20.00,
          capacity: 20,
          enrolledCount: 8,
          description: 'Build and program robots for 5 days',
          requirements: ['Basic programming knowledge', 'Parent permission']
        },
        {
          id: '2',
          name: 'Winter Coding Bootcamp',
          dates: 'December 23-27, 2024',
          ageRange: '10-16',
          basePrice: 150.00,
          memberDiscount: 15.00,
          capacity: 15,
          enrolledCount: 12,
          description: 'Intensive coding workshop',
          requirements: ['Laptop required', 'Basic computer skills']
        }
      ];
      
      setCamps(mockCamps);

      // Mock family plan
      const mockFamilyPlan: FamilyPlan = {
        id: '1',
        totalChildren: 2,
        discountApplied: 25.00,
        savingsAmount: 50.00,
        isActive: true
      };
      
      setFamilyPlan(mockFamilyPlan);

      // Mock referral
      const mockReferral: Referral = {
        id: '1',
        referralCode: 'BUDDY2024',
        status: 'active',
        discountAmount: 15.00,
        requirements: 'Both students must complete at least 2 full months of sessions',
        expiryDate: '2025-12-31'
      };
      
      setReferral(mockReferral);

      // Mock attendance records
      const mockAttendance: AttendanceRecord[] = [
        {
          id: '1',
          date: '2024-10-15',
          status: 'present',
          programName: 'After-School Program',
          notes: 'Great participation in robotics project'
        },
        {
          id: '2',
          date: '2024-10-14',
          status: 'present',
          programName: 'Homeschool Program',
          notes: 'Excellent work on coding project'
        },
        {
          id: '3',
          date: '2024-10-10',
          status: 'late',
          programName: 'After-School Program',
          notes: 'Arrived 5 minutes late'
        }
      ];
      
      setAttendanceRecords(mockAttendance);

      // Mock progress reports
      const mockProgress: ProgressReport[] = [
        {
          id: '1',
          childName: 'Alex Wilson',
          programName: 'After-School Program',
          date: '2024-10-10',
          skills: ['Robot Assembly', 'Programming Logic', 'Problem Solving'],
          achievements: ['Completed first robot build', 'Helped classmates with coding'],
          areasForImprovement: ['Time management', 'Documentation skills'],
          teacherNotes: 'Alex shows excellent creativity and problem-solving skills. He is very helpful to other students.',
          overallRating: 'excellent'
        }
      ];
      
      setProgressReports(mockProgress);

      // Mock messages
      const mockMessages: Message[] = [
        {
          id: '1',
          from: 'Sarah Johnson (Teacher)',
          to: 'Robert Wilson (Parent)',
          subject: 'Alex\'s Progress Update',
          message: 'Alex has been doing exceptionally well in robotics class. His robot building skills have improved significantly.',
          date: '2024-10-12',
          read: false,
          priority: 'medium'
        },
        {
          id: '2',
          from: 'System',
          to: 'Robert Wilson (Parent)',
          subject: 'Upcoming Parent-Teacher Conference',
          message: 'Reminder: Parent-teacher conference scheduled for October 20th at 3:00 PM.',
          date: '2024-10-08',
          read: true,
          priority: 'high'
        }
      ];
      
      setMessages(mockMessages);
      */

    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
      setError(err.response?.data?.detail || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendanceRecords = async (studentId?: string, startDate?: string, endDate?: string) => {
    if (!user?.email) return;
    
    try {
      console.log(`🔍 Fetching attendance records for parent: ${user.email}`);
      if (studentId) {
        console.log(`🔍 Filtering by student: ${studentId}`);
      }
      if (startDate || endDate) {
        console.log(`🔍 Date filter: ${startDate || 'no start'} to ${endDate || 'no end'}`);
      }
      
      // Build query parameters
      const params = new URLSearchParams();
      if (studentId) {
        params.append('student_id', studentId);
      }
      if (startDate) {
        params.append('start_date', startDate);
      }
      if (endDate) {
        params.append('end_date', endDate);
      }
      // Default limit: current date + previous 15 records (handled by backend if no dates provided)
      
      const url = `${config.BACKEND_URL}/api/parent-attendance/parent-attendance/${user.email}${params.toString() ? '?' + params.toString() : ''}`;
      
      const response = await axios.get(url);
      
      if (response.data.success) {
        console.log('✅ Attendance records fetched successfully:', response.data);
        setAttendanceRecords(response.data.attendance_records || []);
        // Only update attendanceChildren if API returns children, otherwise keep existing
        if (response.data.children && response.data.children.length > 0) {
          setAttendanceChildren(response.data.children);
        } else if (attendanceChildren.length === 0 && children.length > 0) {
          // Fallback to main children array if attendanceChildren is empty
          setAttendanceChildren(children);
        }
      } else {
        console.error('❌ Failed to fetch attendance records:', response.data.message);
        setError('Failed to fetch attendance records');
        // Fallback to main children array if fetch fails
        if (attendanceChildren.length === 0 && children.length > 0) {
          setAttendanceChildren(children);
        }
      }
    } catch (error) {
      console.error('❌ Error fetching attendance records:', error);
      setError('Failed to fetch attendance records');
    }
  };

  const fetchConsentForms = async () => {
    if (!user?.email) {
      console.log('⚠️ fetchConsentForms: No user email available');
      return;
    }
    
    try {
      const timestamp = new Date().toISOString();
      console.log(`🔍 [${timestamp}] ========== FETCH CONSENT FORMS START ==========`);
      console.log(`🔍 [${timestamp}] Fetching consent forms for parent: ${user.email}`);
      console.log(`🔍 [${timestamp}] Backend URL: ${config.BACKEND_URL}`);
      console.log(`🔍 [${timestamp}] Full endpoint: ${config.BACKEND_URL}/api/consent-forms/parent-consent-forms/${user.email}`);
      console.log(`🔍 [${timestamp}] Current state - pendingConsentForms.length: ${pendingConsentForms.length}`);
      
      // Clear existing state first to prevent stale data
      console.log(`🔍 [${timestamp}] Clearing existing state...`);
      setConsentForms([]);
      setPendingConsentForms([]);
      
      // Use full backend URL to ensure it goes to the backend, not nginx
      const response = await axios.get(`${config.BACKEND_URL}/api/consent-forms/parent-consent-forms/${user.email}`, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        params: {
          _t: Date.now() // Add timestamp to prevent caching
        }
      });
      
      console.log(`🔍 [${timestamp}] Response status: ${response.status}`);
      console.log(`🔍 [${timestamp}] Response data keys:`, Object.keys(response.data || {}));
      
      if (response.data && response.data.success) {
        const allForms = response.data.consent_forms || [];
        const pendingForms = allForms.filter((form: ConsentForm) => form.status === 'PENDING');
        const signedForms = allForms.filter((form: ConsentForm) => form.status === 'SIGNED');
        
        console.log(`✅ [${timestamp}] Consent forms fetched successfully`);
        console.log(`🔍 [${timestamp}] Total forms: ${allForms.length}`);
        console.log(`🔍 [${timestamp}] Pending forms: ${pendingForms.length}`);
        console.log(`🔍 [${timestamp}] Signed forms: ${signedForms.length}`);
        console.log(`🔍 [${timestamp}] Pending form IDs:`, pendingForms.map((f: ConsentForm) => f.id));
        console.log(`🔍 [${timestamp}] All form details:`, allForms.map((f: ConsentForm) => ({ 
          id: f.id, 
          status: f.status, 
          student_id: f.student_id,
          student_name: f.student_name,
          signed_at: f.signed_at
        })));
        
        // Log state before update
        console.log(`🔍 [${timestamp}] State BEFORE update - pendingConsentForms.length: ${pendingConsentForms.length}`);
        
        console.log(`🔍 [${timestamp}] Setting state - allForms: ${allForms.length}, pendingForms: ${pendingForms.length}`);
        setConsentForms(allForms);
        setPendingConsentForms(pendingForms);
        
        // Log state after update (use setTimeout to check after state update)
        setTimeout(() => {
          console.log(`🔍 [${timestamp}] State AFTER update (100ms delay) - pendingConsentForms should be: ${pendingForms.length}`);
        }, 100);
        
        console.log(`✅ [${timestamp}] State updated - pendingConsentForms set to ${pendingForms.length} forms`);
        console.log(`🔍 [${timestamp}] ========== FETCH CONSENT FORMS END ==========`);
      } else {
        console.error(`❌ [${timestamp}] Failed to fetch consent forms:`, response.data?.message || 'Unknown error');
        console.error(`❌ [${timestamp}] Response data:`, response.data);
        setError('Failed to fetch consent forms');
        setConsentForms([]);
        setPendingConsentForms([]);
      }
    } catch (error: any) {
      const timestamp = new Date().toISOString();
      console.error(`❌ [${timestamp}] Error fetching consent forms:`, error);
      console.error(`❌ [${timestamp}] Error details:`, {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        headers: error.response?.headers
      });
      setError('Failed to fetch consent forms');
      setConsentForms([]);
      setPendingConsentForms([]);
    }
  };

  const fetchSessionDetails = async (studentId: string) => {
    try {
      console.log(`🔍 Fetching session details for student: ${studentId}`);
      
      const response = await fetch(`${config.BACKEND_URL}/api/students/${studentId}/sessions`);
      if (response.ok) {
        const data = await response.json();
        setSessionDetails(data.sessions || []);
        console.log('✅ Session details fetched successfully:', data);
      } else {
        console.error('❌ Failed to fetch session details:', response.statusText);
        setError('Failed to fetch session details');
      }
    } catch (error) {
      console.error('❌ Error fetching session details:', error);
      setError('Failed to fetch session details');
    }
  };

  const handleSignConsentForm = async (formId: string, signatureData: string, skipRefresh: boolean = false) => {
    try {
      console.log(`🔍 Signing consent form: ${formId}, skipRefresh: ${skipRefresh}`);
      console.log(`🔍 Endpoint: ${config.BACKEND_URL}/api/consent-forms/sign`);
      console.log(`🔍 Request data:`, { form_id: formId, signature_data: signatureData?.substring(0, 50) + '...' });
      
      const response = await axios.post(`${config.BACKEND_URL}/api/consent-forms/sign`, {
        form_id: formId,
        signature_data: signatureData,
        ip_address: 'client-ip' // In real implementation, get actual IP
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 30000
      });
      
      console.log('🔍 Sign response:', response.data);
      
      if (response.data && response.data.success) {
        console.log('✅ Consent form signed successfully');
        console.log('🔍 Response data:', response.data);
        
        // Force refresh from server to get the latest state
        console.log('🔄 Force refreshing consent forms from server...');
        await fetchConsentForms();
        console.log('✅ Consent forms refreshed from server');
        
        // Also refresh dashboard data to ensure everything is in sync
        await fetchDashboardData();
        console.log('✅ Dashboard data refreshed');
        
        // Return the response data for the modal to use
        return response.data;
      } else {
        console.error('❌ Failed to sign consent form:', response.data?.message || response.data);
        setError(response.data?.message || 'Failed to sign consent form');
        throw new Error(response.data?.message || 'Failed to sign consent form');
      }
    } catch (error: any) {
      console.error('❌ Error signing consent form:', error);
      console.error('❌ Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || error.message || 'Failed to sign consent form';
      setError(errorMessage);
      throw error;
    }
  };

  const handleSendMessage = async () => {
    try {
      const newMsg: Message = {
        id: Date.now().toString(),
        from: `${user?.email} (Parent)`,
        to: newMessage.to,
        subject: newMessage.subject,
        message: newMessage.message,
        date: new Date().toISOString().split('T')[0],
        read: false,
        priority: 'medium'
      };
      
      setMessages(prev => [newMsg, ...prev]);
      setNewMessage({ to: '', subject: '', message: '' });
      setShowMessageForm(false);
      
      console.log('✅ Message sent successfully');
    } catch (err: any) {
      console.error('Error sending message:', err);
      setError('Failed to send message');
    }
  };

  const markMessageAsRead = (messageId: string) => {
    setMessages(prev => 
      prev.map(msg => 
        msg.id === messageId ? { ...msg, read: true } : msg
      )
    );
  };

  const handlePackagePurchase = async (child: Child, packageType: any) => {
    try {
      setSelectedChildForPurchase(child);
      setSelectedPackageType(packageType);
      
      // Calculate final price with family discount
      const finalPrice = child.tier === 'innovators' ? packageType.price * 0.75 : packageType.price;
      
      // Set up Stripe payment
      setStripePaymentData({
        amount: finalPrice,
        description: `Session Package: ${packageType.name} (${packageType.session_count} sessions)`,
        metadata: {
          type: 'session_package',
          package_id: packageType.id,
          student_id: child.id,
          student_name: `${child.firstName} ${child.lastName}`,
          parent_email: user?.email, // Add parent email for backend lookup
          tier: child.tier,
          session_count: packageType.session_count
        }
      });
      
      setShowStripePayment(true);
    } catch (err: any) {
      console.error('Error setting up payment:', err);
      setError('Failed to set up payment');
    }
  };

  const handleStripePaymentSuccess = (paymentIntent: any) => {
    console.log('🎉 ParentDashboard handleStripePaymentSuccess called with:', paymentIntent);
    console.log('🎉 Current showStripePayment state (before fetch):', showStripePayment);
    console.log('🎉 Current stripePaymentData (before fetch):', stripePaymentData);
    
    // Store payment success at ParentDashboard level to persist across re-renders
    setPaymentSuccess(paymentIntent);
    console.log('🎉 ParentDashboard setPaymentSuccess called with:', paymentIntent);
    
    // Refresh dashboard data
    fetchDashboardData();
    console.log('🎉 ParentDashboard handleStripePaymentSuccess - fetchDashboardData called.');
    console.log('🎉 ParentDashboard handleStripePaymentSuccess - State after fetchDashboardData call: showStripePayment:', showStripePayment, 'stripePaymentData is null:', stripePaymentData === null);
  };

  const handleStripePaymentError = (error: string) => {
    console.error('Payment failed:', error);
    setError(`Payment failed: ${error}`);
  };

  const handleStripePaymentCancel = () => {
    setShowStripePayment(false);
    setStripePaymentData(null);
  };

  const handleStripePaymentBackToDashboard = () => {
    console.log('🏠 ParentDashboard handleStripePaymentBackToDashboard called');
    setShowStripePayment(false);
    setStripePaymentData(null);
    setPaymentSuccess(null);
    setSelectedChildForPurchase(null);
    setSelectedPackageType(null);
  };

  const getTierEmoji = (tier: string) => {
    switch (tier) {
      case 'foundations': return '🌱';
      case 'innovators': return '🚀';
      case 'pioneers': return '🌟';
      default: return '👶';
    }
  };

  const getTierName = (tier: string) => {
    switch (tier) {
      case 'foundations': return 'Foundations (K-5)';
      case 'innovators': return 'Innovators (6-8)';
      case 'pioneers': return 'Pioneers (9-12+)';
      default: return 'Unknown';
    }
  };

  const isChildEligibleForAddSession = (child: Child) => {
    const normalize = (value: string) =>
      (value || '')
        .toLowerCase()
        .replace(/[\s-]+/g, '_')
        .trim();

    const isEligibleType = (value: string) => {
      const normalized = normalize(value);
      return normalized === 'after_school' || normalized === 'home_school' || normalized === 'homeschool';
    };

    const enrollments = child.programEnrollments || [];
    const hasEligibleEnrollment = enrollments.some((enrollment) => isEligibleType(enrollment.programType || ''));
    if (hasEligibleEnrollment) return true;

    // Fallback: some student records may have an active billing plan but stale/missing enrollment payload.
    const plans = child.paymentPlans || [];
    const hasEligiblePlan = plans.some((plan) => {
      const status = normalize(plan.status || '');
      const planType = normalize(plan.planType || '');
      const isActiveLike = status === 'active' || status === 'paused';
      return isActiveLike && (isEligibleType(planType) || planType.includes('after_school') || planType.includes('home_school'));
    });

    return hasEligiblePlan;
  };

  const parseDateOnlyLocal = (dateInput: string) => {
    const datePart = (dateInput || '').split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    return new Date(year, (month || 1) - 1, day || 1);
  };

  const getCampAudienceLabel = (camp: Camp) => {
    const tiers = camp.applicable_tiers && camp.applicable_tiers.length > 0
      ? camp.applicable_tiers
      : (camp.tier && camp.tier !== 'all' ? [camp.tier] : []);

    const labels = tiers.map((tier) => {
      if (tier === 'foundations') return 'Elementary School';
      if (tier === 'innovators') return 'Middle School';
      if (tier === 'pioneers') return 'High School';
      return tier;
    });

    return labels.length > 0 ? Array.from(new Set(labels)).join(', ') : 'All';
  };

  // Format enrollment date compactly (e.g., "Dec '25")
  const formatEnrollmentDateCompact = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const month = date.toLocaleDateString('en-US', { month: 'short' });
      const year = date.toLocaleDateString('en-US', { year: '2-digit' });
      return `${month} '${year}`;
    } catch {
      return dateString;
    }
  };

  // Handle carousel scroll to update pagination dots
  const handleChildrenCarouselScroll = () => {
    if (childrenCarouselRef.current) {
      const scrollLeft = childrenCarouselRef.current.scrollLeft;
      const containerWidth = childrenCarouselRef.current.offsetWidth;
      const cardWidth = containerWidth * 0.9; // 90% width
      const gap = 16; // gap between cards
      const padding = containerWidth * 0.05; // 5% padding on each side
      const scrollPosition = scrollLeft + padding;
      const newIndex = Math.round(scrollPosition / (cardWidth + gap));
      setChildrenCarouselIndex(Math.min(newIndex, children.length)); // Cap at last index (Add New Child)
    }
  };

  const getPackagePricing = (tier: string) => {
    // Filter templates by tier and return in the expected format
    const tierTemplates = sessionPackageTemplates.filter(template => template.tier === tier);
    
    return tierTemplates.map(template => ({
      id: template.id,
      name: template.name,
      sessions: template.session_count,
      price: template.price,
      perSession: template.per_session_price,
      duration: template.duration_description,
      features: template.features || [],
      description: template.description
    }));
  };

  const focusPendingActions = () => {
    // Check what pending items exist and navigate to the appropriate tab
    if (pendingConsentForms.length > 0) {
      // Navigate to consent forms tab if there are pending consent forms
      setActiveTab('consent-forms');
      // Close mobile nav drawer if open
      setShowMobileNavDrawer(false);
      // Scroll to top of the page
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else if (!children || children.length === 0) {
      // Navigate to overview and scroll to register student section
      setActiveTab('overview');
      // Wait for tab content to render, then scroll
      let retryCount = 0;
      const maxRetries = 5;
      
      const scrollToPendingActions = () => {
        const section = document.getElementById('pending-actions-card') || 
                        document.querySelector('.register-student-card');
        if (section) {
          const isMobile = window.innerWidth <= 768;
          if (isMobile) {
            // For mobile: manually calculate scroll position to show section header at top
            const currentScrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
            const sectionRect = section.getBoundingClientRect();
            const sectionAbsoluteTop = currentScrollY + sectionRect.top;
            const targetScrollY = sectionAbsoluteTop - 15;
            
            window.scrollTo({
              top: Math.max(0, targetScrollY),
              behavior: 'smooth'
            });
          } else {
            // For desktop: use standard scrollIntoView
            section.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
          
          // Close mobile nav drawer if open
          setShowMobileNavDrawer(false);
        } else {
          // Retry if section not found
          retryCount++;
          if (retryCount < maxRetries) {
            setTimeout(scrollToPendingActions, 50);
          }
        }
      };
      
      // Initial delay to allow tab switch to complete
      setTimeout(scrollToPendingActions, 150);
    } else {
      // No pending items, just stay on overview
      setActiveTab('overview');
      setShowMobileNavDrawer(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="parent-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading OARA parent dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="parent-dashboard">
        <div className="error-container">
          <h3>Error Loading Dashboard</h3>
          <p>{error}</p>
          <button onClick={fetchDashboardData} className="btn btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const pendingConsentCount = pendingConsentForms.length;
  const needsStudentRegistration = !children || children.length === 0;
  const totalPendingActions = pendingConsentCount + (needsStudentRegistration ? 1 : 0);
  const hasPendingActions = totalPendingActions > 0;
  const displayName =
    (user?.attributes?.first_name as string) ||
    (user?.attributes?.firstName as string) ||
    (user?.attributes?.given_name as string) ||
    (user?.attributes?.name as string) ||
    (user?.email ? user.email.split('@')[0] : 'Parent');
  const heroQuickActions = [
    {
      label: 'Register Student',
      icon: '👶',
      onClick: handleAddNewChild
    },
    {
      label: 'Buy Sessions',
      icon: '💳',
      onClick: () => setActiveTab('packages')
    }
  ];

  // Navigation tabs configuration
  const allNavTabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'children', label: 'Children', icon: '👶' },
    { id: 'programs', label: 'Programs', icon: '🏫' },
    { id: 'camps', label: 'Camps', icon: '🏕️' },
    { id: 'packages', label: 'Session Packs', icon: '📦' },
    { id: 'consent-forms', label: 'Consent Forms', icon: '📝' },
    { id: 'billing', label: 'Billing', icon: '💳' },
    // { id: 'courses', label: 'Courses', icon: '📚' }, // Hidden from parent view
    { id: 'attendance', label: 'Attendance', icon: '📅' },
    { id: 'progress', label: 'Progress', icon: '📈' },
    { id: 'messages', label: 'Messages', icon: '💬' }
  ];

  // Primary tabs for mobile bottom nav (most frequently used)
  const primaryNavTabs = allNavTabs.slice(0, 4); // Overview, Children, Programs, Camps
  const secondaryNavTabs = allNavTabs.slice(4); // Rest of the tabs

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId as any);
    setShowMobileNavDrawer(false);
    // Scroll to top when changing tabs on mobile
    if (window.innerWidth <= 768) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSignOut = async () => {
    try {
      setShowMobileNavDrawer(false);
      await signOut();
      window.location.href = '/login';
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };
  const heroSummaryStats = [
    { label: 'Students', value: children.length, helper: needsStudentRegistration ? 'Add your first learner' : 'Active profiles' },
    { 
      label: '⛺ Spring and Pro D Camps', 
      value: '', 
      helper: 'Click to view camps', 
      isClickable: true, 
      onClick: () => {
        setActiveTab('camps');
        // Scroll to camps content (past hero section)
        setTimeout(() => {
          const dashboardContent = document.querySelector('.dashboard-content');
          if (dashboardContent) {
            const contentTop = dashboardContent.getBoundingClientRect().top + window.pageYOffset;
            window.scrollTo({ top: contentTop - 20, behavior: 'smooth' });
          }
        }, 100);
      }
    }
  ];

  return (
    <div className="parent-dashboard">
      <div className="parent-dashboard-hero">
        <div className="hero-body">
          <p className="hero-eyebrow">Learning Management Portal</p>
          <h1>Welcome back, {displayName}</h1>
          <p className="hero-subtitle">
            Manage students, consent forms, and billing in one modern workspace.
          </p>
          <div className="hero-pill-row">
            <span className="hero-chip">{children.length} student{children.length === 1 ? '' : 's'}</span>
            <span className={`hero-chip ${hasPendingActions ? 'hero-chip-alert' : 'hero-chip-success'}`}>
              {hasPendingActions ? `${totalPendingActions} pending action${totalPendingActions === 1 ? '' : 's'}` : 'All caught up 🎉'}
            </span>
          </div>
          <div className="hero-links">
            <a href="https://oara.slack.com" target="_blank" rel="noopener noreferrer">
              💬 Join OARA Slack
            </a>
            <span>•</span>
            <a href="mailto:oarayouthinnovationlab@gmail.com">📧 Email Support</a>
          </div>
          {hasPendingActions && (
            <button className="hero-primary-action" onClick={focusPendingActions}>
              Review pending items
            </button>
          )}
        </div>
        <div className="hero-summary">
          {heroSummaryStats.map((stat: any) => (
            <div 
              className={`hero-summary-card ${stat.isClickable ? 'clickable' : ''}`}
              key={stat.label}
              onClick={stat.onClick}
              style={stat.isClickable ? { 
                cursor: 'pointer',
                background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
                color: 'white',
                transition: 'transform 0.2s, box-shadow 0.2s'
              } : {}}
              onMouseEnter={(e) => {
                if (stat.isClickable) {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(102, 126, 234, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (stat.isClickable) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '';
                }
              }}
            >
              <p className="hero-summary-label">{stat.label}</p>
              <p className="hero-summary-value">{stat.value}</p>
              <span className="hero-summary-helper">{stat.helper}</span>
            </div>
          ))}
        </div>
      </div>


      {/* Desktop Navigation Tabs */}
      <div className="dashboard-nav desktop-nav">
        {allNavTabs.map(tab => (
          <button 
            key={tab.id}
            className={`nav-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => handleTabChange(tab.id)}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="mobile-bottom-nav">
        {primaryNavTabs.map(tab => (
          <button
            key={tab.id}
            className={`mobile-nav-item ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => handleTabChange(tab.id)}
          >
            <span className="mobile-nav-icon">{tab.icon}</span>
            <span className="mobile-nav-label">{tab.label}</span>
          </button>
        ))}
        <button
          className={`mobile-nav-item mobile-nav-more ${showMobileNavDrawer ? 'active' : ''}`}
          onClick={() => setShowMobileNavDrawer(!showMobileNavDrawer)}
        >
          <span className="mobile-nav-icon">⋯</span>
          <span className="mobile-nav-label">More</span>
        </button>
      </div>

      {/* Mobile Navigation Drawer */}
      {showMobileNavDrawer && (
        <>
          <div className="mobile-nav-overlay" onClick={() => setShowMobileNavDrawer(false)}></div>
          <div className="mobile-nav-drawer">
            <div className="mobile-nav-drawer-header">
              <h3>More Options</h3>
              <button 
                className="mobile-nav-close"
                onClick={() => setShowMobileNavDrawer(false)}
              >
                ✕
              </button>
            </div>
            <div className="mobile-nav-drawer-content">
              {secondaryNavTabs.map(tab => (
                <button
                  key={tab.id}
                  className={`mobile-nav-drawer-item ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => handleTabChange(tab.id)}
                >
                  <span className="mobile-nav-drawer-icon">{tab.icon}</span>
                  <span>{tab.label}</span>
                </button>
              ))}
              <div className="mobile-nav-drawer-divider"></div>
              <button
                className="mobile-nav-drawer-item mobile-nav-drawer-signout"
                onClick={handleSignOut}
              >
                <span className="mobile-nav-drawer-icon">🚪</span>
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </>
      )}

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="dashboard-content">

          {/* Register Student Action - Show if no children */}
          {(!children || children.length === 0) && (
            <div className="pending-action-card register-student-card" style={{ marginBottom: '30px', padding: '30px', background: '#fff', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <div className="pending-action-header">
                <div className="action-icon" style={{ fontSize: '48px', marginBottom: '15px' }}>👶</div>
                <div className="action-content">
                  <h4 style={{ fontSize: '20px', marginBottom: '10px' }}>Register Your First Student</h4>
                  <p style={{ color: '#666', marginBottom: '10px' }}>You haven't registered any students yet. Register your child to start enrolling them in programs and classes.</p>
                  <p style={{ color: '#888', fontSize: '13px', marginBottom: '20px', fontStyle: 'italic' }}>
                    <strong>Quick & Easy:</strong> Registration takes just a few minutes. You can enroll in classes and camps immediately. 
                    Consent forms are required but can be completed later - you'll have time to complete them before your child's first session.
                  </p>
                </div>
              </div>
              <div className="pending-action-actions">
                <button 
                  className="btn btn-primary"
                  onClick={handleAddNewChild}
                >
                  ➕ Register Student
                </button>
              </div>
            </div>
          )}

          {/* Announcements Section - Only show if there are non-dismissed announcements */}
          {(() => {
            const visibleAnnouncements = announcements.filter(announcement => 
              !dismissedAnnouncements.has(announcement.id)
            );
            return visibleAnnouncements.length > 0 ? (
              <div className="announcements">
                <div className="announcements-header">
                  <h3>📢 Announcements</h3>
                </div>
                <div className="announcement-list">
                  {visibleAnnouncements.map((announcement) => (
                    <div key={announcement.id} className="announcement-item">
                      <div className="announcement-content">
                        <div className="announcement-header-row">
                          <h4>{announcement.title}</h4>
                          <button
                            className="announcement-dismiss-btn"
                            onClick={() => {
                              const newDismissed = new Set(dismissedAnnouncements);
                              newDismissed.add(announcement.id);
                              setDismissedAnnouncements(newDismissed);
                              // Save to localStorage
                              try {
                                localStorage.setItem('dismissedAnnouncements', JSON.stringify(Array.from(newDismissed)));
                              } catch (e) {
                                console.error('Failed to save dismissed announcements:', e);
                              }
                            }}
                            aria-label="Dismiss announcement"
                            title="Dismiss"
                          >
                            ×
                          </button>
                        </div>
                        <p>{announcement.content}</p>
                        <div className="announcement-meta">
                          <span className="announcement-date">{announcement.time_ago}</span>
                          {announcement.priority === 'urgent' && (
                            <span className="announcement-priority urgent">⚠️ Urgent</span>
                          )}
                          {announcement.priority === 'high' && (
                            <span className="announcement-priority high">🔴 High Priority</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null;
          })()}

          {/* Upcoming Sessions Section - Hide if no children */}
          {children.length > 0 && (
          <div className="upcoming-classes-section">
            <div className="section-header">
              <h3>📅 Upcoming Sessions</h3>
              <p>View your children's upcoming class sessions and manage schedules</p>
            </div>
            
            {sessionsLoading ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Loading upcoming sessions...</p>
              </div>
            ) : upcomingSessions.length > 0 ? (
              <>
                {/* Desktop Table View */}
                <div className="sessions-table-container desktop-only">
                  <table className="sessions-table">
                    <thead>
                      <tr>
                        <th>Program/Course</th>
                        <th>Student</th>
                        <th>Date</th>
                        <th>Start Time</th>
                        <th>Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {upcomingSessions.map((session) => (
                        <tr key={session.id}>
                          <td>
                            <div className="session-name">
                              <strong>{session.program_name || session.course_name}</strong>
                              <span className={`session-type ${session.session_type}`}>
                                {session.session_type}
                              </span>
                            </div>
                          </td>
                          <td>
                            <span className="student-name">{session.student_name}</span>
                          </td>
                          <td>
                            {(() => {
                              const dateStr = session.date.split('T')[0];
                              const [year, month, day] = dateStr.split('-');
                              const localDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                              return localDate.toLocaleDateString();
                            })()}
                          </td>
                          <td>
                            {new Date(`2000-01-01T${session.start_time}`).toLocaleTimeString('en-US', {
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true
                            })}
                          </td>
                          <td>
                            {Math.floor(session.duration_minutes / 60)}h {session.duration_minutes % 60}m
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Mobile Card View - Grouped by Child */}
                <div className="sessions-cards-container mobile-only">
                  {(() => {
                    // Group sessions by student name
                    const sessionsByChild = upcomingSessions.reduce((acc, session) => {
                      const studentName = session.student_name;
                      if (!acc[studentName]) {
                        acc[studentName] = [];
                      }
                      acc[studentName].push(session);
                      return acc;
                    }, {} as Record<string, any[]>);

                    // Sort sessions within each child by date
                    Object.keys(sessionsByChild).forEach(studentName => {
                      sessionsByChild[studentName].sort((a: any, b: any) => {
                        const dateA = new Date(a.date);
                        const dateB = new Date(b.date);
                        return dateA.getTime() - dateB.getTime();
                      });
                    });

                    return Object.keys(sessionsByChild).map((studentName) => {
                      const childSessions = sessionsByChild[studentName];
                      const isExpanded = expandedChildSessions.has(studentName);
                      const sessionsToShow = isExpanded ? childSessions.slice(0, 10) : childSessions.slice(0, 1);
                      const hasMoreSessions = childSessions.length > 1;
                      const remainingCount = childSessions.length - 1;

                      return (
                        <div key={studentName} className="child-sessions-group">
                          <div className="child-sessions-header">
                            <h4 className="child-name-header">{studentName}</h4>
                            {hasMoreSessions && (
                              <span className="sessions-count-badge">
                                {childSessions.length} session{childSessions.length !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                          
                          {sessionsToShow.map((session: any) => {
                            const dateStr = session.date.split('T')[0];
                            const [year, month, day] = dateStr.split('-');
                            const localDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                            const formattedDate = localDate.toLocaleDateString();
                            const formattedTime = new Date(`2000-01-01T${session.start_time}`).toLocaleTimeString('en-US', {
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true
                            });
                            const duration = `${Math.floor(session.duration_minutes / 60)}h ${session.duration_minutes % 60}m`;
                            
                            return (
                              <div key={session.id} className="session-card">
                                <div className="session-card-header">
                                  <h4>{session.program_name || session.course_name}</h4>
                                  <span className={`session-type ${session.session_type}`}>
                                    {session.session_type}
                                  </span>
                                </div>
                                <div className="session-card-body">
                                  <div className="session-card-row">
                                    <span className="session-card-label">Date:</span>
                                    <span className="session-card-value">{formattedDate}</span>
                                  </div>
                                  <div className="session-card-row">
                                    <span className="session-card-label">Time:</span>
                                    <span className="session-card-value">{formattedTime}</span>
                                  </div>
                                  <div className="session-card-row">
                                    <span className="session-card-label">Duration:</span>
                                    <span className="session-card-value">{duration}</span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}

                          {hasMoreSessions && (
                            <button
                              className="show-more-sessions-btn"
                              onClick={() => {
                                const newExpanded = new Set(expandedChildSessions);
                                if (isExpanded) {
                                  newExpanded.delete(studentName);
                                } else {
                                  newExpanded.add(studentName);
                                }
                                setExpandedChildSessions(newExpanded);
                              }}
                            >
                              {isExpanded ? (
                                <>▲ Show Less</>
                              ) : (
                                <>▼ Show {remainingCount} More Session{remainingCount !== 1 ? 's' : ''}</>
                              )}
                            </button>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              </>
            ) : (
              <div className="no-upcoming-classes">
                {children.length === 0 ? (
                  <>
                    <div className="enrollment-prompt">
                      <h4>👶 Enroll Your Child to Get Started</h4>
                      <p>You haven't enrolled any children yet. Enroll your child to start their STEM journey with OARA!</p>
                      <div className="enrollment-actions">
                        <button 
                          className="btn btn-primary"
                          onClick={() => setActiveTab('children')}
                        >
                          👶 Enroll Your Child
                        </button>
                        <button 
                          className="btn btn-secondary"
                          onClick={() => setActiveTab('programs')}
                        >
                          📚 Browse Programs
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <p>No upcoming sessions scheduled</p>
                    <button 
                      className="btn btn-primary"
                      onClick={() => setActiveTab('programs')}
                    >
                      Browse Programs
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
          )}

        </div>
      )}

      {/* Children Tab */}
      {activeTab === 'children' && (
        <div className="dashboard-content">
          <div className="children-header">
            <h3>My Children</h3>
            <button className="btn btn-primary desktop-only" onClick={handleAddNewChild}>➕ Add New Child</button>
          </div>
          
          {/* Desktop Grid View */}
          <div className="children-grid desktop-only">
            {children.map((child) => (
              <div key={child.id} className="child-card">
                <div className="child-header">
                  <div className="child-info">
                    <h4>{child.firstName} {child.lastName}</h4>
                    <div className="child-tier">
                      {getTierEmoji(child.tier)} {getTierName(child.tier)}
                    </div>
                  </div>
                  <span className={`status-badge ${child.status}`}>{child.status}</span>
                </div>
                <div className="child-details">
                  <p><strong>Age:</strong> {child.age} years old</p>
                  <p><strong>Grade:</strong> {child.gradeLevel}</p>
                  <p><strong>Enrolled:</strong> {child.enrollmentDate}</p>
                  {child.slackUsername && <p><strong>Slack:</strong> @{child.slackUsername}</p>}
                  
                  {/* Enrollment & Payment Information */}
                  <div className="session-stats">
                    <h5>📊 Enrollment & Payment Information</h5>
                    
                    {/* Session Packages (if any) */}
                    {(
                      (child.totalSessionsPurchasedPaid || child.totalSessionsPurchased || 0) > 0 ||
                      (child.totalSessionsPurchasedPending || 0) > 0 ||
                      (child.totalSessionsUsed || 0) > 0 ||
                      (child.remainingSessionsNet || child.remainingSessions || 0) !== 0
                    ) && (
                      <div style={{ marginBottom: '15px', paddingBottom: '15px', borderBottom: '1px solid #e9ecef' }}>
                        <h6 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Session Packages</h6>
                        <p><strong>Paid Sessions Purchased:</strong> {child.totalSessionsPurchasedPaid || child.totalSessionsPurchased || 0}</p>
                        <p><strong>Pending Sessions (payment not completed):</strong> {child.totalSessionsPurchasedPending || 0}</p>
                        <p><strong>Sessions Attended:</strong> {child.totalSessionsUsed || 0}</p>
                        <p>
                          <strong>Sessions Remaining (Net):</strong>{' '}
                          <span
                            className="remaining-sessions"
                            style={{
                              color: (child.remainingSessionsNet || child.remainingSessions || 0) < 0 ? '#dc3545' : undefined,
                              fontWeight: 700
                            }}
                          >
                            {child.remainingSessionsNet ?? child.remainingSessions ?? 0}
                          </span>
                        </p>
                        {(child.remainingSessionsNet || child.remainingSessions || 0) < 0 && (
                          <p style={{ marginTop: '4px', color: '#dc3545', fontSize: '12px' }}>
                            Overused by {Math.abs(child.remainingSessionsNet || child.remainingSessions || 0)} sessions.
                          </p>
                        )}
                        <p><strong>Active Packages:</strong> {child.activePackages || 0}</p>
                      </div>
                    )}
                    
                    {/* Payment Plans (if any) */}
                    {child.paymentPlans && child.paymentPlans.length > 0 && (
                      <div style={{ marginBottom: '15px', paddingBottom: '15px', borderBottom: '1px solid #e9ecef' }}>
                        <h6 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Payment Plans</h6>
                        {child.paymentPlans.map((plan: any) => (
                          <div key={plan.id} style={{ padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '6px', marginBottom: '8px' }}>
                            <p><strong>Plan Type:</strong> {plan.planType === 'club' ? 'Club/Group Arrangement' : plan.planType === 'monthly' ? 'Monthly Subscription' : plan.planType === 'custom' ? 'Custom Payment Plan' : plan.planType}</p>
                            <p><strong>Amount:</strong> ${plan.amount.toFixed(2)} / {plan.frequency}</p>
                            <p><strong>Status:</strong> <span className={`status-badge status-${plan.status.toLowerCase()}`}>{plan.status}</span></p>
                            {plan.nextBillingDate && (
                              <p><strong>Next Billing:</strong> {new Date(plan.nextBillingDate).toLocaleDateString()}</p>
                            )}
                            {plan.notes && (
                              <p style={{ fontSize: '13px', color: '#666', marginTop: '5px' }}><em>{plan.notes}</em></p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Program Enrollments */}
                    {child.programEnrollments && child.programEnrollments.length > 0 && (
                      <div style={{ marginBottom: '15px', paddingBottom: '15px', borderBottom: '1px solid #e9ecef' }}>
                        <h6 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Active Programs</h6>
                        {child.programEnrollments.map((enrollment: any) => (
                          <div key={enrollment.enrollmentId} style={{ padding: '10px', backgroundColor: '#e8f5e9', borderRadius: '6px', marginBottom: '8px' }}>
                            <p><strong>{enrollment.programName}</strong></p>
                            <p style={{ fontSize: '13px' }}>Type: {enrollment.programType.replace('_', ' ')} • Tier: {enrollment.programTier}</p>
                            <p style={{ fontSize: '13px' }}>Enrolled: {new Date(enrollment.enrollmentDate).toLocaleDateString()}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Camp Enrollments */}
                    {child.campEnrollments && child.campEnrollments.length > 0 && (
                      <div>
                        <h6 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>⛺ Camp Enrollments</h6>
                        {child.campEnrollments.map((enrollment: any) => (
                          <div key={enrollment.enrollmentId} style={{ padding: '10px', backgroundColor: '#fff3cd', borderRadius: '6px', marginBottom: '8px', border: '1px solid #ffc107' }}>
                            <p><strong>{enrollment.campName}</strong></p>
                            <p style={{ fontSize: '13px' }}>
                              {enrollment.enrollmentType === 'weekly' && `Week${enrollment.selectedWeeks?.length > 1 ? 's' : ''} ${enrollment.selectedWeeks?.join(', ')}`}
                              {enrollment.enrollmentType === 'full_camp' && 'Full Camp'}
                              {enrollment.enrollmentType === 'drop_in' && `${enrollment.daysAttending?.length || 0} day(s)`}
                            </p>
                            <p style={{ fontSize: '13px' }}>
                              {enrollment.startDate && enrollment.endDate && 
                                `${new Date(enrollment.startDate).toLocaleDateString()} - ${new Date(enrollment.endDate).toLocaleDateString()}`
                              }
                            </p>
                            <p style={{ fontSize: '13px' }}>
                              <strong>Payment:</strong> <span className={`status-badge status-${enrollment.paymentStatus.toLowerCase()}`}>{enrollment.paymentStatus}</span>
                              {' • '}
                              <strong>Total:</strong> ${enrollment.finalPrice.toFixed(2)}
                            </p>
                            {(enrollment.needsEarlyDropoff || enrollment.needsLatePickup) && (
                              <p style={{ fontSize: '12px', color: '#856404', marginTop: '5px' }}>
                                {enrollment.needsEarlyDropoff && '🌅 Early Drop-off'}
                                {enrollment.needsEarlyDropoff && enrollment.needsLatePickup && ' • '}
                                {enrollment.needsLatePickup && '🌙 Late Pickup'}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* No enrollments message */}
                    {(!(child.activePackages) || (child.activePackages || 0) === 0) && 
                     (!child.paymentPlans || child.paymentPlans.length === 0) && 
                     (!child.programEnrollments || child.programEnrollments.length === 0) &&
                     (!child.campEnrollments || child.campEnrollments.length === 0) && (
                      <p style={{ color: '#999', fontStyle: 'italic' }}>No active enrollments or payment plans</p>
                    )}
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="child-actions" style={{ 
                    marginTop: '15px', 
                    paddingTop: '15px', 
                    borderTop: '1px solid #e9ecef',
                    display: 'flex',
                    gap: '10px'
                  }}>
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        setSelectedChildForSchedule(child);
                        setShowChildScheduleView(true);
                      }}
                      style={{ flex: 1 }}
                    >
                      👁️ View Details
                    </button>
                    {isChildEligibleForAddSession(child) && (
                      <button
                        className="btn btn-secondary"
                        onClick={() => {
                          setSelectedChildForAddSession(child);
                          setShowAddSessionModal(true);
                        }}
                        style={{ flex: 1 }}
                      >
                        ➕ Add Session
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile Carousel View */}
          <div className="children-carousel-container mobile-only">
            <div 
              className="children-carousel"
              ref={childrenCarouselRef}
              onScroll={handleChildrenCarouselScroll}
            >
              {children.map((child) => (
                <div key={child.id} className="child-card-carousel">
                  <div className="child-card-header-mobile">
                    <div className="child-info-mobile">
                      <h4>{child.firstName} {child.lastName}</h4>
                      <div className="child-bio-compact">
                        {child.age} Yrs • Grade {child.gradeLevel} • Joined {formatEnrollmentDateCompact(child.enrollmentDate)}
                      </div>
                    </div>
                    <button
                      className="child-view-details-btn"
                      onClick={() => {
                        setSelectedChildForSchedule(child);
                        setShowChildScheduleView(true);
                      }}
                      title="View Details"
                    >
                      →
                    </button>
                  </div>
                  
                  <div className="child-tier-mobile">
                    {getTierEmoji(child.tier)} {getTierName(child.tier)}
                    <span className={`status-badge ${child.status}`}>{child.status}</span>
                  </div>

                  {isChildEligibleForAddSession(child) && (
                    <div style={{ marginTop: '10px' }}>
                      <button
                        className="btn btn-secondary"
                        onClick={() => {
                          setSelectedChildForAddSession(child);
                          setShowAddSessionModal(true);
                        }}
                        style={{ width: '100%' }}
                      >
                        ➕ Add Session
                      </button>
                    </div>
                  )}

                  {/* Program Enrollments - Direct Cards (No Container) */}
                  {child.programEnrollments && child.programEnrollments.length > 0 && (
                    <div className="child-programs-list">
                      {child.programEnrollments.map((enrollment: any) => (
                        <div key={enrollment.enrollmentId} className="child-program-card">
                          <strong>{enrollment.programName}</strong>
                          <span className="program-meta">{enrollment.programType.replace('_', ' ')} • {enrollment.programTier}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Camp Enrollments - Direct Cards */}
                  {child.campEnrollments && child.campEnrollments.length > 0 && (
                    <div className="child-programs-list">
                      {child.campEnrollments.map((enrollment: any) => (
                        <div key={enrollment.enrollmentId} className="child-camp-card">
                          <strong>⛺ {enrollment.campName}</strong>
                          <span className="camp-meta">
                            {enrollment.enrollmentType === 'weekly' && `Week${enrollment.selectedWeeks?.length > 1 ? 's' : ''} ${enrollment.selectedWeeks?.join(', ')}`}
                            {enrollment.enrollmentType === 'full_camp' && 'Full Camp'}
                            {enrollment.enrollmentType === 'drop_in' && `${enrollment.daysAttending?.length || 0} day(s)`}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Session Packages Info */}
                  {(
                    (child.totalSessionsPurchasedPaid || child.totalSessionsPurchased || 0) > 0 ||
                    (child.totalSessionsPurchasedPending || 0) > 0 ||
                    (child.totalSessionsUsed || 0) > 0 ||
                    (child.remainingSessionsNet || child.remainingSessions || 0) !== 0
                  ) && (
                    <div className="child-sessions-info">
                      <span className="sessions-badge">📦 {child.activePackages} Package{child.activePackages !== 1 ? 's' : ''}</span>
                      <span className="sessions-badge">Paid: {child.totalSessionsPurchasedPaid || child.totalSessionsPurchased || 0}</span>
                      <span className="sessions-badge">Pending: {child.totalSessionsPurchasedPending || 0}</span>
                      <span
                        className="sessions-badge"
                        style={{
                          backgroundColor: (child.remainingSessionsNet || child.remainingSessions || 0) < 0 ? '#fdeaea' : undefined,
                          color: (child.remainingSessionsNet || child.remainingSessions || 0) < 0 ? '#b42318' : undefined,
                        }}
                      >
                        Remaining: {child.remainingSessionsNet ?? child.remainingSessions ?? 0}
                      </span>
                    </div>
                  )}

                  {/* No enrollments message */}
                  {(!(child.activePackages) || (child.activePackages || 0) === 0) && 
                   (!child.paymentPlans || child.paymentPlans.length === 0) && 
                   (!child.programEnrollments || child.programEnrollments.length === 0) &&
                   (!child.campEnrollments || child.campEnrollments.length === 0) && (
                    <p className="no-enrollments-message">No active enrollments</p>
                  )}
                </div>
              ))}
              
              {/* Add New Child Card */}
              <div className="child-card-carousel add-child-card">
                <button 
                  className="add-child-button"
                  onClick={handleAddNewChild}
                >
                  <div className="add-child-icon">➕</div>
                  <div className="add-child-text">Add New Child</div>
                </button>
              </div>
            </div>
            
            {/* Pagination Dots */}
            <div className="children-carousel-pagination">
              {[...Array(children.length + 1)].map((_, index) => (
                <span
                  key={index}
                  className={`carousel-dot ${index === childrenCarouselIndex ? 'active' : ''}`}
                  onClick={() => {
                    if (childrenCarouselRef.current) {
                      const containerWidth = childrenCarouselRef.current.offsetWidth;
                      const cardWidth = containerWidth * 0.9;
                      const gap = 16;
                      const padding = containerWidth * 0.05;
                      const scrollPosition = index * (cardWidth + gap) - padding;
                      childrenCarouselRef.current.scrollTo({
                        left: Math.max(0, scrollPosition),
                        behavior: 'smooth'
                      });
                    }
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Child Schedule View Modal */}
      {showChildScheduleView && selectedChildForSchedule && (
        <ChildScheduleView
          child={selectedChildForSchedule}
          onClose={() => {
            setShowChildScheduleView(false);
            setSelectedChildForSchedule(null);
          }}
          onSessionScheduled={() => {
            // Refresh upcoming sessions when a session is scheduled
            fetchUpcomingSessions();
          }}
        />
      )}

      {showAddSessionModal && selectedChildForAddSession && (
        <AddSessionModal
          studentId={selectedChildForAddSession.id}
          studentName={`${selectedChildForAddSession.firstName} ${selectedChildForAddSession.lastName}`}
          onClose={() => {
            setShowAddSessionModal(false);
            setSelectedChildForAddSession(null);
          }}
          onPurchased={() => {
            toast.success('Session purchase recorded.');
            fetchDashboardData();
          }}
        />
      )}

      {/* Program Details Tab */}
      {activeTab === 'program-details' && selectedChild && (
        <div className="dashboard-content">
          <div className="program-details-header">
            <button 
              className="btn btn-secondary"
              onClick={() => setActiveTab('children')}
            >
              ← Back to Children
            </button>
            <h3>Program Details - {selectedChild.firstName} {selectedChild.lastName}</h3>
          </div>

          {loading ? (
            <div className="loading-message">
              <p>Loading program details...</p>
            </div>
          ) : error ? (
            <div className="error-message">
              <p>Error: {error}</p>
            </div>
          ) : programDetails && programDetails.programs && programDetails.programs.length > 0 ? (
            <div className="program-details-content">
              {programDetails.programs.map((program: any, index: number) => (
                <div key={program.program_id || index}>
                  <div className="program-overview">
                    <div className="program-card-large">
                      <div className="program-header">
                        <h4>{program.program_name}</h4>
                        <span className="program-tier-badge">{program.program_tier}</span>
                      </div>
                      <div className="program-info-grid">
                        <div className="info-item">
                          <strong>Duration:</strong> {program.duration_minutes} minutes per session
                        </div>
                        <div className="info-item">
                          <strong>Sessions Required:</strong> {program.sessions_required} sessions
                        </div>
                        <div className="info-item">
                          <strong>Program Type:</strong> {program.program_type?.replace('_', ' ')}
                        </div>
                        <div className="info-item">
                          <strong>Status:</strong> <span className="status-active">{program.enrollment_status}</span>
                        </div>
                        <div className="info-item">
                          <strong>Enrollment Date:</strong> {new Date(program.enrollment_date).toLocaleDateString()}
                        </div>
                        <div className="info-item">
                          <strong>Student Tier:</strong> {programDetails.student.tier}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Session Package Information */}
                  {programDetails.session_package && (
                    <div className="session-package-section">
                      <h4>📦 Session Package Information</h4>
                      <div className="session-package-card">
                        <div className="package-stats">
                          <div className="stat-item">
                            <span className="stat-label">Total Sessions Purchased:</span>
                            <span className="stat-value">{programDetails.session_package.total_sessions_purchased}</span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-label">Sessions Used:</span>
                            <span className="stat-value used">{programDetails.session_package.sessions_used}</span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-label">Remaining Sessions:</span>
                            <span className="stat-value remaining">{programDetails.session_package.sessions_remaining}</span>
                          </div>
                        </div>
                        <div className="package-dates">
                          <p><strong>Purchase Date:</strong> {new Date(programDetails.session_package.purchase_date).toLocaleDateString()}</p>
                          <p><strong>Expiry Date:</strong> {new Date(programDetails.session_package.expiry_date).toLocaleDateString()}</p>
                          <p><strong>Status:</strong> <span className="status-active">{programDetails.session_package.status}</span></p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="sessions-section">
                    <h4>📅 Registered Sessions</h4>
                    {program.sessions && program.sessions.length > 0 ? (
                      <div className="sessions-list">
                        {program.sessions.map((session: any, sessionIndex: number) => (
                          <div key={session.id || sessionIndex} className="session-item">
                            <div className="session-date">
                              <strong>{(() => {
                                // Parse date as local date to avoid timezone offset issues
                                const dateStr = session.date.split('T')[0];
                                const [year, month, day] = dateStr.split('-');
                                const localDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                                return localDate.toLocaleDateString('en-US', {
                                  weekday: 'long',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                });
                              })()}</strong>
                            </div>
                            <div className="session-time">
                              {new Date(`2000-01-01T${session.start_time}`).toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                              })} - {new Date(`2000-01-01T${session.end_time}`).toLocaleTimeString('en-US', {
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                              })}
                            </div>
                            <div className="session-status">
                              <span className="status-scheduled">{session.status}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="no-sessions-message">
                        <p>No registered sessions found for this program.</p>
                      </div>
                    )}
                  </div>

                  <div className="additional-sessions-section">
                    <h4>➕ Register for Additional Sessions</h4>
                    <p>Would you like to register for more sessions in this program?</p>
                    <button 
                      className="btn btn-primary"
                      onClick={() => handleOpenCourseRegistration(selectedChild)}
                    >
                      📚 Register for More Sessions
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-programs-message">
              <p>No active program enrollments found for this student.</p>
            </div>
          )}
        </div>
      )}

      {/* Session Packages Tab */}
      {activeTab === 'packages' && (
        <div className="dashboard-content">
          <div className="packages-header">
            <h3>Session Pack Purchase System</h3>
            <p>Pack Validity: Sep 2, 2025 → Jun 25, 2026</p>
          </div>

          <div className="family-plan-info">
            <h4>👨‍👩‍👧 Family Plan Active</h4>
            <p>You have {familyPlan?.totalChildren} children enrolled. Family discounts are automatically applied:</p>
            <ul>
              <li>1st child: Full price</li>
              <li>2nd child: -25% off</li>
              <li>3rd+ child: -50% off</li>
            </ul>
      <div className="family-policy">
        <h5>📦 Session Pack Policy</h5>
        <p><strong>Individual Packages:</strong> Each child gets their own session pack. Family discounts are automatically applied at purchase time to make multi-child families affordable.</p>
        <p><strong>School Days Only:</strong> All session packs are valid only during regular school days (Monday-Friday, excluding holidays and summer break). This ensures sessions align with the academic calendar.</p>
        <p><strong>Camp Discounts:</strong> Camp discounts are based on your session pack size: {campDiscountRules.length > 0 ? campDiscountRules.map(rule => `${rule.session_pack_size}-session packs get ${rule.discount_percentage}% off camps`).join(', ') : 'Loading discount information...'}</p>
        <p><em>This keeps the system simple while ensuring fair pricing for all families and proper academic alignment.</em></p>
      </div>
          </div>

          {/* Drop-in Sessions Section */}
          <div className="drop-in-section">
            <h3>🎯 Drop-in Sessions</h3>
            <p>Need a single session? Book a drop-in session for your child.</p>
            <div className="drop-in-pricing">
              <div className="pricing-card">
                <h4>🌱 Foundations & 🚀 Innovators</h4>
                <div className="price">$25 per session</div>
                <p>Perfect for trying out OARA or occasional sessions</p>
              </div>
              <div className="pricing-card">
                <h4>🌟 Pioneers</h4>
                <div className="price">$50 per session</div>
                <p>Advanced sessions for experienced students</p>
              </div>
            </div>
            <div className="drop-in-features">
              <p>✅ Valid only during regular school days</p>
              <p>✅ Book up to 24 hours in advance</p>
              <p>✅ E-transfer payment required</p>
              <p>✅ Subject to availability</p>
            </div>
            <button className="btn btn-secondary">Book Drop-in Session</button>
          </div>

          {children.map((child) => (
            <div key={child.id} className="package-section">
              <h4>{getTierEmoji(child.tier)} {child.firstName} {child.lastName} - {getTierName(child.tier)}</h4>
              <div className="package-grid">
                {getPackagePricing(child.tier).map((pkg, index) => (
                  <div key={index} className="package-card">
                    <h5>{pkg.name}</h5>
                    <div className="package-price">
                      <span className="price">${pkg.price}</span>
                      <span className="per-session">${pkg.perSession}/session</span>
                    </div>
                    <p className="package-duration">{pkg.duration}</p>
                    <div className="package-features">
                      {pkg.features && pkg.features.length > 0 ? (
                        pkg.features.map((feature: string, featureIndex: number) => (
                          <p key={featureIndex}>✅ {feature}</p>
                        ))
                      ) : (
                        <>
                          <p>✅ {pkg.sessions} sessions included</p>
                          <p>✅ Valid until Jun 25, 2026</p>
                          <p>✅ Individual package for {child.firstName}</p>
                          <p>✅ Valid only during regular school days</p>
                          <p>✅ Camp discounts: {campDiscountRules.length > 0 ? campDiscountRules.map(rule => `${rule.session_pack_size}-session: ${rule.discount_percentage}% off`).join(', ') : 'Loading...'}</p>
                        </>
                      )}
                    </div>
                    <button 
                      className="btn btn-primary"
                      onClick={() => handlePackagePurchase(child, pkg)}
                    >
                      Purchase Pack
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="add-on-sessions">
            <h4>Add-On Single Sessions</h4>
            <p>After pack completion, additional sessions available:</p>
            <div className="add-on-grid">
              <div className="add-on-card">
                <h5>🌱 Foundations/🚀 Innovators</h5>
                <p className="price">$22/session</p>
              </div>
              <div className="add-on-card">
                <h5>🌟 Pioneers</h5>
                <p className="price">$45/session</p>
              </div>
            </div>
          </div>

          <div className="trial-session">
            <h4>🎉 First Session Special</h4>
            <p>Try OARA: $10 (one-time offer per family)</p>
            <button className="btn btn-secondary">Book Trial Session</button>
          </div>
        </div>
      )}

      {/* Programs Tab */}
      {activeTab === 'programs' && (
        <div className="dashboard-content">
          <div className="program-enrollment-form-container">
            <div className="form-header">
              <h3>Program Enrollment</h3>
              <p className="form-subtitle">Enroll your child in OARA Youth Innovation Lab programs</p>
            </div>

            <form className="program-enrollment-form" onSubmit={(e) => e.preventDefault()}>
              {/* Step 1: Select Child */}
              <div className="form-section">
                <label htmlFor="enrollment-child" className="form-label">
                  <span className="label-icon">👤</span>
                  Select Child <span className="required">*</span>
                </label>
                <select
                  id="enrollment-child"
                  className="form-select"
                  value={selectedChild?.id || ''}
                  onChange={(e) => {
                    const child = children.find(c => c.id === e.target.value);
                    setSelectedChild(child || null);
                    // Clear program selection and homeschool fields when child changes
                    setProgramDetails(null);
                    setSchedulePreference('');
                    setCustomScheduleMessage('');
                    setCallbackNumber('');
                    setCallbackTimePreference('');
                    setPreferredDays([]);
                  }}
                  required
                >
                  <option value="">Choose a child...</option>
                  {children.map((child) => (
                    <option key={child.id} value={child.id}>
                      {child.firstName} {child.lastName} {child.gradeLevel ? `(Grade ${child.gradeLevel})` : ''}
                    </option>
                  ))}
                </select>
                {children.length === 0 && (
                  <p className="form-help-text">No children registered. Please register a child first.</p>
                )}
              </div>

              {/* Step 2: Select Program */}
              {selectedChild && (
                <div className="form-section">
                  <label htmlFor="enrollment-program" className="form-label">
                    <span className="label-icon">🏫</span>
                    Select Program <span className="required">*</span>
                  </label>
                  <select
                    id="enrollment-program"
                    className="form-select"
                    value={programDetails?.id || ''}
                    onChange={async (e) => {
                      const program = programs.find(p => p.id === e.target.value);
                      if (program) {
                        setProgramDetails(program);
                        
                        // Check if this is a Private Group program
                        const isPrivateGroup = program.name && program.name.toLowerCase().includes('private group');
                        
                        if (isPrivateGroup) {
                          // Fetch available sessions for Private Group
                          console.log('🔍 Private Group selected, fetching sessions...');
                          try {
                            const response = await fetch(`${config.BACKEND_URL}/api/class-sessions/?program_id=${program.id}`);
                            if (response.ok) {
                              const sessions = await response.json();
                              // Filter to future sessions only
                              const now = new Date();
                              const futureSessions = sessions.filter((s: any) => {
                                const sessionDate = new Date(s.date_available || s.date);
                                return sessionDate >= now;
                              });
                              console.log('✅ Loaded sessions:', futureSessions.length);
                              setAvailablePrivateSessions(futureSessions);
                            }
                          } catch (error) {
                            console.error('Error loading sessions:', error);
                            setAvailablePrivateSessions([]);
                          }
                        } else {
                          setAvailablePrivateSessions([]);
                          setSelectedPrivateSessions([]);
                        }
                        
                        // Reset homeschool fields when program changes
                        if (program.programType !== 'homeschool') {
                          setSchedulePreference('');
                          setCustomScheduleMessage('');
                          setCallbackNumber('');
                          setCallbackTimePreference('');
                          setPreferredDays([]);
                        }
                      } else {
                        setProgramDetails(null);
                        setAvailablePrivateSessions([]);
                        setSelectedPrivateSessions([]);
                        setSchedulePreference('');
                        setCustomScheduleMessage('');
                        setCallbackNumber('');
                        setCallbackTimePreference('');
                        setPreferredDays([]);
                      }
                    }}
                    required
                  >
                    <option value="">Choose a program...</option>
                    {programs
                      .filter(program => {
                        // Log every program for debugging
                        console.log(`Checking program: ${program.name}, tier: ${program.tier}, type: ${program.programType}`);
                        
                        // Exclude vacation camps
                        if (program.programType === 'vacation_camp') {
                          console.log(`  ❌ Excluding - vacation camp`);
                          return false;
                        }
                        
                        // Show programs with tier="all" (like Private Group, Chess Club) or matching student tier
                        const programTier = (program.tier || '').toLowerCase();
                        const childTier = (selectedChild.tier || '').toLowerCase();
                        const matches = programTier === 'all' || programTier === childTier;
                        console.log(`  Program tier: "${programTier}", Child tier: "${childTier}", Match: ${matches ? '✅' : '❌'}`);
                        return matches;
                      })
                      .map((program) => {
                        console.log(`✅ Including in dropdown: ${program.name}`);
                        return (
                          <option key={program.id} value={program.id}>
                            {program.name} {program.tier && program.tier.toLowerCase() !== 'all' ? `- ${program.tier.charAt(0).toUpperCase() + program.tier.slice(1)}` : ''}
                          </option>
                        );
                      })}
                  </select>
                  {programs.filter(p => {
                    if (p.programType === 'vacation_camp') return false;
                    const programTier = (p.tier || '').toLowerCase();
                    const childTier = (selectedChild.tier || '').toLowerCase();
                    return programTier === 'all' || programTier === childTier;
                  }).length === 0 && (
                    <div className="form-help-text">
                      No programs available for {selectedChild.tier.charAt(0).toUpperCase() + selectedChild.tier.slice(1)} tier at this time.
                    </div>
                  )}
                  {programs.filter(p => p.programType === 'vacation_camp').length > 0 && (
                    <div className="camp-redirect-notice">
                      <p>💡 Looking for vacation camps? <button type="button" className="link-button" onClick={() => setActiveTab('camps')}>View Camps</button></p>
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Private Group Session Selection */}
              {programDetails && selectedChild && (
                <>
                  {/* Private Group Session Selection */}
                  {programDetails.name && programDetails.name.toLowerCase().includes('private group') && (
                    <div className="form-section private-session-selection">
                      <h4 className="preview-title">Select Sessions</h4>
                      <p className="form-help-text" style={{ marginBottom: '15px' }}>
                        Choose the session(s) you want to register for. You can select multiple sessions.
                      </p>
                      
                      {availablePrivateSessions.length === 0 ? (
                        <div style={{ padding: '20px', textAlign: 'center', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                          <p>No sessions available at this time. Please check back later or contact us.</p>
                        </div>
                      ) : (
                        <div className="sessions-list" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {availablePrivateSessions.map((session: any) => {
                            const sessionDate = session.date_available || session.date;
                            const startTime = session.start_time || session.startTime;
                            const endTime = session.end_time || session.endTime;
                            const isSelected = selectedPrivateSessions.includes(session.id);
                            
                            return (
                              <label 
                                key={session.id} 
                                className="session-checkbox-option"
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  padding: '15px',
                                  border: `2px solid ${isSelected ? '#3498db' : '#e9ecef'}`,
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  backgroundColor: isSelected ? '#f0f8ff' : 'white',
                                  transition: 'all 0.2s'
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedPrivateSessions([...selectedPrivateSessions, session.id]);
                                    } else {
                                      setSelectedPrivateSessions(selectedPrivateSessions.filter(id => id !== session.id));
                                    }
                                  }}
                                  style={{ marginRight: '15px', width: '20px', height: '20px' }}
                                />
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: 600, marginBottom: '5px' }}>
                                    {sessionDate ? (() => {
                                      // Parse date as local date to avoid timezone offset issues
                                      const [year, month, day] = sessionDate.split('T')[0].split('-');
                                      const localDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                                      return localDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
                                    })() : 'Date TBD'}
                                  </div>
                                  <div style={{ color: '#666', fontSize: '14px' }}>
                                    {startTime && endTime ? (() => {
                                      // Convert 24-hour time to 12-hour format
                                      const formatTime = (time: string) => {
                                        const [hours, minutes] = time.split(':');
                                        const hour = parseInt(hours);
                                        const ampm = hour >= 12 ? 'PM' : 'AM';
                                        const hour12 = hour % 12 || 12;
                                        return `${hour12}:${minutes} ${ampm}`;
                                      };
                                      return `${formatTime(startTime)} - ${formatTime(endTime)}`;
                                    })() : 'Time TBD'}
                                    {session.location && ` • ${session.location}`}
                                  </div>
                                  {session.description && (
                                    <div style={{ color: '#999', fontSize: '13px', marginTop: '5px' }}>
                                      {session.description}
                                    </div>
                                  )}
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      )}
                      
                      {selectedPrivateSessions.length > 0 && (
                        <div style={{ marginTop: '15px', padding: '12px', backgroundColor: '#e8f5e9', borderRadius: '8px' }}>
                          <strong>Selected:</strong> {selectedPrivateSessions.length} session(s)
                        </div>
                      )}
                    </div>
                  )}

                  {/* Homeschool Program Specific Fields */}
                  {programDetails.programType === 'homeschool' && (
                    <div className="form-section homeschool-schedule-section">
                      <div className="homeschool-info-box">
                        <h4 className="info-title">📅 Homeschool Program Schedule</h4>
                        <p className="info-description">
                          The homeschool program is currently offered on school days from <strong>3:00 PM to 4:00 PM</strong> with an optional complimentary 30-minute stay back to interact with other groups.
                        </p>
                        <p className="info-note">
                          For schedules outside of this time, please reach out to{' '}
                          <a href="mailto:oarayouthinnovationlab@gmail.com">oarayouthinnovationlab@gmail.com</a> via email.
                        </p>
                      </div>

                      {/* Schedule Preference */}
                      <div className="form-section">
                        <label className="form-label">
                          <span className="label-icon">⏰</span>
                          Schedule Preference <span className="required">*</span>
                        </label>
                        <div className="radio-group">
                          <label className="radio-label">
                            <input
                              type="radio"
                              name="schedulePreference"
                              value="standard"
                              checked={schedulePreference === 'standard'}
                              onChange={(e) => {
                                setSchedulePreference('standard');
                                setCustomScheduleMessage('');
                                setCallbackNumber('');
                                setCallbackTimePreference('');
                              }}
                              required
                            />
                            <span className="radio-text">This schedule works for me</span>
                          </label>
                          <label className="radio-label">
                            <input
                              type="radio"
                              name="schedulePreference"
                              value="outside"
                              checked={schedulePreference === 'outside'}
                              onChange={(e) => setSchedulePreference('outside')}
                              required
                            />
                            <span className="radio-text">My preference is outside of this schedule</span>
                          </label>
                        </div>
                      </div>

                      {/* Custom Schedule Fields (shown when outside is selected) */}
                      {schedulePreference === 'outside' && (
                        <div className="form-section custom-schedule-fields">
                          <div className="form-group">
                            <label htmlFor="customScheduleMessage" className="form-label">
                              <span className="label-icon">📝</span>
                              Describe Your Preferred Schedule <span className="required">*</span>
                            </label>
                            <textarea
                              id="customScheduleMessage"
                              className="form-textarea"
                              value={customScheduleMessage}
                              onChange={(e) => setCustomScheduleMessage(e.target.value)}
                              placeholder="Please describe your preferred schedule, including preferred days and times..."
                              rows={4}
                              required
                            />
                          </div>

                          <div className="form-group">
                            <label htmlFor="callbackNumber" className="form-label">
                              <span className="label-icon">📞</span>
                              Best Callback Number <span className="required">*</span>
                            </label>
                            <input
                              type="tel"
                              id="callbackNumber"
                              className="form-input"
                              value={callbackNumber}
                              onChange={(e) => {
                                let value = e.target.value.replace(/\D/g, '');
                                if (value.length > 10) value = value.slice(0, 10);
                                setCallbackNumber(value);
                              }}
                              placeholder="2501234567"
                              maxLength={10}
                              required
                            />
                            <small className="form-help-text">10-digit Canadian phone number</small>
                          </div>

                          <div className="form-group">
                            <label htmlFor="callbackTimePreference" className="form-label">
                              <span className="label-icon">🕐</span>
                              Preferred Callback Time <span className="required">*</span>
                            </label>
                            <select
                              id="callbackTimePreference"
                              className="form-select"
                              value={callbackTimePreference}
                              onChange={(e) => setCallbackTimePreference(e.target.value)}
                              required
                            >
                              <option value="">Select preferred callback time...</option>
                              <option value="morning">Morning (9:00 AM - 12:00 PM)</option>
                              <option value="afternoon">Afternoon (12:00 PM - 5:00 PM)</option>
                              <option value="evening">Evening (5:00 PM - 8:00 PM)</option>
                            </select>
                          </div>
                        </div>
                      )}

                      {/* Preferred Days Selection (for standard schedule) */}
                      {schedulePreference === 'standard' && (
                        <div className="form-section">
                          <label className="form-label">
                            <span className="label-icon">📆</span>
                            Preferred Days (Maximum 2 days per week) <span className="required">*</span>
                          </label>
                          <div className="checkbox-group-days">
                            {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map((day) => (
                              <label key={day} className="checkbox-label-day">
                                <input
                                  type="checkbox"
                                  value={day}
                                  checked={preferredDays.includes(day)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      if (preferredDays.length < 2) {
                                        setPreferredDays([...preferredDays, day]);
                                      } else {
                                        alert('Maximum 2 days per week allowed');
                                      }
                                    } else {
                                      setPreferredDays(preferredDays.filter(d => d !== day));
                                    }
                                  }}
                                />
                                <span className="checkbox-text">{day}</span>
                              </label>
                            ))}
                          </div>
                          {preferredDays.length > 0 && (
                            <p className="form-help-text">
                              Selected: {preferredDays.join(', ')} ({preferredDays.length}/2)
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Step 4: Enrollment Button */}
              {selectedChild && programDetails && (
                <div className="form-section form-actions">
                  {programDetails.programType === 'homeschool' && (
                    <>
                      {schedulePreference === '' && (
                        <p className="form-validation-message">Please select a schedule preference to continue.</p>
                      )}
                      {schedulePreference === 'standard' && preferredDays.length === 0 && (
                        <p className="form-validation-message">Please select at least one preferred day.</p>
                      )}
                      {schedulePreference === 'outside' && (!customScheduleMessage || !callbackNumber || !callbackTimePreference) && (
                        <p className="form-validation-message">Please complete all required fields for custom schedule.</p>
                      )}
                    </>
                  )}
                  <button
                    type="button"
                    className="btn btn-primary btn-large"
                    onClick={async () => {
                      if (!selectedChild || !programDetails) return;
                      
                      // Validate homeschool-specific fields
                      if (programDetails.programType === 'homeschool') {
                        if (!schedulePreference) {
                          alert('Please select a schedule preference.');
                          return;
                        }
                        if (schedulePreference === 'standard' && preferredDays.length === 0) {
                          alert('Please select at least one preferred day.');
                          return;
                        }
                        if (schedulePreference === 'outside' && (!customScheduleMessage || !callbackNumber || !callbackTimePreference)) {
                          alert('Please complete all required fields for custom schedule.');
                          return;
                        }
                      }
                      
                      try {
                        // Check if student is already enrolled in this program
                        const childProgramEnrollments = selectedChild.programEnrollments || [];
                        const isAlreadyEnrolled = childProgramEnrollments.some(
                          (enrollment: ProgramEnrollment) => enrollment.programId === programDetails.id
                        );
                        
                        if (isAlreadyEnrolled) {
                          toast.info(`${selectedChild.firstName} ${selectedChild.lastName} is already enrolled in ${programDetails.name}.`);
                          return;
                        }
                        
                        const isPrivateGroup = programDetails.name && programDetails.name.toLowerCase().includes('private group');
                        
                        // Prepare enrollment data
                        const enrollmentData = {
                          studentId: selectedChild.id,
                          programId: programDetails.id,
                          selectedSessions: isPrivateGroup ? selectedPrivateSessions : [],
                          ...(programDetails.programType === 'homeschool' && {
                            schedulePreference,
                            preferredDays: schedulePreference === 'standard' ? preferredDays : [],
                            customScheduleMessage: schedulePreference === 'outside' ? customScheduleMessage : '',
                            callbackNumber: schedulePreference === 'outside' ? callbackNumber : '',
                            callbackTimePreference: schedulePreference === 'outside' ? callbackTimePreference : ''
                          })
                        };
                        
                        console.log('📤 Submitting enrollment:', enrollmentData);
                        
                        // Call the enrollment API
                        const response = await fetch(`${config.BACKEND_URL}/api/course-registrations`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                          },
                          body: JSON.stringify(enrollmentData)
                        });
                        
                        if (!response.ok) {
                          const errorData = await response.json();
                          throw new Error(errorData.detail || 'Failed to process enrollment');
                        }
                        
                        const result = await response.json();
                        console.log('✅ Enrollment successful:', result);
                        
                        // Show appropriate success message - direct enrollment confirmation
                        if (isPrivateGroup && selectedPrivateSessions.length > 0) {
                          const sessionText = selectedPrivateSessions.length === 1 ? 'session' : 'sessions';
                          toast.success(`${selectedChild.firstName} ${selectedChild.lastName} has been successfully enrolled in the Private Group Program for ${selectedPrivateSessions.length} ${sessionText}.`);
                        } else {
                          toast.success(`${selectedChild.firstName} ${selectedChild.lastName} has been successfully enrolled in ${programDetails.name}.`);
                        }
                        
                        // Refresh dashboard data to show new enrollment
                        await fetchDashboardData();
                        
                        // Reset form
                        setSelectedChild(null);
                        setProgramDetails(null);
                        setSchedulePreference('');
                        setCustomScheduleMessage('');
                        setCallbackNumber('');
                        setCallbackTimePreference('');
                        setPreferredDays([]);
                        setSelectedPrivateSessions([]);
                        setAvailablePrivateSessions([]);
                      } catch (error) {
                        console.error('❌ Enrollment error:', error);
                        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
                        toast.error(`Enrollment failed: ${errorMessage}. Please try again or contact us at oarayouthinnovationlab@gmail.com for assistance.`);
                      }
                    }}
                    disabled={
                      programDetails.programType === 'homeschool' && (
                        !schedulePreference ||
                        (schedulePreference === 'standard' && preferredDays.length === 0) ||
                        (schedulePreference === 'outside' && (!customScheduleMessage || !callbackNumber || !callbackTimePreference))
                      )
                    }
                  >
                    Enroll {selectedChild.firstName}
                  </button>
                </div>
              )}
            </form>

            {/* Current Enrollments Section */}
            {children.some(child => child.programEnrollments && child.programEnrollments.length > 0) && (
              <div className="current-enrollments-section">
                <h4 className="section-title">Current Enrollments</h4>
                <div className="enrollments-list">
                  {children.map((child) => 
                    child.programEnrollments && child.programEnrollments.map((enrollment: ProgramEnrollment) => (
                      <div key={enrollment.enrollmentId} className="enrollment-item">
                        <div className="enrollment-info">
                          <h5>{enrollment.programName}</h5>
                          <p className="enrollment-meta">
                            <span className="enrollment-child">{child.firstName} {child.lastName}</span> • 
                            {enrollment.programType.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())} • 
                            Tier: {enrollment.programTier.charAt(0).toUpperCase() + enrollment.programTier.slice(1)} • 
                            Status: <span className={`status-badge status-${enrollment.enrollmentStatus.toLowerCase()}`}>
                              {enrollment.enrollmentStatus}
                            </span>
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Courses Tab - Hidden from parent view */}

      {/* Course Enrollment Modal */}
      {showEnrollmentModal && enrollmentCourse && (
        <div className="modal-overlay">
          <div className="modal enrollment-modal">
            <div className="modal-header">
              <h3>📚 Enroll in Course</h3>
              <button 
                className="modal-close"
                onClick={() => {
                  setShowEnrollmentModal(false);
                  setEnrollmentCourse(null);
                  setSelectedChildForEnrollment(null);
                }}
              >
                ×
              </button>
            </div>
            
            <div className="modal-content">
              <div className="course-info">
                <h4>{enrollmentCourse.name}</h4>
                <p className="course-description">{enrollmentCourse.description}</p>
                <div className="course-details">
                  <p><strong>Tier:</strong> {enrollmentCourse.tier.charAt(0).toUpperCase() + enrollmentCourse.tier.slice(1)}</p>
                  <p><strong>Duration:</strong> {Math.floor(enrollmentCourse.durationMinutes / 60)}h {enrollmentCourse.durationMinutes % 60}m</p>
                  {enrollmentCourse.prerequisites && (
                    <p><strong>Prerequisites:</strong> {enrollmentCourse.prerequisites}</p>
                  )}
                </div>
              </div>
              
              <div className="children-selection">
                <h4>Select Child to Enroll:</h4>
                <div className="children-list">
                  {children.map(child => {
                    const childEnrollments = getChildEnrollments(child.id);
                    const isEnrolled = isChildEnrolledInCourse(child.id, enrollmentCourse.id);
                    const canEnroll = canEnrollChild(child.id);
                    
                    return (
                      <div 
                        key={child.id} 
                        className={`child-option ${selectedChildForEnrollment?.id === child.id ? 'selected' : ''} ${!canEnroll ? 'disabled' : ''}`}
                        onClick={() => canEnroll && setSelectedChildForEnrollment(child)}
                      >
                        <div className="child-info">
                          <h5>{child.firstName} {child.lastName}</h5>
                          <p className="child-tier">Tier: {child.tier}</p>
                          <p className="enrollment-status">
                            {childEnrollments.length}/2 courses enrolled
                            {isEnrolled && <span className="already-enrolled"> (Already enrolled)</span>}
                          </p>
                        </div>
                        
                        {!canEnroll && (
                          <div className="enrollment-limit">
                            <p>⚠️ Maximum 2 courses per child</p>
                          </div>
                        )}
                        
                        {isEnrolled && (
                          <div className="already-enrolled-badge">
                            <span>Already Enrolled</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            
            <div className="modal-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowEnrollmentModal(false);
                  setEnrollmentCourse(null);
                  setSelectedChildForEnrollment(null);
                }}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={() => selectedChildForEnrollment && handleConfirmEnrollment(selectedChildForEnrollment.id)}
                disabled={!selectedChildForEnrollment || isChildEnrolledInCourse(selectedChildForEnrollment.id, enrollmentCourse.id)}
              >
                Enroll Child
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scheduling Modal */}
      {showScheduleModal && (
        <div className="modal-overlay">
          <div className="modal schedule-modal">
            <div className="modal-header">
              <h3>📅 Schedule {scheduleType.charAt(0).toUpperCase() + scheduleType.slice(1)}</h3>
              <button 
                className="modal-close"
                onClick={() => {
                  setShowScheduleModal(false);
                  setSelectedScheduleItem(null);
                }}
              >
                ×
              </button>
            </div>
            
            <div className="modal-content">
              <div className="schedule-form">
                <div className="form-group">
                  <label htmlFor="schedule-child">Select Child:</label>
                  <select id="schedule-child" className="form-control">
                    {children.map(child => (
                      <option key={child.id} value={child.id}>
                        {child.firstName} {child.lastName}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="schedule-date">Date:</label>
                  <input 
                    id="schedule-date"
                    type="date" 
                    className="form-control"
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="schedule-time">Time:</label>
                  <input 
                    id="schedule-time"
                    type="time" 
                    className="form-control"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="schedule-duration">Duration (minutes):</label>
                  <select id="schedule-duration" className="form-control">
                    <option value="60">1 hour</option>
                    <option value="90">1.5 hours</option>
                    <option value="120">2 hours</option>
                    <option value="180">3 hours</option>
                    <option value="240">4 hours</option>
                    <option value="300">5 hours</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="schedule-location">Location:</label>
                  <select id="schedule-location" className="form-control">
                    <option value="OARA Makerspace - Room A">Room A</option>
                    <option value="OARA Makerspace - Computer Lab">Computer Lab</option>
                    <option value="OARA Makerspace - Robotics Lab">Robotics Lab</option>
                    <option value="OARA Makerspace - Main Hall">Main Hall</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="schedule-instructor">Instructor (optional):</label>
                  <input 
                    id="schedule-instructor"
                    type="text" 
                    className="form-control"
                    placeholder="Instructor name"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="schedule-notes">Notes (optional):</label>
                  <textarea 
                    id="schedule-notes"
                    className="form-control"
                    rows={3}
                    placeholder="Any special notes or requirements"
                  />
                </div>
              </div>
            </div>
            
            <div className="modal-actions">
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowScheduleModal(false);
                  setSelectedScheduleItem(null);
                }}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={() => {
                  alert(`${scheduleType.charAt(0).toUpperCase() + scheduleType.slice(1)} scheduled successfully!`);
                  setShowScheduleModal(false);
                  setSelectedScheduleItem(null);
                }}
              >
                Schedule {scheduleType.charAt(0).toUpperCase() + scheduleType.slice(1)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Camps Tab */}
      {activeTab === 'camps' && (
        <div className="dashboard-content">
          <div className="camps-header" style={{ marginBottom: '20px' }}>
            <h3>🏕️ Vacation & Summer Camps</h3>
            <p style={{ color: '#666', marginTop: '10px' }}>Browse and enroll in our exciting camp programs!</p>
          </div>

          {camps.length === 0 ? (
            <div className="no-camps" style={{ textAlign: 'center', padding: '40px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <h4>No camps available at this time</h4>
              <p>Check back later for upcoming vacation and summer camp programs!</p>
            </div>
          ) : (
            <div className="camps-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '20px' }}>
              {camps.map((camp) => {
                const availableSpots = camp.capacity - camp.enrolled_count;
                const isFull = availableSpots <= 0;
                const startDate = parseDateOnlyLocal(camp.start_date);
                const endDate = parseDateOnlyLocal(camp.end_date);
                
                // Detect mobile view
                const isMobile = window.innerWidth <= 768;
                const isExpanded = expandedCamps.has(camp.id);
                
                return (
                  <div key={camp.id} className="camp-card" style={{ 
                    border: '1px solid #ddd', 
                    borderRadius: '12px', 
                    overflow: 'hidden',
                    backgroundColor: '#fff',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease',
                    opacity: isFull ? 0.7 : 1
                  }}>
                    <div 
                      style={{ 
                        background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
                        color: 'white',
                        padding: isMobile ? '12px' : '20px',
                        cursor: isMobile ? 'pointer' : 'default',
                        position: 'relative'
                      }}
                      onClick={() => {
                        if (isMobile) {
                          setSelectedCamp(camp);
                          setShowCampDetails(true);
                        }
                      }}
                    >
                      <h4 style={{ margin: 0, fontSize: isMobile ? '16px' : '20px', fontWeight: 'bold' }}>
                        {camp.name}
                        {isMobile && <span style={{ fontSize: '12px', marginLeft: '8px', opacity: 0.8 }}>ℹ️</span>}
                      </h4>
                      {camp.theme && <p style={{ margin: '5px 0 0 0', fontSize: isMobile ? '12px' : '13px', opacity: 0.9 }}>{camp.theme}</p>}
                      {isFull && <div style={{ marginTop: '8px', padding: '4px 8px', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' }}>FULLY BOOKED</div>}
                      {isMobile && <p style={{ margin: '8px 0 0 0', fontSize: '11px', opacity: 0.85, fontStyle: 'italic' }}>Tap header for full details</p>}
                    </div>

                    <div style={{ padding: isMobile ? '12px' : '20px' }}>
                      {/* Always show basic info */}
                      <div style={{ marginBottom: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px', fontSize: isMobile ? '12px' : '14px' }}>
                          <span style={{ fontSize: '14px', marginRight: '6px' }}>📅</span>
                          <span><strong>Dates:</strong> {startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        </div>
                        {camp.age_min && camp.age_max && (
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px', fontSize: isMobile ? '12px' : '14px' }}>
                            <span style={{ fontSize: '14px', marginRight: '6px' }}>👶</span>
                            <span><strong>Ages:</strong> {camp.age_min}-{camp.age_max} years</span>
                          </div>
                        )}
                        {(camp.pricing_model === 'weekly' || camp.pricing_model === 'weekly_and_drop_in') && camp.weekly_price && (
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px', fontSize: isMobile ? '12px' : '14px' }}>
                            <span style={{ fontSize: '14px', marginRight: '6px' }}>💰</span>
                            <span><strong>Weekly:</strong> ${Number(camp.weekly_price).toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Expanded details - show only when expanded */}
                      {(!isMobile || isExpanded) && (
                      <div style={{ marginBottom: isMobile ? '10px' : '15px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px', fontSize: isMobile ? '12px' : '14px' }}>
                          <span style={{ fontSize: '14px', marginRight: '6px' }}>⏰</span>
                          <span><strong>Time:</strong> {camp.camp_start_time} - {camp.camp_end_time}</span>
                        </div>
                        {camp.location_name && (
                          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '6px', fontSize: isMobile ? '12px' : '14px' }}>
                            <span style={{ fontSize: '14px', marginRight: '6px' }}>📍</span>
                            <span><strong>Location:</strong> {camp.location_name}</span>
                          </div>
                        )}
                      </div>
                      )}

                      {(!isMobile || isExpanded) && (
                      <div style={{ 
                        backgroundColor: '#f8f9fa', 
                        padding: isMobile ? '10px' : '12px', 
                        borderRadius: '8px', 
                        marginBottom: isMobile ? '10px' : '15px',
                        fontSize: isMobile ? '12px' : '14px'
                      }}>
                        <h5 style={{ margin: '0 0 8px 0', fontSize: isMobile ? '12px' : '14px', fontWeight: 'bold' }}>💰 All Pricing Options:</h5>
                        {camp.pricing_model === 'full_camp' && camp.base_price && (
                          <div><strong>Full Camp:</strong> ${Number(camp.base_price).toFixed(2)}</div>
                        )}
                        {(camp.pricing_model === 'weekly' || camp.pricing_model === 'weekly_and_drop_in') && camp.weekly_price && (
                          <div><strong>Weekly:</strong> ${Number(camp.weekly_price).toFixed(2)} per week</div>
                        )}
                        {(camp.pricing_model === 'drop_in' || camp.pricing_model === 'weekly_and_drop_in') && camp.drop_in_price && (
                          <div><strong>Drop-in:</strong> ${Number(camp.drop_in_price).toFixed(2)} per day</div>
                        )}
                        
                        {(camp.early_dropoff_enabled || camp.late_pickup_enabled) && (
                          <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #dee2e6', fontSize: isMobile ? '11px' : '13px' }}>
                            <strong>Extended Hours:</strong>
                            {camp.early_dropoff_enabled && camp.early_dropoff_fee && (
                              <div>• Early drop-off ({camp.early_dropoff_start} - {camp.camp_start_time}): +${Number(camp.early_dropoff_fee).toFixed(2)}/day</div>
                            )}
                            {camp.late_pickup_enabled && camp.late_pickup_fee && (
                              <div>• Late pickup ({camp.camp_end_time} - {camp.late_pickup_end}): +${Number(camp.late_pickup_fee).toFixed(2)}/day</div>
                            )}
                          </div>
                        )}
                      </div>
                      )}

                      {camp.note && (
                        <div style={{ 
                          backgroundColor: '#fff9e6', 
                          padding: isMobile ? '10px' : '12px', 
                          borderRadius: '8px', 
                          marginBottom: isMobile ? '10px' : '15px',
                          borderLeft: '3px solid #ffc107',
                          fontSize: isMobile ? '12px' : '13px'
                        }}>
                          <p style={{ margin: 0, lineHeight: '1.5', color: '#555' }}>
                            <strong>📝 Note:</strong> {camp.note}
                          </p>
                        </div>
                      )}

                      {(!isMobile || isExpanded) && camp.camp_days && camp.camp_days.length > 0 && (
                        <div style={{ marginBottom: '12px', fontSize: isMobile ? '11px' : '13px', color: '#666' }}>
                          <strong>Camp Days:</strong>{' '}
                          {(() => {
                            const availableDays = camp.camp_days
                              .filter(d => d.is_available)
                              .map(d => {
                                const [year, month, day] = d.date.split('-').map(Number);
                                return { date: new Date(year, month - 1, day), month, day };
                              });
                            
                            if (availableDays.length === 0) return '';
                            
                            // Group by month
                            const grouped = availableDays.reduce((acc, item) => {
                              const monthName = item.date.toLocaleDateString('en-US', { month: 'short' });
                              if (!acc[monthName]) acc[monthName] = [];
                              acc[monthName].push(item.day);
                              return acc;
                            }, {} as Record<string, number[]>);
                            
                            // Format as "Dec 22,23,24,26,29,30,31, Jan 2"
                            return Object.entries(grouped)
                              .map(([month, days]) => `${month} ${days.join(',')}`)
                              .join(', ');
                          })()}
                        </div>
                      )}

                      {(!isMobile || isExpanded) && camp.description && (
                        <p style={{ 
                          fontSize: isMobile ? '12px' : '14px', 
                          color: '#555', 
                          marginBottom: '10px', 
                          lineHeight: '1.5'
                        }}>{camp.description}</p>
                      )}

                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginTop: '10px',
                        paddingTop: '10px',
                        borderTop: '1px solid #dee2e6',
                        gap: '8px',
                        flexWrap: 'wrap'
                      }}>
                        {isFull && (
                          <div style={{ fontSize: '11px', color: '#dc3545', fontWeight: 'bold', width: '100%', textAlign: 'center' }}>
                            Fully Booked
                          </div>
                        )}
                        {isMobile && !isFull && (
                          <>
                            <button 
                              className="btn btn-secondary btn-sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleCampExpanded(camp.id);
                              }}
                              style={{
                                padding: '8px 16px',
                                fontSize: '13px',
                                fontWeight: 'bold',
                                backgroundColor: '#6c757d',
                                border: 'none',
                                color: 'white',
                                flex: '1'
                              }}
                            >
                              {isExpanded ? '▲ Less Info' : '▼ More Info'}
                            </button>
                            <button 
                              className="btn btn-primary btn-sm"
                              onClick={() => {
                                setSelectedCamp(camp);
                                setShowCampEnrollment(true);
                                setCampEnrollmentType('full_camp');
                                setSelectedWeeks([]);
                                setSelectedDays([]);
                                setNeedsEarlyDropoff(false);
                                setNeedsLatePickup(false);
                                setEarlyDropoffDays([]);
                                setLatePickupDays([]);
                              }}
                              style={{
                                padding: '8px 16px',
                                fontSize: '13px',
                                fontWeight: 'bold',
                                flex: '1'
                              }}
                            >
                              Enroll Now
                            </button>
                          </>
                        )}
                        {!isMobile && (
                          <>
                            <button 
                              className="btn btn-secondary btn-sm"
                              onClick={() => {
                                setSelectedCamp(camp);
                                setShowCampDetails(true);
                              }}
                              style={{
                                padding: '8px 16px',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                backgroundColor: '#6c757d',
                                border: 'none',
                                color: 'white'
                              }}
                            >
                              View Details
                            </button>
                            <button 
                              className="btn btn-primary btn-sm"
                              onClick={() => {
                                setSelectedCamp(camp);
                                setShowCampEnrollment(true);
                                setCampEnrollmentType('full_camp');
                                setSelectedWeeks([]);
                                setSelectedDays([]);
                                setNeedsEarlyDropoff(false);
                                setNeedsLatePickup(false);
                                setEarlyDropoffDays([]);
                                setLatePickupDays([]);
                              }}
                              disabled={isFull}
                              style={{
                                padding: '8px 16px',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                opacity: isFull ? 0.5 : 1,
                                cursor: isFull ? 'not-allowed' : 'pointer'
                              }}
                            >
                              {isFull ? 'Full' : 'Enroll Now'}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Camp Details Modal */}
          {showCampDetails && selectedCamp && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 9999,
              padding: '20px'
            }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                maxWidth: '800px',
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
              }}>
                {/* Modal Header */}
                <div style={{
                  background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
                  color: 'white',
                  padding: '25px',
                  borderTopLeftRadius: '12px',
                  borderTopRightRadius: '12px',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                      <h2 style={{ margin: 0, fontSize: '24px' }}>{selectedCamp.name}</h2>
                      <p style={{ margin: '5px 0 0 0', fontSize: '14px', opacity: 0.9 }}>{selectedCamp.theme}</p>
                      {((selectedCamp as any).age_min || (selectedCamp as any).age_max) && (
                        <p style={{ 
                          margin: '8px 0 0 0', 
                          fontSize: '13px', 
                          opacity: 0.95,
                          fontWeight: '600',
                          backgroundColor: 'rgba(255,255,255,0.2)',
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: '12px'
                        }}>
                          👦 Ages {(selectedCamp as any).age_min}-{(selectedCamp as any).age_max} | 
                          {' '}{getCampAudienceLabel(selectedCamp)}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => setShowCampDetails(false)}
                      style={{
                        background: 'rgba(255,255,255,0.2)',
                        border: 'none',
                        color: 'white',
                        fontSize: '24px',
                        cursor: 'pointer',
                        borderRadius: '50%',
                        width: '36px',
                        height: '36px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0
                      }}
                    >
                      ×
                    </button>
                  </div>
                </div>

                {/* Modal Body */}
                <div style={{ padding: '25px' }}>
                  {/* Description */}
                  {selectedCamp.description && (
                    <div style={{ marginBottom: '25px' }}>
                      <h3 style={{ fontSize: '18px', marginBottom: '10px', color: '#333' }}>📝 About This Camp</h3>
                      <p style={{ fontSize: '15px', lineHeight: '1.6', color: '#555' }}>{selectedCamp.description}</p>
                    </div>
                  )}

                  {/* Camp Details */}
                  <div style={{ marginBottom: '25px' }}>
                    <h3 style={{ fontSize: '18px', marginBottom: '15px', color: '#333' }}>ℹ️ Camp Information</h3>
                    <div style={{ display: 'grid', gap: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ fontSize: '18px', marginRight: '10px' }}>📅</span>
                        <div>
                          <strong>Dates:</strong> {parseDateOnlyLocal(selectedCamp.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - {parseDateOnlyLocal(selectedCamp.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{ fontSize: '18px', marginRight: '10px' }}>⏰</span>
                        <div>
                          <strong>Camp Hours:</strong> {selectedCamp.camp_start_time} - {selectedCamp.camp_end_time}
                        </div>
                      </div>
                      {selectedCamp.location_name && (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span style={{ fontSize: '18px', marginRight: '10px' }}>📍</span>
                          <div>
                            <strong>Location:</strong> {selectedCamp.location_name}
                            {selectedCamp.location_address && <span style={{ display: 'block', fontSize: '13px', color: '#666', marginTop: '2px' }}>{selectedCamp.location_address}</span>}
                          </div>
                        </div>
                      )}
                      {((selectedCamp as any).age_min || (selectedCamp as any).age_max) && (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span style={{ fontSize: '18px', marginRight: '10px' }}>👶</span>
                          <div>
                            <strong>Age Group:</strong> {(selectedCamp as any).age_min}-{(selectedCamp as any).age_max} years ({getCampAudienceLabel(selectedCamp)})
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Pricing */}
                  <div style={{ marginBottom: '25px' }}>
                    <h3 style={{ fontSize: '18px', marginBottom: '15px', color: '#333' }}>💰 Pricing Options</h3>
                    <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px' }}>
                      {(selectedCamp.pricing_model === 'weekly' || selectedCamp.pricing_model === 'weekly_and_drop_in') && selectedCamp.weekly_price && (
                        <div style={{ marginBottom: '10px' }}>
                          <strong>Weekly:</strong> ${Number(selectedCamp.weekly_price).toFixed(2)} per week
                        </div>
                      )}
                      {(selectedCamp.pricing_model === 'drop_in' || selectedCamp.pricing_model === 'weekly_and_drop_in') && selectedCamp.drop_in_price && (
                        <div style={{ marginBottom: '10px' }}>
                          <strong>Drop-in:</strong> ${Number(selectedCamp.drop_in_price).toFixed(2)} per day
                        </div>
                      )}
                      
                      {(selectedCamp.early_dropoff_enabled || selectedCamp.late_pickup_enabled) && (
                        <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #dee2e6' }}>
                          <strong>Extended Hours Available:</strong>
                          {selectedCamp.early_dropoff_enabled && selectedCamp.early_dropoff_fee && (
                            <div style={{ marginTop: '5px', fontSize: '14px' }}>
                              • Early drop-off ({selectedCamp.early_dropoff_start} - {selectedCamp.camp_start_time}): +${Number(selectedCamp.early_dropoff_fee).toFixed(2)}/day
                            </div>
                          )}
                          {selectedCamp.late_pickup_enabled && selectedCamp.late_pickup_fee && (
                            <div style={{ marginTop: '5px', fontSize: '14px' }}>
                              • Late pickup ({selectedCamp.camp_end_time} - {selectedCamp.late_pickup_end}): +${Number(selectedCamp.late_pickup_fee).toFixed(2)}/day
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Schedule/Days */}
                  {selectedCamp.camp_days && selectedCamp.camp_days.length > 0 && (
                    <div style={{ marginBottom: '25px' }}>
                      <h3 style={{ fontSize: '18px', marginBottom: '15px', color: '#333' }}>📆 Camp Schedule</h3>
                      <p style={{ marginBottom: '10px', lineHeight: '1.6' }}>
                        <strong>Camp Days:</strong> {selectedCamp.camp_days
                          .filter(d => d.is_available)
                          .map(d => {
                            const [year, month, day] = d.date.split('-').map(Number);
                            const date = new Date(year, month - 1, day);
                            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                          })
                          .join(', ')}
                      </p>
                      <p style={{ fontSize: '14px', color: '#666' }}>
                        <strong>Total:</strong> {selectedCamp.camp_days.filter(d => d.is_available).length} days
                        {selectedCamp.camp_days.filter(d => !d.is_available).length > 0 && (
                          <span> ({selectedCamp.camp_days.filter(d => !d.is_available).length} holiday{selectedCamp.camp_days.filter(d => !d.is_available).length !== 1 ? 's' : ''})</span>
                        )}
                      </p>
                    </div>
                  )}


                  {/* Action Buttons */}
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', paddingTop: '15px', borderTop: '1px solid #dee2e6' }}>
                    <button
                      onClick={() => setShowCampDetails(false)}
                      style={{
                        padding: '10px 20px',
                        fontSize: '14px',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      Close
                    </button>
                    {selectedCamp.capacity - selectedCamp.enrolled_count > 0 && (
                      <button
                        onClick={() => {
                          setShowCampDetails(false);
                          setShowCampEnrollment(true);
                          setCampEnrollmentType('full_camp');
                          setSelectedWeeks([]);
                          setSelectedDays([]);
                          setNeedsEarlyDropoff(false);
                          setNeedsLatePickup(false);
                        }}
                        style={{
                          padding: '10px 20px',
                          fontSize: '14px',
                          background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: 'bold'
                        }}
                      >
                        Enroll Now
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Camp Enrollment Modal */}
          {showCampEnrollment && selectedCamp && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 9999,
              padding: '20px'
            }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                maxWidth: '700px',
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
              }}>
                {/* Modal Header */}
                <div style={{
                  background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
                  color: 'white',
                  padding: '20px',
                  borderTopLeftRadius: '12px',
                  borderTopRightRadius: '12px'
                }}>
                  <h3 style={{ margin: 0, fontSize: '22px' }}>Enroll in {selectedCamp.name}</h3>
                  <p style={{ margin: '5px 0 0 0', fontSize: '14px', opacity: 0.9 }}>{selectedCamp.theme}</p>
                </div>

                {/* Modal Body */}
                <div style={{ padding: '30px' }}>
                  {/* Step 1: Select Child */}
                  <div style={{ marginBottom: '25px' }}>
                    <h4 style={{ marginBottom: '15px', fontSize: '16px', fontWeight: 'bold' }}>1. Select Child</h4>
                    <select 
                      className="form-control"
                      value={selectedChildForCamp?.id || ''}
                      onChange={(e) => {
                        const child = children.find(c => c.id === e.target.value);
                        setSelectedChildForCamp(child || null);
                      }}
                      style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                    >
                      <option value="">-- Select a child --</option>
                      {children.map(child => (
                        <option key={child.id} value={child.id}>
                          {child.firstName} {child.lastName} (Age: {child.age || 'N/A'})
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedChildForCamp && (
                    <>
                      {/* Step 2: Select Enrollment Type */}
                      <div style={{ marginBottom: '25px' }}>
                        <h4 style={{ marginBottom: '15px', fontSize: '16px', fontWeight: 'bold' }}>2. Select Enrollment Type</h4>
                        {selectedCamp.pricing_model === 'full_camp' && (
                          <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <input 
                                type="radio" 
                                checked={true}
                                readOnly
                                style={{ marginRight: '10px' }}
                              />
                              <div>
                                <strong>Full Camp</strong> - ${Number(selectedCamp.base_price).toFixed(2)}
                                <div style={{ fontSize: '13px', color: '#666' }}>Complete camp enrollment</div>
                              </div>
                            </div>
                          </div>
                        )}

                        {(selectedCamp.pricing_model === 'weekly' || selectedCamp.pricing_model === 'weekly_and_drop_in') && (
                          <div style={{ marginBottom: '10px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', cursor: 'pointer', border: campEnrollmentType === 'weekly' ? '2px solid #667eea' : '2px solid transparent' }}
                               onClick={() => setCampEnrollmentType('weekly')}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <input 
                                type="radio" 
                                checked={campEnrollmentType === 'weekly'}
                                onChange={() => setCampEnrollmentType('weekly')}
                                style={{ marginRight: '10px' }}
                              />
                              <div>
                                <strong>Weekly</strong> - ${Number(selectedCamp.weekly_price).toFixed(2)} per week
                                <div style={{ fontSize: '13px', color: '#666' }}>Select which week(s) to attend</div>
                              </div>
                            </div>
                          </div>
                        )}

                        {(selectedCamp.pricing_model === 'drop_in' || selectedCamp.pricing_model === 'weekly_and_drop_in') && (
                          <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', cursor: 'pointer', border: campEnrollmentType === 'drop_in' ? '2px solid #667eea' : '2px solid transparent' }}
                               onClick={() => setCampEnrollmentType('drop_in')}>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <input 
                                type="radio" 
                                checked={campEnrollmentType === 'drop_in'}
                                onChange={() => setCampEnrollmentType('drop_in')}
                                style={{ marginRight: '10px' }}
                              />
                              <div>
                                <strong>Drop-in</strong> - ${Number(selectedCamp.drop_in_price).toFixed(2)} per day
                                <div style={{ fontSize: '13px', color: '#666' }}>Select specific days to attend</div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Step 3: Week/Day Selection */}
                      {campEnrollmentType === 'weekly' && selectedCamp.camp_days && selectedCamp.camp_days.length > 0 && (
                        <div style={{ marginBottom: '25px' }}>
                          <h4 style={{ marginBottom: '15px', fontSize: '16px', fontWeight: 'bold' }}>3. Select Week(s)</h4>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
                            {Array.from(new Set(selectedCamp.camp_days.filter(d => d.is_available).map(d => d.week_number))).sort().map(weekNum => {
                              const weekDays = selectedCamp.camp_days!.filter(d => d.week_number === weekNum && d.is_available);
                              return (
                                <div key={weekNum} 
                                     style={{ 
                                       padding: '12px', 
                                       backgroundColor: selectedWeeks.includes(weekNum) ? '#667eea' : '#f8f9fa', 
                                       color: selectedWeeks.includes(weekNum) ? 'white' : '#333',
                                       borderRadius: '8px', 
                                       cursor: 'pointer',
                                       border: '2px solid transparent',
                                       transition: 'all 0.3s'
                                     }}
                                     onClick={() => {
                                       if (selectedWeeks.includes(weekNum)) {
                                         setSelectedWeeks(selectedWeeks.filter(w => w !== weekNum));
                                       } else {
                                         setSelectedWeeks([...selectedWeeks, weekNum]);
                                       }
                                     }}>
                                  <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Week {weekNum}</div>
                                  <div style={{ fontSize: '12px', opacity: 0.9 }}>{weekDays.length} days</div>
                                  <div style={{ fontSize: '12px', opacity: 0.9 }}>${Number(selectedCamp.weekly_price).toFixed(2)}</div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {campEnrollmentType === 'drop_in' && selectedCamp.camp_days && selectedCamp.camp_days.length > 0 && (
                        <div style={{ marginBottom: '25px' }}>
                          <h4 style={{ marginBottom: '15px', fontSize: '16px', fontWeight: 'bold' }}>3. Select Day(s)</h4>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '10px' }}>
                            {selectedCamp.camp_days.filter(d => d.is_available).map(day => {
                              const [year, month, dayNum] = day.date.split('-').map(Number);
                              const date = new Date(year, month - 1, dayNum);
                              const isSelected = selectedDays.includes(day.date);
                              
                              return (
                                <div key={day.id} 
                                     style={{ 
                                       padding: '12px', 
                                       backgroundColor: isSelected ? '#667eea' : '#f8f9fa', 
                                       color: isSelected ? 'white' : '#333',
                                       borderRadius: '8px', 
                                       cursor: 'pointer',
                                       border: '2px solid transparent',
                                       transition: 'all 0.3s'
                                     }}
                                     onClick={() => {
                                       if (isSelected) {
                                         setSelectedDays(selectedDays.filter(d => d !== day.date));
                                       } else {
                                         setSelectedDays([...selectedDays, day.date]);
                                       }
                                     }}>
                                  <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                                    {date.toLocaleDateString('en-US', { weekday: 'short' })}
                                  </div>
                                  <div style={{ fontSize: '13px', opacity: 0.9 }}>
                                    {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                  </div>
                                  <div style={{ fontSize: '12px', opacity: 0.9, marginTop: '5px' }}>
                                    ${Number(selectedCamp.drop_in_price).toFixed(2)}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Step 4: Extended Hours */}
                      {(selectedCamp.early_dropoff_enabled || selectedCamp.late_pickup_enabled) && (
                        <div style={{ marginBottom: '25px' }}>
                          <h4 style={{ marginBottom: '15px', fontSize: '16px', fontWeight: 'bold' }}>4. Extended Hours (Optional)</h4>
                          <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>Select which days you need extended hours</p>
                          
                          {(() => {
                            // Get list of days based on enrollment type
                            let daysToShow: Array<{date: string, displayName: string}> = [];
                            
                            if (campEnrollmentType === 'weekly' && selectedWeeks.length > 0 && selectedCamp.camp_days) {
                              // Show days from selected weeks
                              selectedWeeks.forEach(weekNum => {
                                const weekDays = (selectedCamp.camp_days || [])
                                  .filter((d: any) => d.week_number === weekNum && d.is_available)
                                  .sort((a: any, b: any) => a.date.localeCompare(b.date));
                                weekDays.forEach((day: any) => {
                                  const date = new Date(day.date + 'T00:00:00');
                                  const displayName = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                                  daysToShow.push({ date: day.date, displayName });
                                });
                              });
                            } else if (campEnrollmentType === 'drop_in' && selectedDays.length > 0) {
                              // Show selected drop-in days
                              selectedDays.forEach(dateStr => {
                                const date = new Date(dateStr + 'T00:00:00');
                                const displayName = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                                daysToShow.push({ date: dateStr, displayName });
                              });
                            } else if (campEnrollmentType === 'full_camp' && selectedCamp.camp_days) {
                              // Show all available camp days
                              (selectedCamp.camp_days || [])
                                .filter((d: any) => d.is_available)
                                .sort((a: any, b: any) => a.date.localeCompare(b.date))
                                .forEach((day: any) => {
                                  const date = new Date(day.date + 'T00:00:00');
                                  const displayName = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                                  daysToShow.push({ date: day.date, displayName });
                                });
                            }
                            
                            if (daysToShow.length === 0) {
                              return <p style={{ fontSize: '14px', color: '#999', fontStyle: 'italic' }}>Please select enrollment type and days first</p>;
                            }
                            
                            return (
                              <div style={{ display: 'grid', gap: '10px' }}>
                                {/* Header row */}
                                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '10px', padding: '8px 12px', backgroundColor: '#e9ecef', borderRadius: '6px', fontWeight: 'bold', fontSize: '13px' }}>
                                  <div>Day</div>
                                  {selectedCamp.early_dropoff_enabled && <div style={{ textAlign: 'center' }}>Early Drop-off</div>}
                                  {selectedCamp.late_pickup_enabled && <div style={{ textAlign: 'center' }}>Late Pickup</div>}
                                </div>
                                
                                {/* Day rows */}
                                {daysToShow.map((day) => (
                                  <div key={day.date} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '10px', padding: '10px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', alignItems: 'center' }}>
                                    <div style={{ fontSize: '14px', fontWeight: '500' }}>{day.displayName}</div>
                                    
                                    {selectedCamp.early_dropoff_enabled && (
                                      <div style={{ textAlign: 'center' }}>
                                        <input 
                                          type="checkbox" 
                                          checked={earlyDropoffDays.includes(day.date)}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setEarlyDropoffDays([...earlyDropoffDays, day.date]);
                                              setNeedsEarlyDropoff(true);
                                            } else {
                                              const updated = earlyDropoffDays.filter(d => d !== day.date);
                                              setEarlyDropoffDays(updated);
                                              if (updated.length === 0) setNeedsEarlyDropoff(false);
                                            }
                                          }}
                                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                        />
                                      </div>
                                    )}
                                    
                                    {selectedCamp.late_pickup_enabled && (
                                      <div style={{ textAlign: 'center' }}>
                                        <input 
                                          type="checkbox" 
                                          checked={latePickupDays.includes(day.date)}
                                          onChange={(e) => {
                                            if (e.target.checked) {
                                              setLatePickupDays([...latePickupDays, day.date]);
                                              setNeedsLatePickup(true);
                                            } else {
                                              const updated = latePickupDays.filter(d => d !== day.date);
                                              setLatePickupDays(updated);
                                              if (updated.length === 0) setNeedsLatePickup(false);
                                            }
                                          }}
                                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                ))}
                                
                                {/* Cost summary */}
                                {(earlyDropoffDays.length > 0 || latePickupDays.length > 0) && (
                                  <div style={{ padding: '12px', backgroundColor: '#e7f3ff', borderRadius: '6px', fontSize: '13px', marginTop: '5px' }}>
                                    {earlyDropoffDays.length > 0 && (
                                      <div><strong>Early Drop-off:</strong> {earlyDropoffDays.length} days × ${Number(selectedCamp.early_dropoff_fee).toFixed(2)} = ${(earlyDropoffDays.length * Number(selectedCamp.early_dropoff_fee)).toFixed(2)}</div>
                                    )}
                                    {latePickupDays.length > 0 && (
                                      <div><strong>Late Pickup:</strong> {latePickupDays.length} days × ${Number(selectedCamp.late_pickup_fee).toFixed(2)} = ${(latePickupDays.length * Number(selectedCamp.late_pickup_fee)).toFixed(2)}</div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      )}

                      {/* Step 5: Discount Code (only show if enabled for this camp) */}
                      {selectedCamp.discount_codes_enabled && (
                      <div style={{ marginBottom: '25px' }}>
                        <h4 style={{ marginBottom: '15px', fontSize: '16px', fontWeight: 'bold' }}>5. Discount Code (Optional)</h4>
                        <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>If you have a discount code, enter it below</p>
                        
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                          <div style={{ flex: 1 }}>
                            <input
                              type="text"
                              placeholder="Enter discount code"
                              value={discountCode}
                              onChange={(e) => {
                                setDiscountCode(e.target.value.toUpperCase());
                                setDiscountValidation(null);
                              }}
                              style={{
                                width: '100%',
                                padding: '10px 15px',
                                border: '1px solid #ced4da',
                                borderRadius: '6px',
                                fontSize: '14px'
                              }}
                            />
                            {discountValidation && (
                              <div style={{
                                marginTop: '8px',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                fontSize: '13px',
                                backgroundColor: discountValidation.valid ? '#d4edda' : '#f8d7da',
                                color: discountValidation.valid ? '#155724' : '#721c24',
                                border: `1px solid ${discountValidation.valid ? '#c3e6cb' : '#f5c6cb'}`
                              }}>
                                {discountValidation.valid ? (
                                  <div>
                                    <strong>✓ Discount Applied!</strong>
                                    <div style={{ marginTop: '4px' }}>
                                      {discountValidation.discount_type === 'percentage' 
                                        ? `${discountValidation.discount_value}% off`
                                        : `$${discountValidation.discount_value.toFixed(2)} off`}
                                      {' '}(${discountValidation.discount_amount.toFixed(2)} discount)
                                    </div>
                                  </div>
                                ) : (
                                  <div>
                                    <strong>✗ {discountValidation.message || 'Invalid discount code'}</strong>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={async () => {
                              if (!discountCode.trim()) {
                                setDiscountValidation(null);
                                return;
                              }
                              
                              setValidatingDiscount(true);
                              try {
                                const token = localStorage.getItem('cognitoToken') || '';
                                
                                // Calculate current purchase amount for validation
                                let purchaseAmount = 0;
                                if (campEnrollmentType === 'full_camp') {
                                  purchaseAmount = Number(selectedCamp.base_price || 0);
                                } else if (campEnrollmentType === 'weekly' && selectedWeeks.length > 0) {
                                  purchaseAmount = Number(selectedCamp.weekly_price || 0) * selectedWeeks.length;
                                } else if (campEnrollmentType === 'drop_in' && selectedDays.length > 0) {
                                  purchaseAmount = Number(selectedCamp.drop_in_price || 0) * selectedDays.length;
                                }
                                
                                // Add extended hours fees
                                purchaseAmount += (earlyDropoffDays.length * Number(selectedCamp.early_dropoff_fee || 0));
                                purchaseAmount += (latePickupDays.length * Number(selectedCamp.late_pickup_fee || 0));
                                
                                const response = await fetch(`${config.BACKEND_URL}/api/camps/discounts/validate`, {
                                  method: 'POST',
                                  headers: {
                                    'Authorization': `Bearer ${token}`,
                                    'Content-Type': 'application/json'
                                  },
                                  body: JSON.stringify({
                                    camp_id: selectedCamp.id,
                                    discount_code: discountCode.trim(),
                                    purchase_amount: purchaseAmount
                                  })
                                });
                                
                                if (response.ok) {
                                  const validation = await response.json();
                                  console.log('✅ Discount validation response:', validation);
                                  console.log('💰 Discount amount:', validation.discount_amount, 'Type:', typeof validation.discount_amount);
                                  setDiscountValidation(validation);
                                } else {
                                  let errorMessage = 'Failed to validate discount code';
                                  try {
                                    const error = await response.json();
                                    errorMessage = error.detail || error.message || `HTTP ${response.status}: ${response.statusText}`;
                                    console.error('Discount validation error:', error);
                                  } catch (e) {
                                    errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                                    console.error('Failed to parse error response:', e);
                                  }
                                  setDiscountValidation({
                                    valid: false,
                                    message: errorMessage
                                  });
                                }
                              } catch (error) {
                                console.error('Error validating discount code:', error);
                                setDiscountValidation({
                                  valid: false,
                                  message: 'Error validating discount code'
                                });
                              } finally {
                                setValidatingDiscount(false);
                              }
                            }}
                            disabled={validatingDiscount || !discountCode.trim()}
                            style={{
                              padding: '10px 20px',
                              backgroundColor: validatingDiscount ? '#6c757d' : '#007bff',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '14px',
                              fontWeight: '500',
                              cursor: validatingDiscount ? 'not-allowed' : 'pointer',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {validatingDiscount ? 'Validating...' : 'Apply'}
                          </button>
                        </div>
                      </div>
                      )}

                      {/* Price Summary */}
                      {(campEnrollmentType !== 'full_camp' && (selectedWeeks.length > 0 || selectedDays.length > 0)) && (
                        <div style={{ 
                          backgroundColor: '#f8f9fa', 
                          padding: '15px', 
                          borderRadius: '8px', 
                          marginBottom: '20px' 
                        }}>
                          <h4 style={{ marginBottom: '10px', fontSize: '16px', fontWeight: 'bold' }}>Price Summary</h4>
                          {campEnrollmentType === 'weekly' && selectedWeeks.length > 0 && (
                            <div>
                              <div>Weekly rate: ${Number(selectedCamp.weekly_price).toFixed(2)} × {selectedWeeks.length} week(s) = ${(Number(selectedCamp.weekly_price) * selectedWeeks.length).toFixed(2)}</div>
                              {earlyDropoffDays.length > 0 && selectedCamp.early_dropoff_fee && (
                                <div style={{ fontSize: '13px', color: '#666' }}>+ Early drop-off: ${(Number(selectedCamp.early_dropoff_fee) * earlyDropoffDays.length).toFixed(2)} ({earlyDropoffDays.length} day{earlyDropoffDays.length !== 1 ? 's' : ''})</div>
                              )}
                              {latePickupDays.length > 0 && selectedCamp.late_pickup_fee && (
                                <div style={{ fontSize: '13px', color: '#666' }}>+ Late pickup: ${(Number(selectedCamp.late_pickup_fee) * latePickupDays.length).toFixed(2)} ({latePickupDays.length} day{latePickupDays.length !== 1 ? 's' : ''})</div>
                              )}
                              {discountValidation?.valid && discountValidation?.discount_amount > 0 && (
                                <div style={{ fontSize: '13px', color: '#28a745', fontWeight: '500' }}>
                                  - Discount ({discountCode}): ${discountValidation.discount_amount.toFixed(2)}
                                </div>
                              )}
                              <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #dee2e6', fontWeight: 'bold', fontSize: '18px' }}>
                                Total: ${(
                                  (Number(selectedCamp.weekly_price) * selectedWeeks.length) +
                                  (earlyDropoffDays.length > 0 && selectedCamp.early_dropoff_fee ? Number(selectedCamp.early_dropoff_fee) * earlyDropoffDays.length : 0) +
                                  (latePickupDays.length > 0 && selectedCamp.late_pickup_fee ? Number(selectedCamp.late_pickup_fee) * latePickupDays.length : 0) -
                                  (discountValidation?.valid && discountValidation?.discount_amount > 0 ? Number(discountValidation.discount_amount) : 0)
                                ).toFixed(2)}
                              </div>
                            </div>
                          )}
                          {campEnrollmentType === 'drop_in' && selectedDays.length > 0 && (
                            <div>
                              <div>Drop-in rate: ${Number(selectedCamp.drop_in_price).toFixed(2)} × {selectedDays.length} day(s) = ${(Number(selectedCamp.drop_in_price) * selectedDays.length).toFixed(2)}</div>
                              {earlyDropoffDays.length > 0 && selectedCamp.early_dropoff_fee && (
                                <div style={{ fontSize: '13px', color: '#666' }}>+ Early drop-off: ${(Number(selectedCamp.early_dropoff_fee) * earlyDropoffDays.length).toFixed(2)} ({earlyDropoffDays.length} day{earlyDropoffDays.length !== 1 ? 's' : ''})</div>
                              )}
                              {latePickupDays.length > 0 && selectedCamp.late_pickup_fee && (
                                <div style={{ fontSize: '13px', color: '#666' }}>+ Late pickup: ${(Number(selectedCamp.late_pickup_fee) * latePickupDays.length).toFixed(2)} ({latePickupDays.length} day{latePickupDays.length !== 1 ? 's' : ''})</div>
                              )}
                              {discountValidation?.valid && discountValidation?.discount_amount > 0 && (
                                <div style={{ fontSize: '13px', color: '#28a745', fontWeight: '500' }}>
                                  - Discount ({discountCode}): ${discountValidation.discount_amount.toFixed(2)}
                                </div>
                              )}
                              <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #dee2e6', fontWeight: 'bold', fontSize: '18px' }}>
                                Total: ${(
                                  (Number(selectedCamp.drop_in_price) * selectedDays.length) +
                                  (earlyDropoffDays.length > 0 && selectedCamp.early_dropoff_fee ? Number(selectedCamp.early_dropoff_fee) * earlyDropoffDays.length : 0) +
                                  (latePickupDays.length > 0 && selectedCamp.late_pickup_fee ? Number(selectedCamp.late_pickup_fee) * latePickupDays.length : 0) -
                                  (discountValidation?.valid && discountValidation?.discount_amount > 0 ? Number(discountValidation.discount_amount) : 0)
                                ).toFixed(2)}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Modal Footer */}
                <div style={{ 
                  padding: '20px 30px', 
                  borderTop: '1px solid #dee2e6', 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  gap: '10px'
                }}>
                  <button 
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowCampEnrollment(false);
                      setSelectedCamp(null);
                      setSelectedChildForCamp(null);
                    }}
                    style={{ padding: '10px 20px' }}
                  >
                    Cancel
                  </button>
                  <button 
                    className="btn btn-primary"
                    onClick={async () => {
                      if (isCampEnrollmentSubmitting) return;

                      // Validation
                      if (!selectedChildForCamp) {
                        alert('Please select a child');
                        return;
                      }

                      if (campEnrollmentType === 'weekly' && selectedWeeks.length === 0) {
                        alert('Please select at least one week');
                        return;
                      }

                      if (campEnrollmentType === 'drop_in' && selectedDays.length === 0) {
                        alert('Please select at least one day');
                        return;
                      }

                      // Prepare enrollment data
                      const enrollmentData = {
                        student_id: selectedChildForCamp.id,
                        camp_id: selectedCamp.id,
                        enrollment_type: campEnrollmentType,
                        selected_weeks: campEnrollmentType === 'weekly' ? selectedWeeks : null,
                        days_attending: campEnrollmentType === 'drop_in' ? selectedDays : null,
                        needs_early_dropoff: needsEarlyDropoff,
                        needs_late_pickup: needsLatePickup,
                        early_dropoff_days: earlyDropoffDays.length > 0 ? earlyDropoffDays : null,
                        late_pickup_days: latePickupDays.length > 0 ? latePickupDays : null,
                        discount_code: discountCode.trim() && discountValidation?.valid ? discountCode.trim() : null
                      };

                      setIsCampEnrollmentSubmitting(true);
                      try {
                        const token = localStorage.getItem('cognitoToken') || '';
                        const response = await fetch(`${config.BACKEND_URL}/api/camps/enroll`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            ...(token && { 'Authorization': `Bearer ${token}` })
                          },
                          body: JSON.stringify(enrollmentData)
                        });

                        if (response.ok) {
                          const result = await response.json();
                          console.log('🎯 Enrollment response:', result);
                          console.log('🎯 Invoice in response?', result.invoice);
                          console.log('🎯 Enrollment data:', result.enrollment);
                          
                          // Check if invoice was created
                          if (result.invoice) {
                            console.log('✅ Opening payment modal with invoice:', result.invoice);
                            console.log('   Enrollment ID:', result.enrollment.id);
                            // Show payment modal
                            setCampInvoiceData(result.invoice);
                            console.log('   ✅ Set campInvoiceData');
                            setCampEnrollmentId(result.enrollment.id);
                            console.log('   ✅ Set campEnrollmentId');
                            setShowCampEnrollment(false);
                            console.log('   ✅ Closed enrollment modal');
                            setShowCampPayment(true);
                            console.log('   ✅ Set showCampPayment to TRUE');
                            // Note: fetchDashboardData will be called after payment completes
                          } else {
                            console.log('⚠️ No invoice in response, showing success message');
                            // No invoice (shouldn't happen, but handle gracefully)
                            alert(`✅ Enrollment successful!\n\n${selectedChildForCamp.firstName} has been enrolled in ${selectedCamp.name}.\n\nYou will receive a confirmation email shortly with payment details.`);
                            setShowCampEnrollment(false);
                            setSelectedCamp(null);
                            setSelectedChildForCamp(null);
                            fetchCamps(); // Refresh camps to update availability
                            fetchDashboardData(); // Refresh children data to show camp enrollment
                          }
                        } else {
                          const error = await response.json();
                          alert(`❌ Enrollment failed: ${error.detail || 'Please try again'}`);
                        }
                      } catch (error) {
                        console.error('Enrollment error:', error);
                        alert('❌ An error occurred during enrollment. Please try again or contact support.');
                      } finally {
                        setIsCampEnrollmentSubmitting(false);
                      }
                    }}
                    disabled={
                      isCampEnrollmentSubmitting ||
                      !selectedChildForCamp ||
                      (campEnrollmentType === 'weekly' && selectedWeeks.length === 0) ||
                      (campEnrollmentType === 'drop_in' && selectedDays.length === 0)
                    }
                    style={{ 
                      padding: '10px 30px',
                      fontWeight: 'bold'
                    }}
                  >
                    {isCampEnrollmentSubmitting ? 'Submitting...' : 'Complete Enrollment'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Attendance Tab */}
      {activeTab === 'attendance' && (
        <div className="dashboard-content">
          {/* Mobile Filter - Horizontal Scrollable Pills (Primary Focus - Outside white container) */}
          <div className="attendance-pills-container mobile-only">
            <div className="attendance-pills-header">
              <div className="attendance-pills-label">Showing records for:</div>
            </div>
            <div className="attendance-pills-scroll">
              <button
                className={`attendance-pill ${!selectedChildForAttendance ? 'active' : ''}`}
                onClick={() => {
                  setSelectedChildForAttendance(null);
                  fetchAttendanceRecords(undefined, attendanceStartDate || undefined, attendanceEndDate || undefined);
                }}
              >
                All Students
              </button>
              {children.map((child) => (
                <button
                  key={child.id}
                  className={`attendance-pill ${selectedChildForAttendance === child.id ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedChildForAttendance(child.id);
                    fetchAttendanceRecords(child.id, attendanceStartDate || undefined, attendanceEndDate || undefined);
                  }}
                >
                  {child.firstName}
                </button>
              ))}
            </div>
          </div>

          {/* Attendance Filters */}
          <div className="attendance-filters" style={{ display: 'flex', gap: '15px', alignItems: 'flex-end', flexWrap: 'wrap', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', marginBottom: '15px' }}>
            <div className="filter-group" style={{ minWidth: '180px' }}>
              <label htmlFor="student-filter" style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333', fontSize: '14px' }}>Student:</label>
              <div className="searchable-select-container" style={{ position: 'relative' }}>
                <input
                  type="text"
                  id="student-filter"
                  className="searchable-select-input"
                  placeholder="All Students"
                  value={(() => {
                    if (selectedChildForAttendance) {
                      const selectedChild = attendanceChildren.find(c => c.id === selectedChildForAttendance);
                      if (selectedChild) {
                        // Handle different API response formats
                        const childName = (selectedChild as any).name || 
                                         ((selectedChild as any).first_name && (selectedChild as any).last_name ? `${(selectedChild as any).first_name} ${(selectedChild as any).last_name}` : '') ||
                                         (selectedChild.firstName && selectedChild.lastName ? `${selectedChild.firstName} ${selectedChild.lastName}` : '') ||
                                         '';
                        return childName.trim() || 'Unknown Student';
                      }
                    }
                    return 'All Students';
                  })()}
                  readOnly
                  onClick={() => setShowStudentDropdown(!showStudentDropdown)}
                  style={{ cursor: 'pointer' }}
                />
                {showStudentDropdown && (
                  <>
                    <div 
                      className="searchable-select-overlay"
                      onClick={() => setShowStudentDropdown(false)}
                    />
                    <div className="searchable-select-dropdown">
                      <div
                        className={`searchable-select-option ${selectedChildForAttendance === null ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedChildForAttendance(null);
                          setShowStudentDropdown(false);
                          fetchAttendanceRecords(undefined, attendanceStartDate || undefined, attendanceEndDate || undefined);
                        }}
                      >
                        <strong>All Students</strong>
                      </div>
                      {attendanceChildren.map((child) => {
                        // Handle different API response formats
                        // Backend returns: { id, name, tier } from attendance API
                        // Main children array has: { id, firstName, lastName, ... }
                        const childName = (child as any).name || 
                                         ((child as any).first_name && (child as any).last_name ? `${(child as any).first_name} ${(child as any).last_name}` : '') ||
                                         (child.firstName && child.lastName ? `${child.firstName} ${child.lastName}` : '') ||
                                         '';
                        return (
                          <div
                            key={child.id}
                            className={`searchable-select-option ${selectedChildForAttendance === child.id ? 'selected' : ''}`}
                            onClick={() => {
                              setSelectedChildForAttendance(child.id);
                              setShowStudentDropdown(false);
                              fetchAttendanceRecords(child.id, attendanceStartDate || undefined, attendanceEndDate || undefined);
                            }}
                          >
                            <strong>{childName.trim() || 'Unknown Student'}</strong>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </div>
            
            <div className="filter-group" style={{ minWidth: '150px' }}>
              <label htmlFor="start-date" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#333' }}>Start Date:</label>
              <input
                type="date"
                id="start-date"
                className="form-input"
                value={attendanceStartDate}
                onChange={(e) => {
                  setAttendanceStartDate(e.target.value);
                  fetchAttendanceRecords(selectedChildForAttendance || undefined, e.target.value || undefined, attendanceEndDate || undefined);
                }}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #dee2e6', borderRadius: '6px', fontSize: '1rem' }}
              />
            </div>
            
            <div className="filter-group" style={{ minWidth: '150px' }}>
              <label htmlFor="end-date" style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600', color: '#333' }}>End Date:</label>
              <input
                type="date"
                id="end-date"
                className="form-input"
                value={attendanceEndDate}
                onChange={(e) => {
                  setAttendanceEndDate(e.target.value);
                  fetchAttendanceRecords(selectedChildForAttendance || undefined, attendanceStartDate || undefined, e.target.value || undefined);
                }}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #dee2e6', borderRadius: '6px', fontSize: '1rem' }}
              />
            </div>
            
            <div className="filter-group">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setAttendanceStartDate('');
                  setAttendanceEndDate('');
                  setSelectedChildForAttendance(null);
                  fetchAttendanceRecords();
                }}
                style={{ padding: '10px 20px', fontSize: '14px', whiteSpace: 'nowrap' }}
              >
                Clear Filters
              </button>
            </div>
          </div>

          <div className="attendance-table-container">
            {attendanceRecords.length === 0 ? (
              <div className="no-attendance">
                <p>No attendance records found.</p>
                {selectedChildForAttendance ? (
                  <p>Try selecting a different student or check back later.</p>
                ) : (
                  <p>Attendance records will appear here once your children attend sessions.</p>
                )}
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="attendance-table-wrapper desktop-only">
                  <table className="attendance-table">
                    <thead>
                      <tr>
                        <th>Student</th>
                        <th>Date</th>
                        <th>Time</th>
                        <th>Program</th>
                        <th>Status</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendanceRecords.map((record) => {
                        // Parse date string directly to avoid timezone issues
                        // Format: "YYYY-MM-DD" -> "MM/DD/YYYY"
                        const dateParts = record.date.split('-');
                        const formattedDate = dateParts.length === 3 
                          ? `${dateParts[1]}/${dateParts[2]}/${dateParts[0]}`
                          : record.date;
                        
                        return (
                          <tr key={record.id}>
                            <td>{record.student_name}</td>
                            <td>{formattedDate}</td>
                            <td>{record.time ? formatTimePST(record.time, record.date) : '-'}</td>
                            <td>{record.program_name}</td>
                            <td>
                              <span className={`status-badge ${record.status}`}>
                                {record.status}
                              </span>
                            </td>
                            <td>{record.notes || '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Condensed List View */}
                <div className="attendance-list-container mobile-only">
                  {attendanceRecords.map((record) => {
                    const isExpanded = expandedAttendanceRecords.has(record.id);
                    // Parse date string directly to avoid timezone issues
                    // Format: "YYYY-MM-DD"
                    const dateParts = record.date.split('-');
                    const day = dateParts.length === 3 ? parseInt(dateParts[2], 10) : 0;
                    const dateObj = dateParts.length === 3 
                      ? new Date(parseInt(dateParts[0], 10), parseInt(dateParts[1], 10) - 1, parseInt(dateParts[2], 10))
                      : new Date(record.date);
                    const month = dateObj.toLocaleDateString('en-US', { month: 'short' });
                    
                    const toggleExpand = () => {
                      const newExpanded = new Set(expandedAttendanceRecords);
                      if (isExpanded) {
                        newExpanded.delete(record.id);
                      } else {
                        newExpanded.add(record.id);
                      }
                      setExpandedAttendanceRecords(newExpanded);
                    };
                    
                    return (
                      <div 
                        key={record.id} 
                        className={`attendance-list-row ${isExpanded ? 'expanded' : ''}`}
                        onClick={toggleExpand}
                      >
                        <div className="attendance-row-content">
                          {/* Left: Date */}
                          <div className="attendance-date-column">
                            <div className="attendance-day">{day}</div>
                            <div className="attendance-month">{month}</div>
                          </div>
                          
                          {/* Middle: Student Name + Program */}
                          <div className="attendance-info-column">
                            <div className="attendance-student-name">{record.student_name}</div>
                            <div className="attendance-program-name">{record.program_name}</div>
                          </div>
                          
                          {/* Right: Status Badge */}
                          <div className="attendance-status-column">
                            <span className={`status-badge ${record.status.toLowerCase()}`}>
                              {record.status}
                            </span>
                          </div>
                        </div>
                        
                        {/* Expanded Notes Section */}
                        {isExpanded && record.notes && (
                          <div className="attendance-notes-section">
                            <div className="attendance-notes-label">Notes:</div>
                            <div className="attendance-notes-content">{record.notes}</div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Progress Tab */}
      {activeTab === 'progress' && (
        <div className="dashboard-content">
          <h3>Progress Reports</h3>
          <div className="progress-reports">
            {progressReports.map((report) => (
              <div key={report.id} className="progress-card">
                <div className="progress-header">
                  <h4>{report.childName} - {report.programName}</h4>
                  <span className={`rating-badge ${report.overallRating}`}>
                    {report.overallRating}
                  </span>
                </div>
                <div className="progress-content">
                  <div className="skills-section">
                    <h5>Skills Demonstrated:</h5>
                    <ul>
                      {report.skills.map((skill, index) => (
                        <li key={index}>{skill}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="achievements-section">
                    <h5>Achievements:</h5>
                    <ul>
                      {report.achievements.map((achievement, index) => (
                        <li key={index}>{achievement}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="teacher-notes">
                    <h5>Teacher Notes:</h5>
                    <p>{report.teacherNotes}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Messages Tab */}
      {activeTab === 'messages' && (
        <div className="dashboard-content">
          <div className="messages-header">
            <h3>Communication Hub</h3>
            <button 
              className="btn btn-primary"
              onClick={() => setShowMessageForm(true)}
            >
              ✉️ New Message
            </button>
          </div>

          <div className="messages-list">
            {messages.map((message) => (
              <div 
                key={message.id} 
                className={`message-item ${!message.read ? 'unread' : ''}`}
                onClick={() => markMessageAsRead(message.id)}
              >
                <div className="message-header">
                  <div className="message-from">
                    <strong>{message.from}</strong>
                    <span className={`priority-badge ${message.priority}`}>
                      {message.priority}
                    </span>
                  </div>
                  <div className="message-date">{message.date}</div>
                </div>
                <div className="message-subject">{message.subject}</div>
                <div className="message-content">{message.message}</div>
                {!message.read && <div className="unread-indicator">●</div>}
              </div>
            ))}
          </div>
        </div>
      )}


      {/* Billing Tab */}
      {activeTab === 'billing' && (
        <div className="parent-dashboard-section">
          <ParentBillingDashboard />
        </div>
      )}

      {/* Legacy Billing Tab - Removed, using ParentBillingDashboard component */}

      {/* Message Form Modal */}
      {showMessageForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Send Message</h3>
              <button 
                className="close-btn"
                onClick={() => setShowMessageForm(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-content">
              <div className="message-form">
                <div className="form-group">
                  <label>To:</label>
                  <select 
                    value={newMessage.to}
                    onChange={(e) => setNewMessage({...newMessage, to: e.target.value})}
                    className="form-select"
                  >
                    <option value="">Select recipient</option>
                    <option value="teacher1@example.com">Sarah Johnson (Teacher)</option>
                    <option value="teacher2@example.com">Michael Smith (Teacher)</option>
                    <option value="admin@example.com">Administration</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Subject:</label>
                  <input 
                    type="text"
                    value={newMessage.subject}
                    onChange={(e) => setNewMessage({...newMessage, subject: e.target.value})}
                    className="form-input"
                    placeholder="Enter message subject"
                  />
                </div>
                <div className="form-group">
                  <label>Message:</label>
                  <textarea 
                    value={newMessage.message}
                    onChange={(e) => setNewMessage({...newMessage, message: e.target.value})}
                    className="form-textarea"
                    rows={5}
                    placeholder="Enter your message"
                  />
                </div>
                <div className="form-actions">
                  <button 
                    className="btn btn-primary"
                    onClick={handleSendMessage}
                    disabled={!newMessage.to || !newMessage.subject || !newMessage.message}
                  >
                    Send Message
                  </button>
                  <button 
                    className="btn btn-secondary"
                    onClick={() => setShowMessageForm(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* E-Transfer Payment Modal */}
      {showPaymentModal && purchaseCalculation && (
        <div className="modal-overlay">
          <div className="modal payment-modal">
            <div className="modal-header">
              <h3>💳 E-Transfer Payment Instructions</h3>
              <button 
                className="close-btn"
                onClick={() => {
                  setShowPaymentModal(false);
                  setPurchaseCalculation(null);
                  setSelectedChildForPurchase(null);
                  setSelectedPackageType(null);
                }}
              >
                ✕
              </button>
            </div>
            <div className="modal-content">
              <div className="payment-instructions">
                <div className="payment-summary">
                  <h4>📦 Purchase Summary</h4>
                  <div className="summary-details">
                    <p><strong>Student:</strong> {purchaseCalculation.student.name}</p>
                    <p><strong>Package:</strong> {purchaseCalculation.package.name}</p>
                    <p><strong>Sessions:</strong> {purchaseCalculation.package.session_count}</p>
                    <p><strong>Tier:</strong> {getTierEmoji(purchaseCalculation.student.tier)} {getTierName(purchaseCalculation.student.tier)}</p>
                  </div>
                </div>

                <div className="pricing-breakdown">
                  <h4>💰 Pricing Breakdown</h4>
                  <div className="pricing-details">
                    <div className="price-line">
                      <span>Base Price:</span>
                      <span>${purchaseCalculation.pricing.base_price.toFixed(2)}</span>
                    </div>
                    {purchaseCalculation.pricing.family_discount_amount > 0 && (
                      <div className="price-line discount">
                        <span>Family Discount ({purchaseCalculation.pricing.family_discount_percentage.toFixed(0)}%):</span>
                        <span>-${purchaseCalculation.pricing.family_discount_amount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="price-line total">
                      <span><strong>Total Amount:</strong></span>
                      <span><strong>${purchaseCalculation.pricing.final_price.toFixed(2)}</strong></span>
                    </div>
                  </div>
                </div>

                <div className="etransfer-instructions">
                  <h4>🏦 E-Transfer Instructions</h4>
                  <div className="instructions-content">
                    <div className="instruction-step">
                      <div className="step-number">1</div>
                      <div className="step-content">
                        <p><strong>Send E-Transfer to:</strong></p>
                        <div className="email-address">
                          <code>oarayouthinnovationlab@gmail.com</code>
                          <button 
                            className="copy-btn"
                            onClick={() => navigator.clipboard.writeText('oarayouthinnovationlab@gmail.com')}
                          >
                            📋 Copy
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="instruction-step">
                      <div className="step-number">2</div>
                      <div className="step-content">
                        <p><strong>Amount to Transfer:</strong></p>
                        <div className="amount-display">
                          <span className="amount">${purchaseCalculation.pricing.final_price.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>

                    <div className="instruction-step">
                      <div className="step-number">3</div>
                      <div className="step-content">
                        <p><strong>Include in Message/Comment:</strong></p>
                        <div className="message-template">
                          <textarea 
                            readOnly
                            value={`Student: ${purchaseCalculation.student.name}
Package: ${purchaseCalculation.package.name} (${purchaseCalculation.package.session_count} sessions)
Tier: ${getTierName(purchaseCalculation.student.tier)}
Amount: $${purchaseCalculation.pricing.final_price.toFixed(2)}
Parent: ${user?.email}`}
                            className="message-textarea"
                          />
                          <button 
                            className="copy-btn"
                            onClick={() => navigator.clipboard.writeText(`Student: ${purchaseCalculation.student.name}
Package: ${purchaseCalculation.package.name} (${purchaseCalculation.package.session_count} sessions)
Tier: ${getTierName(purchaseCalculation.student.tier)}
Amount: $${purchaseCalculation.pricing.final_price.toFixed(2)}
Parent: ${user?.email}`)}
                          >
                            📋 Copy Message
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="school-days-info">
                  <h4>📚 School Days Policy</h4>
                  <div className="school-days-content">
                    <p><strong>Session Usage:</strong> This package is valid only during regular school days:</p>
                    <ul>
                      <li>Monday through Friday</li>
                      <li>Excludes weekends (Saturday & Sunday)</li>
                      <li>Excludes summer break (July & August)</li>
                      <li>Excludes major holidays (New Year's, Canada Day, Christmas, Boxing Day)</li>
                    </ul>
                    <p><em>This ensures sessions align with the academic calendar and provide consistent learning opportunities.</em></p>
                  </div>
                </div>

                <div className="payment-note">
                  <div className="note-content">
                    <h5>📝 Important Notes:</h5>
                    <ul>
                      <li>Please include all the information above in your e-transfer message</li>
                      <li>Your package will be activated within 24 hours of payment confirmation</li>
                      <li>You will receive an email confirmation once payment is processed</li>
                      <li><strong>Automated payment processing will be implemented in the future</strong></li>
                    </ul>
                  </div>
                </div>

                <div className="modal-actions">
                  <button 
                    className="btn btn-primary"
                    onClick={() => {
                      setShowPaymentModal(false);
                      setPurchaseCalculation(null);
                      setSelectedChildForPurchase(null);
                      setSelectedPackageType(null);
                    }}
                  >
                    ✅ I Understand
                  </button>
                  <button 
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowPaymentModal(false);
                      setPurchaseCalculation(null);
                      setSelectedChildForPurchase(null);
                      setSelectedPackageType(null);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stripe Payment Modal */}
      {console.log('🔍 ParentDashboard rendering StripePayment? Condition: stripePaymentData:', stripePaymentData !== null, '&& showStripePayment:', showStripePayment, 'paymentSuccess:', paymentSuccess)}
      {stripePaymentData && (
        <StripePayment
          amount={stripePaymentData.amount}
          description={stripePaymentData.description}
          metadata={stripePaymentData.metadata}
          onSuccess={handleStripePaymentSuccess}
          onError={handleStripePaymentError}
          onCancel={handleStripePaymentCancel}
          onBackToDashboard={handleStripePaymentBackToDashboard}
          show={showStripePayment}
          paymentSuccess={paymentSuccess}
        />
      )}

      {/* Student Enrollment Form Modal */}
      {showStudentForm && (
        <div className="modal-overlay">
          <div className="modal-content student-enrollment-modal">
            <div className="modal-header">
              <h3>
                {registrationStep === 'student-info' && '👶 Enroll Your Child'}
                {registrationStep === 'consent-forms' && '📝 Program Consent & Registration Form'}
                {registrationStep === 'complete' && '✅ Registration Complete'}
              </h3>
              {registrationStep !== 'consent-forms' && (
                <button 
                  className="close-btn"
                  onClick={() => {
                    setShowStudentForm(false);
                    setRegistrationStep('student-info');
                    setNewlyRegisteredStudent(null);
                    setNewlyCreatedConsentForms([]);
                    setSigningConsentFormIndex(0);
                  }}
                >
                  ✕
                </button>
              )}
            </div>
            
            {/* Student Registration Step - OLD FORM (kept for backward compatibility, but skipped in normal flow) */}
            {registrationStep === 'student-info' && (
            <form onSubmit={handleStudentFormSubmit} className="student-form">
              <div className="form-section">
                <h4>Basic Information</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input
                      type="text"
                      value={studentFormData.first_name}
                      onChange={(e) => handleStudentFormChange('first_name', e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name *</label>
                    <input
                      type="text"
                      value={studentFormData.last_name}
                      onChange={(e) => handleStudentFormChange('last_name', e.target.value)}
                      required
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Date of Birth *</label>
                    <input
                      type="date"
                      value={studentFormData.date_of_birth}
                      onChange={(e) => handleStudentFormChange('date_of_birth', e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Grade <span className="required">*</span></label>
                    <select
                      value={studentFormData.grade}
                      onChange={(e) => handleStudentFormChange('grade', e.target.value)}
                      required
                    >
                      <option value="">Select Grade</option>
                      <option value="K">Kindergarten</option>
                      <option value="1">Grade 1</option>
                      <option value="2">Grade 2</option>
                      <option value="3">Grade 3</option>
                      <option value="4">Grade 4</option>
                      <option value="5">Grade 5</option>
                      <option value="6">Grade 6</option>
                      <option value="7">Grade 7</option>
                      <option value="8">Grade 8</option>
                      <option value="9">Grade 9</option>
                      <option value="10">Grade 10</option>
                      <option value="11">Grade 11</option>
                      <option value="12">Grade 12</option>
                    </select>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>School <span className="required">*</span></label>
                  <input
                    type="text"
                    value={studentFormData.school}
                    onChange={(e) => handleStudentFormChange('school', e.target.value)}
                    placeholder="School name"
                    required
                  />
                </div>
              </div>

              <div className="form-section">
                <h4>Emergency Contact</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Emergency Contact Name <span className="required">*</span></label>
                    <input
                      type="text"
                      value={studentFormData.emergency_contact}
                      onChange={(e) => handleStudentFormChange('emergency_contact', e.target.value)}
                      placeholder="Full name"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Emergency Contact Phone <span className="required">*</span></label>
                    <input
                      type="tel"
                      value={studentFormData.emergency_phone}
                      onChange={(e) => handleStudentFormChange('emergency_phone', e.target.value)}
                      placeholder="(555) 123-4567"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h4>Medical Information</h4>
                <div className="form-group">
                  <label>Medical Conditions</label>
                  <textarea
                    value={studentFormData.medical_conditions}
                    onChange={(e) => handleStudentFormChange('medical_conditions', e.target.value)}
                    placeholder="Any medical conditions we should be aware of"
                    rows={3}
                  />
                </div>
                <div className="form-group">
                  <label>Allergies</label>
                  <textarea
                    value={studentFormData.allergies}
                    onChange={(e) => handleStudentFormChange('allergies', e.target.value)}
                    placeholder="Any allergies (food, environmental, etc.)"
                    rows={3}
                  />
                </div>
              </div>

              <div className="form-section">
                <h4>Program Level</h4>
                <div className="form-group">
                  <label>Recommended Tier</label>
                  <div className="tier-options">
                    <label className="tier-option">
                      <input
                        type="radio"
                        name="tier"
                        value="foundations"
                        checked={studentFormData.tier === 'foundations'}
                        onChange={(e) => handleStudentFormChange('tier', e.target.value)}
                      />
                      <div className="tier-card">
                        <h5>🏗️ Foundations</h5>
                        <p>Ages 6-9 • Basic robotics, programming, and STEM concepts</p>
                      </div>
                    </label>
                    <label className="tier-option">
                      <input
                        type="radio"
                        name="tier"
                        value="innovators"
                        checked={studentFormData.tier === 'innovators'}
                        onChange={(e) => handleStudentFormChange('tier', e.target.value)}
                      />
                      <div className="tier-card">
                        <h5>🚀 Innovators</h5>
                        <p>Ages 10-13 • Advanced programming, robotics, and engineering</p>
                      </div>
                    </label>
                    <label className="tier-option">
                      <input
                        type="radio"
                        name="tier"
                        value="pioneers"
                        checked={studentFormData.tier === 'pioneers'}
                        onChange={(e) => handleStudentFormChange('tier', e.target.value)}
                      />
                      <div className="tier-card">
                        <h5>🌟 Pioneers</h5>
                        <p>Ages 14+ • Advanced AI, machine learning, and research projects</p>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h4>Student Login Account (Optional)</h4>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={false}
                      disabled={true}
                      readOnly
                    />
                    <span>Allow student to login and access their account</span>
                  </label>
                  <small className="form-hint" style={{ color: '#d32f2f', fontStyle: 'italic' }}>
                    ⚠️ This option is currently disabled by admin.
                  </small>
                </div>

                {false && (
                  <div className="login-options">
                    <div className="form-group">
                      <label>Login Email (Optional)</label>
                      <input
                        type="email"
                        value={studentFormData.login_email}
                        onChange={(e) => handleStudentFormChange('login_email', e.target.value)}
                        placeholder="Leave empty to auto-generate from student name"
                      />
                      <small className="form-hint">
                        If not provided, an email will be auto-generated based on the student's name and your email domain.
                      </small>
                    </div>
                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={studentFormData.force_password_change}
                          onChange={(e) => handleStudentFormChange('force_password_change', e.target.checked)}
                        />
                        <span>Require password change on first login</span>
                      </label>
                      <small className="form-hint">
                        A temporary password will be generated. The student will need to change it on their first login.
                      </small>
                    </div>
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => setShowStudentForm(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={studentFormLoading}
                >
                  {studentFormLoading ? '⏳ Enrolling...' : '✅ Enroll Child'}
                </button>
              </div>
            </form>
            )}
            
            {/* Consent Forms Step - Unified Multi-Page Form */}
            {registrationStep === 'consent-forms' && (
              <div className="consent-forms-step">
                <UnifiedConsentFormModal
                  studentInfo={{
                    firstName: newlyRegisteredStudent?.firstName || '',
                    lastName: newlyRegisteredStudent?.lastName || '',
                    dateOfBirth: newlyRegisteredStudent?.dateOfBirth || '',
                    grade: studentFormData.grade || ''
                  }}
                  parentInfo={{
                    firstName: user?.attributes?.first_name || user?.attributes?.firstName || 'Parent',
                    lastName: user?.attributes?.last_name || user?.attributes?.lastName || 'Name',
                    email: user?.email || 'parent@example.com',
                    phone: user?.attributes?.phone || user?.attributes?.phone_number || '+1-555-0123',
                    address: ''
                  }}
                  isOpen={true}
                  isRegistrationMode={true}
                  onClose={() => {
                    // This should never be called in registration mode
                    console.log('⚠️ onClose called during registration - ignoring');
                  }}
                  onSign={async (formData, signatureData) => {
                    try {
                      console.log('🔍 Processing unified consent form submission...');
                      
                      // If student hasn't been created yet, create it first
                      if (!newlyRegisteredStudent) {
                        console.log('🔍 Creating student from consent form data...');
                        
                        if (!user?.email) {
                          throw new Error('User email not available');
                        }
                        
                        // Get parent ID from dashboard data (same as old form)
                        const dashboardResponse = await fetch(`${config.BACKEND_URL}/api/parent-dashboard/${user.email}`);
                        if (!dashboardResponse.ok) {
                          throw new Error('Failed to get parent information');
                        }
                        const dashboardData = await dashboardResponse.json();
                        const parentId = dashboardData.parent_id;
                        
                        if (!parentId) {
                          throw new Error('Parent ID not found');
                        }
                        
                        // Extract student info from form data
                        const medicalConditions = formData.medical?.medicalConditions || 'None';
                        const allergies = formData.medical?.allergies || 'None';
                        const emergencyContactName = formData.emergencyContact?.fullName || 'Not provided';
                        const emergencyContactPhone = formData.emergencyContact?.phone || 'Not provided';
                        
                        // Validate and format phone number - must match pattern ^\+[1-9]\d{10,14}$ or be null
                        let phoneValue = formData.student.phone || null;
                        if (phoneValue && phoneValue.trim() !== '') {
                          // If phone doesn't start with +, add it (assuming North American number)
                          if (!phoneValue.startsWith('+')) {
                            // Remove any non-digit characters except +
                            const digitsOnly = phoneValue.replace(/\D/g, '');
                            if (digitsOnly.length >= 10) {
                              phoneValue = `+1${digitsOnly}`; // Default to +1 for North America
                            } else {
                              phoneValue = null; // Invalid phone, set to null
                            }
                          }
                        } else {
                          phoneValue = null; // Empty string becomes null
                        }
                        
                        const studentData = {
                          first_name: formData.student.firstName,
                          last_name: formData.student.lastName,
                          date_of_birth: formData.student.dateOfBirth,
                          grade_level: formData.student.grade || 'K',
                          email: formData.student.email || null,
                          phone: phoneValue,
                          parent_id: parentId,
                          // Note: tier is calculated from grade_level on backend, don't send it
                          profile_notes: `Medical: ${medicalConditions}\nAllergies: ${allergies}\nEmergency Contact: ${emergencyContactName}\nEmergency Phone: ${emergencyContactPhone}\nSchool: ${studentFormData.school || 'Not provided'}`,
                          learning_goals: `Enrolled in ${studentFormData.tier || 'foundations'} tier program`,
                          create_login: studentFormData.create_login || false,
                          login_email: studentFormData.login_email || null,
                          force_password_change: studentFormData.force_password_change || false
                        };
                        
                        // Create student
                        console.log('📤 Sending student data:', JSON.stringify(studentData, null, 2));
                        const response = await axios.post(
                          `${config.BACKEND_URL}/api/students`,
                          studentData,
                          {
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${localStorage.getItem('token') || localStorage.getItem('cognitoToken') || ''}`
                            },
                            withCredentials: true
                          }
                        ).catch((error) => {
                          console.error('❌ Error creating student:', error);
                          if (error.response) {
                            console.error('❌ Response status:', error.response.status);
                            console.error('❌ Response data:', error.response.data);
                            throw new Error(error.response.data?.detail || `Failed to create student: ${error.response.status}`);
                          }
                          throw error;
                        });
                        
                        if (response.data && response.data.id) {
                          const studentId = response.data.id;
                          console.log('✅ [REGISTRATION] Student created:', studentId);
                          
                          // Set newly registered student
                          setNewlyRegisteredStudent({
                            id: studentId,
                            firstName: formData.student.firstName,
                            lastName: formData.student.lastName,
                            dateOfBirth: formData.student.dateOfBirth
                          });
                          
                          // Wait a moment for database to commit the student and consent forms
                          console.log('⏳ [REGISTRATION] Waiting for database to commit student and consent forms...');
                          await new Promise(resolve => setTimeout(resolve, 1000)); // Increased to 1 second
                          
                          // Fetch consent forms that were auto-created (with retry logic)
                          let formsToSign: ConsentForm[] = [];
                          let retries = 0;
                          const maxRetries = 5; // Increased retries
                          
                          console.log(`🔍 [REGISTRATION] Starting consent form fetch with retry logic...`);
                          console.log(`🔍 [REGISTRATION] Student ID: ${studentId}`);
                          
                          while (retries < maxRetries && formsToSign.length === 0) {
                            console.log(`🔍 [REGISTRATION] Fetching consent forms (attempt ${retries + 1}/${maxRetries})...`);
                            const consentFormsResponse = await axios.get(
                              `${config.BACKEND_URL}/api/consent-forms/parent-consent-forms/${user.email}`,
                              {
                                headers: {
                                  'Cache-Control': 'no-cache',
                                  'Pragma': 'no-cache'
                                },
                                params: {
                                  _t: Date.now()
                                }
                              }
                            );
                            
                            console.log(`🔍 [REGISTRATION] Response received, checking data...`);
                            
                            if (consentFormsResponse.data && consentFormsResponse.data.consent_forms) {
                              const allForms = consentFormsResponse.data.consent_forms;
                              console.log(`🔍 [REGISTRATION] Total forms in response: ${allForms.length}`);
                              console.log(`🔍 [REGISTRATION] All form details:`, allForms.map((f: ConsentForm) => ({ 
                                id: f.id, 
                                student_id: f.student_id, 
                                status: f.status,
                                student_name: f.student_name
                              })));
                              
                              const studentForms = allForms.filter((form: ConsentForm) => {
                                const matches = form.student_id === studentId && form.status === 'PENDING';
                                console.log(`🔍 [REGISTRATION] Form ${form.id}: student_id=${form.student_id} (match: ${form.student_id === studentId}), status=${form.status} (pending: ${form.status === 'PENDING'}), matches=${matches}`);
                                return matches;
                              });
                              
                              console.log(`🔍 [REGISTRATION] Filtered student forms: ${studentForms.length}`);
                              console.log(`🔍 [REGISTRATION] Looking for student_id: ${studentId}`);
                              
                              if (studentForms.length > 0) {
                                // Create a deep copy to ensure it's not lost
                                formsToSign = JSON.parse(JSON.stringify(studentForms));
                                setNewlyCreatedConsentForms(JSON.parse(JSON.stringify(studentForms)));
                                console.log(`🔍 [REGISTRATION] Setting formsToSign to ${studentForms.length} forms`);
                                console.log(`✅ [REGISTRATION] Found consent forms to sign: ${studentForms.length}`);
                                console.log(`🔍 [REGISTRATION] Form IDs to sign:`, studentForms.map((f: ConsentForm) => f.id));
                                console.log(`🔍 [REGISTRATION] formsToSign.length after assignment: ${formsToSign.length}`);
                                console.log(`🔍 [REGISTRATION] formsToSign contents:`, formsToSign.map(f => ({ id: f.id, status: f.status, student_id: f.student_id })));
                                break; // Exit retry loop
                              } else {
                                console.log(`⚠️ [REGISTRATION] No forms found for student ${studentId}, retrying...`);
                                retries++;
                                if (retries < maxRetries) {
                                  await new Promise(resolve => setTimeout(resolve, 1000)); // Increased delay
                                }
                              }
                            } else {
                              console.log('⚠️ [REGISTRATION] No consent forms in response, retrying...');
                              retries++;
                              if (retries < maxRetries) {
                                await new Promise(resolve => setTimeout(resolve, 1000));
                              }
                            }
                          }
                          
                          console.log(`🔍 [REGISTRATION] After retry loop - formsToSign.length: ${formsToSign.length}`);
                          console.log(`🔍 [REGISTRATION] formsToSign contents:`, formsToSign.map(f => ({ id: f.id, status: f.status, student_id: f.student_id })));
                          
                          // Now sign all consent forms immediately (don't wait for state update)
                          if (formsToSign.length > 0) {
                            console.log('🔍 [REGISTRATION] Signing consent forms...');
                            console.log(`🔍 [REGISTRATION] Forms to sign: ${formsToSign.length}`, formsToSign.map((f: ConsentForm) => ({ id: f.id, status: f.status })));
                            const formIds = formsToSign.map((f: ConsentForm) => f.id);
                            console.log(`🔍 [REGISTRATION] Form IDs array:`, formIds);
                            const results = [];
                            
                            for (const formId of formIds) {
                              console.log(`🔍 [REGISTRATION] Signing form ${formId}...`);
                              try {
                                const signResponse = await handleSignConsentForm(formId, signatureData, false);
                                if (signResponse && signResponse.success) {
                                  results.push({ formId, success: true });
                                  console.log(`✅ [REGISTRATION] Form ${formId} signed successfully`, signResponse);
                                } else {
                                  results.push({ formId, success: false, error: 'Response not successful' });
                                  console.error(`❌ [REGISTRATION] Failed to sign form ${formId}:`, signResponse);
                                }
                              } catch (signError: any) {
                                results.push({ formId, success: false, error: signError.message });
                                console.error(`❌ [REGISTRATION] Error signing form ${formId}:`, signError);
                              }
                              await new Promise(resolve => setTimeout(resolve, 500));
                            }
                            
                            const allSigned = results.every(r => r.success);
                            console.log(`🔍 [REGISTRATION] Signing results: ${results.filter(r => r.success).length}/${results.length} succeeded`);
                            
                            if (allSigned) {
                              // Wait a moment for database to commit
                              console.log('⏳ [REGISTRATION] Waiting for database to commit signed forms...');
                              await new Promise(resolve => setTimeout(resolve, 1000));
                              console.log('🔄 [REGISTRATION] Refreshing consent forms...');
                              await fetchConsentForms();
                              console.log('🔄 [REGISTRATION] Refreshing dashboard data...');
                              await fetchDashboardData();
                              console.log('✅ [REGISTRATION] Unified consent form signed, completing registration');
                              setRegistrationStep('complete');
                              
                              // Reset form
                              setStudentFormData({
                                first_name: '',
                                last_name: '',
                                date_of_birth: '',
                                grade: '',
                                school: '',
                                emergency_contact: '',
                                emergency_phone: '',
                                medical_conditions: '',
                                allergies: '',
                                tier: 'foundations',
                                create_login: false,
                                login_email: '',
                                force_password_change: false
                              });
                              
                              setEnrollmentSuccess(true);
                              return { success: true, message: 'Student registered and consent forms signed successfully' };
                            } else {
                              const failedForms = results.filter(r => !r.success);
                              console.error('❌ [REGISTRATION] Some forms failed to sign:', failedForms);
                              throw new Error(`Failed to sign ${failedForms.length} form(s). Please try signing manually.`);
                            }
                          } else {
                            // No forms to sign, just complete registration
                            console.log('⚠️ [REGISTRATION] No consent forms found to sign after retries');
                            setRegistrationStep('complete');
                            setEnrollmentSuccess(true);
                            return { success: true, message: 'Student registered successfully. Please sign consent forms manually.' };
                          }
                        } else {
                          throw new Error('Failed to create student');
                        }
                      }
                      
                      // If student already exists, sign existing forms
                      if (newlyRegisteredStudent && newlyCreatedConsentForms.length > 0) {
                        console.log('🔍 Signing consent forms for existing student...');
                        console.log(`🔍 Forms to sign: ${newlyCreatedConsentForms.length}`, newlyCreatedConsentForms.map(f => ({ id: f.id, status: f.status })));
                        const formIds = newlyCreatedConsentForms.map(f => f.id);
                        const results = [];
                        
                        for (const formId of formIds) {
                          console.log(`🔍 Signing form ${formId}...`);
                          const response = await handleSignConsentForm(formId, signatureData, false);
                          if (response && response.success) {
                            results.push({ formId, success: true });
                            console.log(`✅ Form ${formId} signed successfully`, response);
                          } else {
                            results.push({ formId, success: false });
                            console.error(`❌ Failed to sign form ${formId}`, response);
                          }
                          await new Promise(resolve => setTimeout(resolve, 200));
                        }
                        
                        const allSigned = results.every(r => r.success);
                        
                        if (allSigned) {
                          await fetchConsentForms();
                          console.log('✅ Unified consent form signed, completing registration');
                          setRegistrationStep('complete');
                          
                          // Reset form
                          setStudentFormData({
                            first_name: '',
                            last_name: '',
                            date_of_birth: '',
                            grade: '',
                            school: '',
                            emergency_contact: '',
                            emergency_phone: '',
                            medical_conditions: '',
                            allergies: '',
                            tier: 'foundations',
                            create_login: false,
                            login_email: '',
                            force_password_change: false
                          });
                          
                          await fetchDashboardData();
                          return { success: true, message: 'Consent form signed successfully' };
                        } else {
                          throw new Error('Some forms failed to sign');
                        }
                      }
                      
                      // If no forms to sign and student already exists, just complete
                      if (newlyRegisteredStudent) {
                        console.log('⚠️ No consent forms to sign for existing student');
                        setRegistrationStep('complete');
                        setEnrollmentSuccess(true);
                        return { success: true, message: 'Student information updated successfully' };
                      }
                    } catch (error: any) {
                      console.error('❌ Error processing unified consent form:', error);
                      if (error.response) {
                        console.error('❌ Response status:', error.response.status);
                        console.error('❌ Response data:', error.response.data);
                        const errorDetail = error.response.data?.detail || error.message || 'Unknown error';
                        // If it's a validation error, show the details
                        if (error.response.status === 422 && error.response.data?.detail) {
                          const detail = error.response.data.detail;
                          if (Array.isArray(detail)) {
                            const validationErrors = detail.map((err: any) => 
                              `${err.loc?.join('.')}: ${err.msg}`
                            ).join(', ');
                            throw new Error(`Validation error: ${validationErrors}`);
                          } else if (typeof detail === 'string') {
                            throw new Error(`Validation error: ${detail}`);
                          }
                        }
                        throw new Error(errorDetail);
                      }
                      throw error;
                    }
                  }}
                />
              </div>
            )}
            
            {/* Completion Step */}
            {registrationStep === 'complete' && (
              <div className="registration-complete">
                <div className="success-icon-large">🎉</div>
                <h3>Student Registration Complete!</h3>
                <p>
                  {newlyRegisteredStudent?.firstName} {newlyRegisteredStudent?.lastName} has been successfully registered.
                </p>
                <p style={{ color: '#666', fontSize: '14px', marginTop: '10px' }}>
                  <strong>Important:</strong> Consent forms have been created and are available in the "Consent Forms" tab. 
                  While you can enroll in classes and camps now, <strong>consent forms must be completed</strong> before your child's first session. 
                  You have time to complete them at your convenience.
                </p>
                
                <div className="next-steps">
                  <h4>What's Next?</h4>
                  <div className="next-step-options">
                    <div className="next-step-card">
                      <h5>🆓 Try a Free Trial Session</h5>
                      <p>Experience our program with a complimentary trial session</p>
                      <button 
                        className="btn btn-primary"
                        onClick={() => {
                          setShowStudentForm(false);
                          setEnrollmentSuccess(false);
                          setRegistrationStep('student-info');
                          setNewlyRegisteredStudent(null);
                          setNewlyCreatedConsentForms([]);
                          setSigningConsentFormIndex(0);
                          setActiveTab('programs');
                          fetchDashboardData();
                          fetchConsentForms();
                        }}
                      >
                        Book Trial Session
                      </button>
                    </div>
                    <div className="next-step-card">
                      <h5>💳 Purchase Session Package</h5>
                      <p>Get started with a session package for your child</p>
                      <button 
                        className="btn btn-secondary"
                        onClick={() => {
                          setShowStudentForm(false);
                          setEnrollmentSuccess(false);
                          setRegistrationStep('student-info');
                          setNewlyRegisteredStudent(null);
                          setNewlyCreatedConsentForms([]);
                          setSigningConsentFormIndex(0);
                          setActiveTab('packages');
                          fetchDashboardData();
                          fetchConsentForms();
                        }}
                      >
                        View Packages
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="registration-actions">
                  <button 
                    className="btn btn-primary"
                    onClick={() => {
                      setShowStudentForm(false);
                      setEnrollmentSuccess(false);
                      setRegistrationStep('student-info');
                      setNewlyRegisteredStudent(null);
                      setNewlyCreatedConsentForms([]);
                      setSigningConsentFormIndex(0);
                      // Refresh dashboard
                      fetchDashboardData();
                      fetchConsentForms();
                    }}
                  >
                    Continue to Dashboard
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Enrollment Success Modal - Only show if not in registration flow */}
      {enrollmentSuccess && registrationStep !== 'complete' && (
        <div className="modal-overlay">
          <div className="modal-content success-modal">
            <div className="success-header">
              <div className="success-icon">🎉</div>
              <h3>Student Registered Successfully!</h3>
              <p>Your child has been registered in the OARA program.</p>
              <p style={{ marginTop: '10px', color: '#666', fontSize: '14px' }}>
                <strong>Important:</strong> Consent forms have been created and are available in the "Consent Forms" tab. 
                While you can enroll in classes and camps now, <strong>consent forms must be completed</strong> before your child's first session. 
                You have time to complete them at your convenience.
              </p>
            </div>
            
            <div className="next-steps">
              <h4>What's Next?</h4>
              <div className="next-step-options">
                <div className="next-step-card">
                  <h5>🆓 Try a Free Trial Session</h5>
                  <p>Experience our program with a complimentary trial session</p>
                  <button 
                    className="btn btn-primary"
                    onClick={() => {
                      setEnrollmentSuccess(false);
                      setActiveTab('programs');
                    }}
                  >
                    Book Trial Session
                  </button>
                </div>
                <div className="next-step-card">
                  <h5>💳 Purchase Session Package</h5>
                  <p>Get started with a session package for your child</p>
                  <button 
                    className="btn btn-secondary"
                    onClick={() => {
                      setEnrollmentSuccess(false);
                      setActiveTab('packages');
                    }}
                  >
                    View Packages
                  </button>
                </div>
              </div>
            </div>
            
            <div className="success-actions">
              <button 
                className="btn btn-primary"
                onClick={() => setEnrollmentSuccess(false)}
              >
                Continue to Dashboard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Consent Forms Tab */}
      {activeTab === 'consent-forms' && (
        <div className="dashboard-content">
          <div className="consent-forms-header">
            <h3>📝 Consent Forms</h3>
            <p>Manage unified consent forms for your children's participation in OARA programs</p>
            <p style={{ color: '#666', fontSize: '14px', marginTop: '10px', fontStyle: 'italic' }}>
              <strong>Required:</strong> Consent forms must be completed for each student. While you can enroll in classes and camps now, 
              <strong> consent forms must be completed before your child's first session</strong>. You have time to complete them at your convenience.
            </p>
          </div>

          <div className="consent-forms-container">
            {children.length === 0 ? (
              <div className="no-consent-forms">
                <p>No children registered yet.</p>
                <p>Consent forms will appear here after you register a student.</p>
              </div>
            ) : (
              <div className="consent-forms-list">
                {children.map((child) => {
                  // Get consent forms for this child
                  const childForms = consentForms.filter(f => {
                    // Try to match by student ID or student name
                    return f.student_id === child.id || 
                           f.student_name === `${child.firstName} ${child.lastName}`;
                  });
                  
                  const pendingForms = childForms.filter(f => f.status === 'PENDING');
                  const signedForms = childForms.filter(f => f.status === 'SIGNED');
                  const hasAnyForms = childForms.length > 0;
                  
                  return (
                    <div key={child.id} className="consent-form-card unified-form-card">
                      <div className="consent-form-header">
                        <h4>OARA Unified Consent Form</h4>
                        <span className={`status-badge ${pendingForms.length > 0 ? 'pending' : 'signed'}`}>
                          {pendingForms.length > 0 ? 'PENDING' : 'SIGNED'}
                        </span>
                      </div>
                      <div className="consent-form-details">
                        <p><strong>Student:</strong> {child.firstName} {child.lastName}</p>
                        <p><strong>Form Type:</strong> Comprehensive Program Consent (8 Sections)</p>
                        {hasAnyForms ? (
                          <>
                            <p><strong>Created:</strong> {formatDateLocal(childForms[0].created_at)}</p>
                            {signedForms.length > 0 && signedForms[0].signed_at && (
                              <p><strong>Signed:</strong> {formatDateLocal(signedForms[0].signed_at)}</p>
                            )}
                            {childForms[0].expires_at && (
                              <p><strong>Expires:</strong> {formatDateLocal(childForms[0].expires_at)}</p>
                            )}
                            <p className="form-sections-info">
                              <strong>Sections Covered:</strong> Student Info, Program Acknowledgment, Media Consent, Privacy, Medical, Liability, Code of Conduct, Signature
                            </p>
                          </>
                        ) : (
                          <p className="no-forms-message">
                            No consent form has been created for this student yet. One will be created automatically during registration.
                            <br />
                            <small style={{ color: '#888', fontStyle: 'italic' }}>
                              You can enroll in classes and camps now, but consent forms must be completed before your child's first session.
                            </small>
                          </p>
                        )}
                      </div>
                      <div className="consent-form-actions">
                        {pendingForms.length > 0 ? (
                          <div>
                            <button
                              className="btn btn-primary"
                              onClick={() => {
                                // Find the child object
                                const selectedChildObj = children.find(c => c.id === child.id);
                                if (selectedChildObj) {
                                  setSelectedChild(selectedChildObj);
                                  setShowConsentFormModal(true);
                                }
                              }}
                            >
                              {hasAnyForms ? 'Complete Required Form' : 'Create & Sign Form'}
                            </button>
                            <p style={{ color: '#d32f2f', fontSize: '12px', marginTop: '8px', fontStyle: 'italic' }}>
                              ⚠️ Required before first session
                            </p>
                          </div>
                        ) : signedForms.length > 0 ? (
                          <div className="signed-form-info" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            <span className="signed-badge">✅ Form Signed</span>
                            {signedForms[0].s3_url && (
                              <>
                                <button
                                  className="btn btn-primary"
                                  onClick={() => {
                                    if (signedForms[0].s3_url) {
                                      setSelectedPdfUrl(signedForms[0].s3_url);
                                      setSelectedPdfForm(signedForms[0]);
                                      setShowPdfViewer(true);
                                    }
                                  }}
                                >
                                  📄 View PDF
                                </button>
                                <a
                                  href={signedForms[0].s3_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-secondary"
                                  download
                                >
                                  ⬇️ Download
                                </a>
                              </>
                            )}
                          </div>
                        ) : (
                          <button
                            className="btn btn-secondary"
                            onClick={() => {
                              const selectedChildObj = children.find(c => c.id === child.id);
                              if (selectedChildObj) {
                                setSelectedChild(selectedChildObj);
                                setShowConsentFormModal(true);
                              }
                            }}
                          >
                            Create Form
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Session Details Tab */}
      {activeTab === 'session-details' && selectedChild && (
        <div className="dashboard-content">
          <div className="session-details-header">
            <button 
              className="btn btn-secondary"
              onClick={() => setActiveTab('children')}
            >
              ← Back to Children
            </button>
            <h3>📋 Session Details - {selectedChild.firstName} {selectedChild.lastName}</h3>
            <p>View all sessions (completed and registered) for this student</p>
          </div>

          {/* Filters */}
          <div className="session-filters">
            <div className="filter-row">
              <div className="filter-group">
                <label>Filter by Program:</label>
                <select 
                  value={sessionFilterProgram} 
                  onChange={(e) => setSessionFilterProgram(e.target.value)}
                >
                  <option value="">All Programs</option>
                  <option value="After School">After School</option>
                  <option value="Summer Camp">Summer Camp</option>
                  <option value="Weekend Workshop">Weekend Workshop</option>
                </select>
              </div>
              <div className="filter-group">
                <label>Filter by Course:</label>
                <select 
                  value={sessionFilterCourse} 
                  onChange={(e) => setSessionFilterCourse(e.target.value)}
                >
                  <option value="">All Courses</option>
                  <option value="Robotics Foundations">Robotics Foundations</option>
                  <option value="Advanced Programming">Advanced Programming</option>
                  <option value="3D Design">3D Design</option>
                </select>
              </div>
              <div className="filter-group">
                <label>Filter by Status:</label>
                <select 
                  value={sessionFilterStatus} 
                  onChange={(e) => setSessionFilterStatus(e.target.value)}
                >
                  <option value="">All Statuses</option>
                  <option value="completed">Completed</option>
                  <option value="registered">Registered</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>

          {/* Session Details Table */}
          <div className="session-details-table">
            {sessionDetails.length === 0 ? (
              <div className="no-sessions">
                <p>No session details found for this student.</p>
              </div>
            ) : (
              <table className="sessions-table">
                <thead>
                  <tr>
                    <th>Session Date</th>
                    <th>Program</th>
                    <th>Course</th>
                    <th>Status</th>
                    <th>Duration</th>
                    <th>Instructor</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {sessionDetails
                    .filter(session => {
                      if (sessionFilterProgram && session.program !== sessionFilterProgram) return false;
                      if (sessionFilterCourse && session.course !== sessionFilterCourse) return false;
                      if (sessionFilterStatus && session.status !== sessionFilterStatus) return false;
                      return true;
                    })
                    .map((session, index) => (
                      <tr key={session.id || index}>
                        <td>{new Date(session.session_date).toLocaleDateString()}</td>
                        <td>{session.program || 'N/A'}</td>
                        <td>{session.course || 'N/A'}</td>
                        <td>
                          <span className={`status-badge ${session.status?.toLowerCase()}`}>
                            {session.status || 'Unknown'}
                          </span>
                        </td>
                        <td>{session.duration || '90 min'}</td>
                        <td>{session.instructor || 'TBD'}</td>
                        <td>{session.notes || '-'}</td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Course Registration Form Modal */}
      {showCourseRegistration && (
        <CourseRegistrationForm
          students={children.map(child => ({
            id: child.id,
            firstName: child.firstName,
            lastName: child.lastName,
            tier: child.tier
          }))}
          onClose={handleCloseCourseRegistration}
          onSuccess={handleCourseRegistrationSuccess}
        />
      )}

      {/* Unified Consent Form Modal */}
      {selectedChild && showConsentFormModal && (
        <UnifiedConsentFormModal
          studentInfo={{
            firstName: selectedChild.firstName,
            lastName: selectedChild.lastName,
            // Extract date part only (YYYY-MM-DD) - date-only fields should never be timezone-converted
            dateOfBirth: selectedChild.dateOfBirth 
              ? (selectedChild.dateOfBirth.includes('T') ? selectedChild.dateOfBirth.split('T')[0] : selectedChild.dateOfBirth)
              : (selectedChild as any).date_of_birth 
                ? ((selectedChild as any).date_of_birth.includes('T') ? (selectedChild as any).date_of_birth.split('T')[0] : (selectedChild as any).date_of_birth)
                : '',
            grade: selectedChild.gradeLevel
          }}
          parentInfo={{
            firstName: user?.attributes?.first_name || user?.attributes?.firstName || 'Parent',
            lastName: user?.attributes?.last_name || user?.attributes?.lastName || 'Name',
            email: user?.email || 'parent@example.com',
            phone: user?.attributes?.phone || user?.attributes?.phone_number || '+1-555-0123',
            address: ''
          }}
          isOpen={showConsentFormModal}
          isRegistrationMode={false}
          onClose={() => {
            setShowConsentFormModal(false);
            setSelectedChild(null);
            fetchConsentForms();
          }}
          onSign={async (formData, signatureData) => {
            try {
              console.log('🔍 Signing unified consent form for existing student...');
              console.log('🔍 Selected child:', selectedChild?.id, selectedChild?.firstName, selectedChild?.lastName);
              console.log('🔍 Current consent forms:', consentForms.length);
              
              // Refresh consent forms first to get the latest list
              await fetchConsentForms();
              
              // Get all pending forms for this child
              const childForms = consentForms.filter(f => 
                f.student_id === selectedChild.id || 
                f.student_name === `${selectedChild.firstName} ${selectedChild.lastName}`
              );
              
              console.log('🔍 Child forms found:', childForms.length);
              console.log('🔍 Child forms:', childForms.map(f => ({ id: f.id, status: f.status, student_id: f.student_id })));
              
              const pendingFormIds = childForms
                .filter(f => f.status === 'PENDING')
                .map(f => f.id);
              
              console.log('🔍 Pending form IDs to sign:', pendingFormIds);
              
              // If no pending forms exist, we might need to create one
              // For now, sign existing pending forms
              if (pendingFormIds.length > 0) {
                const results = [];
                for (const formId of pendingFormIds) {
                  console.log(`🔍 Signing form ${formId}...`);
                  try {
                    const response = await handleSignConsentForm(formId, signatureData, false);
                    if (response && response.success) {
                      results.push({ formId, success: true });
                      console.log(`✅ Form ${formId} signed successfully`);
                    } else {
                      results.push({ formId, success: false, error: 'Response not successful' });
                      console.error(`❌ Failed to sign form ${formId}:`, response);
                    }
                  } catch (signError: any) {
                    results.push({ formId, success: false, error: signError.message });
                    console.error(`❌ Error signing form ${formId}:`, signError);
                  }
                  await new Promise(resolve => setTimeout(resolve, 300));
                }
                
                const allSigned = results.every(r => r.success);
                console.log(`🔍 Signing results: ${results.filter(r => r.success).length}/${results.length} succeeded`);
                
                if (allSigned) {
                  // Wait for database to commit
                  await new Promise(resolve => setTimeout(resolve, 500));
                  await fetchConsentForms();
                  await fetchDashboardData();
                  setShowConsentFormModal(false);
                  setSelectedChild(null);
                  return { success: true, message: 'Consent form signed successfully' };
                } else {
                  const failedForms = results.filter(r => !r.success);
                  console.error('❌ Some forms failed to sign:', failedForms);
                  throw new Error(`Failed to sign ${failedForms.length} form(s). Please try again.`);
                }
              } else {
                // No pending forms - might be viewing already signed form
                console.log('⚠️ No pending forms found for this child');
                await fetchConsentForms();
                setShowConsentFormModal(false);
                setSelectedChild(null);
                return { success: true, message: 'Form information updated' };
              }
            } catch (error: any) {
              console.error('❌ Error signing unified consent form:', error);
              setError(error.message || 'Failed to sign consent form');
              throw error;
            }
          }}
        />
      )}

      {/* PDF Viewer Modal */}
      {showPdfViewer && selectedPdfUrl && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div className="pdf-viewer-modal" style={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '1200px',
            height: '90%',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
          }}>
            <div className="pdf-viewer-header" style={{
              padding: '15px 20px',
              borderBottom: '1px solid #ddd',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px 8px 0 0'
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px' }}>📄 Consent Form PDF</h3>
                {selectedPdfForm && (
                  <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#666' }}>
                    {selectedPdfForm.student_name} - Signed on {selectedPdfForm.signed_at ? formatDateLocal(selectedPdfForm.signed_at) : 'N/A'}
                  </p>
                )}
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <a
                  href={selectedPdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-secondary"
                  download
                  style={{ padding: '8px 16px', fontSize: '14px' }}
                >
                  ⬇️ Download
                </a>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowPdfViewer(false);
                    setSelectedPdfUrl(null);
                    setSelectedPdfForm(null);
                  }}
                  style={{ padding: '8px 16px', fontSize: '14px' }}
                >
                  ✕ Close
                </button>
              </div>
            </div>
            <div className="pdf-viewer-content" style={{
              flex: 1,
              overflow: 'auto',
              padding: '20px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              backgroundColor: '#525252'
            }}>
              <iframe
                src={selectedPdfUrl}
                style={{
                  width: '100%',
                  height: '100%',
                  minHeight: '600px',
                  border: 'none',
                  borderRadius: '4px'
                }}
                title="Consent Form PDF"
              />
            </div>
          </div>
        </div>
      )}

      {/* Camp Payment Modal */}
      {console.log('🔍 Payment modal render check:', { showCampPayment, hasCampInvoiceData: !!campInvoiceData, hasCampEnrollmentId: !!campEnrollmentId })}
      {showCampPayment && campInvoiceData && campEnrollmentId && (
        <CampPaymentModal
          enrollmentId={campEnrollmentId}
          invoiceData={campInvoiceData}
          campName={selectedCamp?.name}
          studentName={selectedChildForCamp ? `${selectedChildForCamp.firstName} ${selectedChildForCamp.lastName}` : undefined}
          onSuccess={async () => {
            console.log('🎉 Payment success handler called');
            
            // Store names before clearing (for success message)
            const childName = selectedChildForCamp?.firstName || 'Your child';
            const campName = selectedCamp?.name || 'the camp';
            
            // Clear selected items FIRST
            setSelectedCamp(null);
            setSelectedChildForCamp(null);
            
            // Close modal and reset payment state IMMEDIATELY
            setShowCampPayment(false);
            setCampInvoiceData(null);
            setCampEnrollmentId('');
            
            // Ensure we're on camps tab
            setActiveTab('camps');
            
            // Refresh camps and dashboard data once after payment succeeds
            console.log('🔄 Refreshing camps list and dashboard data...');
            await fetchCamps(); // Use the existing fetchCamps function
            await fetchDashboardData(); // Refresh children data to show camp enrollment
            
            // Show modern success notification AFTER refresh
            setSuccessMessage({
              title: '🎉 Payment Successful!',
              message: `${childName} is now fully enrolled in ${campName}. You will receive a confirmation email shortly.`
            });
            setShowSuccessNotification(true);
            
            // Auto-dismiss after 5 seconds
            setTimeout(() => setShowSuccessNotification(false), 5000);
            
          }}
          onCancel={async () => {
            console.log('✅ Pay later handler called');
            
            // Clear selected items FIRST
            setSelectedCamp(null);
            setSelectedChildForCamp(null);
            
            // Show modern success notification for pay later
            setSuccessMessage({
              title: '✅ Enrollment Complete!',
              message: `You can pay the invoice later from the Billing tab. Invoice Number: ${campInvoiceData.invoice_number}`
            });
            setShowSuccessNotification(true);
            
            // Auto-dismiss after 5 seconds
            setTimeout(() => setShowSuccessNotification(false), 5000);
            
            // Close modal and reset payment state
            setShowCampPayment(false);
            setCampInvoiceData(null);
            setCampEnrollmentId('');
            
            // Ensure we're on camps tab
            setActiveTab('camps');
            
            // Force camps refresh and children data refresh after modal closes
            setTimeout(async () => {
              console.log('🔄 Force refreshing camps list...');
              try {
                const token = localStorage.getItem('cognitoToken') || '';
                const response = await fetch(`${config.BACKEND_URL}/api/camps?status=published&include_days=true`, {
                  headers: {
                    ...(token && { 'Authorization': `Bearer ${token}` })
                  }
                });
                
                if (response.ok) {
                  const campsData = await response.json();
                  console.log('✅ Camps refreshed, count:', campsData.length);
                  console.log('✅ Camp details:', campsData);
                  setCamps(campsData);
                } else {
                  console.error('❌ Failed to refresh camps, status:', response.status);
                }
              } catch (error) {
                console.error('❌ Error refreshing camps:', error);
              }
              
              // Refresh children data to show camp enrollment
              console.log('🔄 Refreshing children data to show camp enrollment...');
              fetchDashboardData();
            }, 300);
          }}
        />
      )}

      {/* Modern Success Notification */}
      {showSuccessNotification && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: '#10B981',
          color: 'white',
          padding: '20px 24px',
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(16, 185, 129, 0.3)',
          zIndex: 10000,
          minWidth: '350px',
          maxWidth: '500px',
          animation: 'slideIn 0.3s ease-out',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px'
        }}>
          <style>{`
            @keyframes slideIn {
              from {
                transform: translateX(400px);
                opacity: 0;
              }
              to {
                transform: translateX(0);
                opacity: 1;
              }
            }
          `}</style>
          <div style={{ fontSize: '24px', flexShrink: 0 }}>
            ✅
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ 
              fontSize: '18px', 
              fontWeight: '600', 
              marginBottom: '6px',
              letterSpacing: '0.3px'
            }}>
              {successMessage.title}
            </div>
            <div style={{ 
              fontSize: '14px', 
              lineHeight: '1.5',
              opacity: '0.95'
            }}>
              {successMessage.message}
            </div>
          </div>
          <button
            onClick={() => setShowSuccessNotification(false)}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: 'white',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '6px',
              lineHeight: '1',
              transition: 'background 0.2s',
              flexShrink: 0
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
          >
            ×
          </button>
        </div>
      )}

    </div>
  );
};

export default ParentDashboard;